import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import PasoPlegable from '../components/business/PasoPlegable';
// El alta y la edición de personal son el mismo recorrido plegable que el
// onboarding, y ahora literalmente el mismo componente.
import FormularioDePersona from '../components/business/FormularioDePersona';
import BuscadorDeLista from '../components/business/BuscadorDeLista';
import FilaAnimada from '../components/business/FilaAnimada';
import { filtrarYOrdenar, ORDEN_ALFABETICO } from '../lib/listas';
import {
  catalogoDeRoles,
  cuantosUsanElRol,
  normalizarRol,
  quitarRolDelEquipo,
  rolesDe,
  rolParaGuardar,
} from '../lib/roles';
import { businessTemplates } from '../data/rubros';
import { useAuth } from "../contexts/AuthContext";
import "./Dashboard.css";
import { FaSave } from 'react-icons/fa';
// Iconos del panel lateral. Antes eran emojis (📅 🕒 ✂️ 👥 📊 🔗 🤖 ⚙️ ❓), que
// se ven distinto en cada sistema operativo y no heredan el color del texto.
import { Calendar, Clock, Scissors, Users, HeartPulse, BarChart3, Link2, Bot, Settings, HelpCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import BrandPreview from '../components/BrandPreview';
import mercadoPagoLogo from '../assets/images/Mercado/mercadopago.webp';


import BarraDeTurnos from '../components/BarraDeTurnos';
import CalendarioDeTurnos from '../components/CalendarioDeTurnos';
import EditableServiceCard from '../components/EditableServiceCard';
import DiscountsModal from '../components/DiscountsModal';
import BusinessInfoForm from '../components/BusinessInfoForm';
import SelectorDeRubro from '../components/business/SelectorDeRubro';
import BusinessScheduleForm from '../components/BusinessScheduleForm';
import SeccionWhatsApp from '../components/SeccionWhatsApp';
import SeccionPacientes from '../components/SeccionPacientes';
import EstadisticasPanel from '../components/EstadisticasPanel';
import CurrentPlan from '../components/CurrentPlan';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay';
import AgregarTurno from '../components/AgregarTurno';
import BloquearHorarios from '../components/BloquearHorarios';
import ListaDeBloqueos from '../components/ListaDeBloqueos';
import {
  obrasSociales as TODAS_LAS_OBRAS,
  idDeObraSocial,
  // Resuelve el id guardado en el turno al nombre que se muestra: sin esto la
  // tarjeta diría "osde-210" en vez de "OSDE 210". Contempla también las
  // coberturas que el negocio cargó a mano, que la lista maestra no conoce.
  nombreDeObraSocial,
} from '../data/obrasSociales';
import { useDemo } from '../contexts/DemoContext';
import { TrialAlert } from '../components/PlanProtectedRoute';
import { checkAndWarnIncognito } from '../utils/detectIncognito';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
// El Toaster está montado una sola vez en App.js y sirve a toda la app.
import { toast } from 'sonner';
import { aFechaISO } from '../lib/calendario';
import {
  aTurnoDePanel,
  ordenarTurnos,
  turnosNuevos,
  mismoProfesional,
  turnosDelProfesional,
} from '../lib/turnos';
import useTurnosEnVivo from '../hooks/useTurnosEnVivo';
import { aplicarBranding, limpiarBranding, COLORES_POR_DEFECTO } from '../lib/branding';
import { mismoHorario } from '../lib/horarios';
import { mismosDatosDelNegocio, datosComparables, CAMPOS_DEL_NEGOCIO } from '../lib/negocio';
import { mismoPersonal } from '../lib/personal';
import {
  haceCuanto,
  fechaExacta,
  minutosHasta,
  retencionVigente,
} from '../lib/tiempo';
import { esTurnoNuevo, contarNuevos, leerUltimaVisita, marcarVisita } from '../lib/novedades';
import { API_URL } from '../config/api';



// API URL Configuration

/**
 * Un número del formulario, o el valor por defecto si lo que hay no es un número.
 *
 * Existe porque `parseFloat('')` es `NaN` y `JSON.stringify(NaN)` es `null`: el
 * campo vacío no llegaba al backend como "vacío" sino como `null`, y ahí lo
 * rechazaba la validación. Nunca devuelve `NaN`.
 */
const numeroO = (valor, porDefecto) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : porDefecto;
};

/**
 * El servicio en blanco del que parte el formulario de "agregar servicio".
 *
 * ESTÁ ACÁ AFUERA A PROPÓSITO, y es un arreglo de bug, no un prolijeo. Antes era
 * un objeto literal escrito dentro del JSX (`service={editingService || {...}}`),
 * o sea uno NUEVO en cada render del panel. `EditableServiceCard` reinicia su
 * formulario con `useEffect(..., [service, ...])`, así que esa identidad nueva
 * disparaba el efecto y volvía a poner el nombre en `''`: escribías el nombre y
 * se borraba solo.
 *
 * Y el panel re-renderiza sin que nadie lo toque — `useTurnosEnVivo` está
 * suscripto a Firestore con `onSnapshot` y empuja cada turno que entra. Por eso
 * el borrado parecía aleatorio: dependía de que llegara una reserva mientras
 * tipeabas. Al editar no pasaba, porque ahí `editingService` es un objeto estable
 * guardado en estado.
 *
 * Congelado para que un descuido no lo convierta en estado compartido entre dos
 * aperturas del formulario.
 */
const SERVICIO_NUEVO = Object.freeze({
  name: '',
  duration: 30,
  price: 0,
  description: '',
  maxAdvanceBookingDays: 30,
  maxSimultaneousBookings: 1,
  allowWaitingList: false,
  allowCancellation: true,
  requiresDeposit: false
});

// Configuración de Mercado Pago OAuth
const MERCADO_PAGO_CONFIG = {
    CLIENT_ID: '8962166513954821', // Cliente ID de MercadoPago
    REDIRECT_URI: (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
      ? 'http://localhost:3001/api/oauth/mercadopago/callback' 
      : 'https://aturno-backend.onrender.com/api/oauth/mercadopago/callback'), // Callback del backend
    AUTH_URL: 'https://auth.mercadopago.com.ar/authorization'
};

// Componente PlanInfo para mostrar información del plan actual
function PlanInfo({ currentUser, handleChangePlan, handleCancelPlan, isDemo }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        if (!currentUser) return;
        
        console.log('🔍 DASHBOARD: Fetching plan data from backend...');
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_URL}/api/plan/current`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('🔍 DASHBOARD: Plan data received:', data);
        setPlan(data);
        setLoading(false);
      } catch (err) {
        console.error('❌ DASHBOARD: Error fetching plan:', err);
        setError("No se pudo cargar el plan");
        setLoading(false);
      }
    };
    fetchPlan();
  }, [currentUser]);

  // Actualizar el tiempo cada minuto para mostrar el trial actualizado
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Cada 60 segundos

    return () => clearInterval(interval);
  }, []);

  if (loading) return <div>Cargando plan...</div>;
  if (error) return <div>{error}</div>;
  if (!plan) return null;

  console.log('🔍 DASHBOARD: Full plan object structure:', plan);
  
  const planData = plan?.plan;
  const features = plan?.features;
  const usage = plan?.usage;
  
  console.log('🔍 DASHBOARD: planData extracted:', planData);
  
  if (!planData) {
    console.error('❌ DASHBOARD: planData is undefined!');
    return <div>Error: No se pudo cargar la información del plan</div>;
  }
  
  const { 
    name = 'Plan Desconocido', 
    status = 'inactive', 
    monthlyPrice = 0, 
    nextBilling = null, 
    isInTrial = false, 
    trialDaysRemaining = 0, 
    billingCycle = 'monthly',
    freeTrialEndDate = null
  } = planData;
  
  console.log('🎯 DASHBOARD: Extracted plan variables:');
  console.log('🎯 name:', name);
  console.log('🎯 isInTrial:', isInTrial);
  console.log('🎯 trialDaysRemaining:', trialDaysRemaining);
  console.log('🎯 status:', status);
  console.log('🎯 freeTrialEndDate:', freeTrialEndDate);
  console.log('🎯 Should show trial badge:', isInTrial ? 'YES' : 'NO');
  const premiumFeatures = [
    { key: "customBranding", label: "Página personalizable" },
    { key: "analytics", label: "Estadísticas avanzadas" },
    { key: "integrations", label: "Integraciones" },
    { key: "multiLocation", label: "Multi-sucursal" },
  ];

  return (
    <div className="plan-container">
      <div className="plan-main-header">
        <div className="plan-title-section">
          <div className="plan-badge">
            <span className="plan-badge-text">Plan Actual</span>
          </div>
          <h4 className="plan-name">{name}</h4>
          <div className="plan-pricing">
            <span className="plan-price">
              {monthlyPrice === 0 ? "Gratis" : billingCycle === 'annual' ? `$ ${monthlyPrice * 10}` : `$ ${monthlyPrice}`}
            </span>
            <span className="billing-cycle">/{billingCycle === 'annual' ? 'anual' : 'mensual'}</span>
          </div>
          {isInTrial && (
            <div className="trial-badge" style={{backgroundColor: '#28a745', color: 'white', fontWeight: 'bold', border: '2px solid #155724'}}>
              <span className="trial-icon">🎉</span>
              <span className="trial-text">
                {(() => {
                  // Calcular tiempo restante en horas y minutos
                  const trialEndDate = new Date(freeTrialEndDate);
                  const now = currentTime;
                  const diffMs = trialEndDate - now;
                  
                  if (diffMs <= 0) return 'Trial expirado';
                  
                  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
                  const remainingMs = diffMs % (1000 * 60 * 60);
                  const minutes = Math.floor(remainingMs / (1000 * 60));
                  
                  const days = Math.floor(totalHours / 24);
                  const hours = totalHours % 24;
                  
                  if (days > 0) {
                    return `${days}d ${hours}h ${minutes}m de prueba gratis restante`;
                  } else if (hours > 0) {
                    return `${hours}h ${minutes}m de prueba gratis restante`;
                  } else {
                    return `${minutes}m de prueba gratis restante`;
                  }
                })()}
              </span>
            </div>
          )}
        </div>
        <div className="plan-status-section">
          <div className={`status-indicator ${status}`}>
            <div className="status-dot"></div>
            <span className="status-text">
              {status === "active" ? "Activo" : "Inactivo"}
            </span>
          </div>
        </div>
      </div>
      <div className="plan-content-grid">
        <div className="billing-card">
          <h5 className="card-title">
            <span className="card-icon">💳</span>Información de pago
          </h5>
          <div className="billing-details">
            <div className="billing-row">
              <span className="label">Próxima facturación</span>
              <span className="value">
                {nextBilling ? new Date(nextBilling).toLocaleDateString() : "N/A"}
              </span>
            </div>
          </div>
        </div>
        <div className="features-card">
          <h5 className="card-title">
            <span className="card-icon">✨</span>Funciones incluidas
          </h5>
          <div className="features-list">
            <div className="feature-item">
              <span className="feature-check">✓</span>
              <span className="feature-text">
                Staff: {usage.staff.current} / {usage.staff.limit === -1 ? "∞" : usage.staff.limit}
              </span>
            </div>
            <div className="feature-item">
              <span className="feature-check">✓</span>
              <span className="feature-text">
                Servicios: {usage.services.current} / {usage.services.limit === -1 ? "∞" : usage.services.limit}
              </span>
            </div>
            <div className="feature-item">
              <span className="feature-check">✓</span>
              <span className="feature-text">
                Recursos: {usage.resources.current} / {usage.resources.limit === -1 ? "∞" : usage.resources.limit}
              </span>
            </div>
            <div className="feature-item">
              <span className="feature-check">✓</span>
              <span className="feature-text">
                Reservas/mes: {usage.bookingsThisMonth.current} / {usage.bookingsThisMonth.limit === -1 ? "∞" : usage.bookingsThisMonth.limit}
              </span>
            </div>
            {premiumFeatures.map(
              (f) =>
                features[f.key] && (
                  <div className="feature-item" key={f.key}>
                    <span className="feature-check">✓</span>
                    <span className="feature-text">{f.label}</span>
                  </div>
                )
            )}
          </div>
        </div>
      </div>
      <div className="plan-actions-footer">
        <button className="btn-plan-primary" onClick={handleChangePlan}>
          <span className="btn-icon">🔄</span>Cambiar Plan
        </button>
        {status === "active" && (
          <button className="btn-plan-secondary" onClick={handleCancelPlan}>
            <span className="btn-icon">⚠️</span>Cancelar Suscripción
          </button>
        )}
      </div>
    </div>
  );
}


// ===============================================
// INICIO: Tarea - Corregir puerto del Backend
// ===============================================

const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const minutes = ['00', '15', '30', '45'];

// --- CONSTANTES DEL COMPONENTE ---
const dayNames = {
  monday: "Lunes",
  tuesday: "Martes",
  wednesday: "Miércoles",
  thursday: "Jueves",
  friday: "Viernes",
  saturday: "Sábado",
  sunday: "Domingo"
};

/**
 * Los estados en los que puede estar un turno.
 *
 * Faltaban los dos de seña, y por eso un turno esperando el pago aparecía como
 * "Desconocido": `getStatusLabel` cae al `default` con cualquier estado que no
 * esté acá, y el backend viene guardando `pending_deposit` y
 * `pending_confirmation` desde que existen las señas.
 *
 * Los dos dicen "esperando" pero no son lo mismo y por eso se distinguen:
 * `pending_deposit` tiene un reloj corriendo —son 30 minutos y después el
 * horario se libera solo—, mientras que `pending_confirmation` es la seña por
 * transferencia o efectivo, que no vence y la confirma el negocio a mano.
 */
const statusColors = {
  confirmed: { bg: "rgba(76, 175, 80, 0.1)", color: "#4caf50" },
  pending: { bg: "rgba(255, 193, 7, 0.1)", color: "#ffc107" },
  pending_deposit: { bg: "rgba(255, 152, 0, 0.12)", color: "#e65100" },
  pending_confirmation: { bg: "rgba(255, 193, 7, 0.1)", color: "#ffc107" },
  cancelled: { bg: "rgba(244, 67, 54, 0.1)", color: "#f44336" },
  default: { bg: "rgba(158, 158, 158, 0.1)", color: "#9e9e9e" }
};

const statusLabels = {
  confirmed: "Confirmado",
  pending: "Pendiente",
  pending_deposit: "Esperando pago",
  pending_confirmation: "Esperando seña",
  cancelled: "Cancelado",
  default: "Desconocido"
};

// Función helper para obtener colores de status de forma segura
const getStatusColors = (status) => {
  if (!status || !statusColors[status]) {
    return statusColors.default;
  }
  return statusColors[status];
};

// Función helper para obtener labels de status de forma segura
const getStatusLabel = (status) => {
  if (!status || !statusLabels[status]) {
    return statusLabels.default;
  }
  return statusLabels[status];
};

/**
 * ¿El turno ya pasó?
 *
 * A nivel de módulo y no adentro del componente: no lee nada del estado, y
 * declarada adentro con `const` no se la puede usar desde un `useMemo` que esté
 * más arriba —los memos corren durante el render y ahí todavía está en la zona
 * muerta temporal—. Acá está disponible para todos.
 */
const isAppointmentPast = (appointment) => {
  const now = new Date();
  const appointmentDateTime = new Date(`${appointment.date}T${appointment.time}:00`);
  return appointmentDateTime < now;
};

export default function Dashboard({ isDemo = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userData, logout, loading: authLoading } = useAuth();
  const { demoBookings, updateBookingStatus, borrarDemoBooking } = useDemo();
  const [activeSection, setActiveSection] = useState("appointments");
  // Borrado de cuenta. `confirmacionBorrado` guarda lo que la persona escribe:
  // el botón recién se habilita cuando coincide con el nombre de su negocio, para
  // que no se borre una cuenta por un click de más.
  const [mostrarBorrarCuenta, setMostrarBorrarCuenta] = useState(false);
  const [confirmacionBorrado, setConfirmacionBorrado] = useState('');
  const [borrandoCuenta, setBorrandoCuenta] = useState(false);

  /**
   * Borra la cuenta y todo lo que cuelga de ella.
   *
   * El backend cancela primero la suscripción de Mercado Pago y sólo después
   * borra los datos: si la cancelación falla devuelve 409 y no borra nada, para
   * no dejar un cobro activo sobre una cuenta que ya no existe.
   */
  const borrarCuenta = async () => {
    setBorrandoCuenta(true);

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/account`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setBorrandoCuenta(false);
        toast.error('No pudimos borrar tu cuenta', {
          description: data.message || 'Probá de nuevo en un momento.',
          duration: 10000,
        });
        return;
      }

      // Se cierra sesión antes de navegar: el usuario de Auth ya no existe y
      // cualquier pantalla protegida intentaría usar un token de una cuenta
      // borrada.
      toast.success('Tu cuenta fue borrada');
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error borrando la cuenta:', error);
      setBorrandoCuenta(false);
      toast.error('No pudimos borrar tu cuenta', {
        description: 'Revisá tu conexión y probá de nuevo.',
      });
    }
  };
  const [showEditableCard, setShowEditableCard] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showBlockTimeModal, setShowBlockTimeModal] = useState(false);
  const [bloqueandoHorarios, setBloqueandoHorarios] = useState(false);
  const [mostrarAgregarTurno, setMostrarAgregarTurno] = useState(false);
  const [guardandoTurnos, setGuardandoTurnos] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [isAnnualBilling, setIsAnnualBilling] = useState(true); // Empieza con anual activado
  const [processingSubscription, setProcessingSubscription] = useState(false);
  
  // Estado para el modal de descuentos
  const [showDiscountsModal, setShowDiscountsModal] = useState(false);
  const [discountsService, setDiscountsService] = useState(null);
  
  // Estados para el sistema responsive avanzado
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  

  const [blockedSlots, setBlockedSlots] = useState([]);
  const [error, setError] = useState("");
  const [savingStaff, setSavingStaff] = useState(false);
  const [savingResources, setSavingResources] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [businessDataLoaded, setBusinessDataLoaded] = useState(false);
  const [modalError, setModalError] = useState("");
  
  // Estado para el plan del usuario (para controlar acceso a funcionalidades)
  const [userPlan, setUserPlan] = useState(null);
  
  // Estados para la sección de ayuda
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  // Estados para integraciones disponibles
  const [integrations, setIntegrations] = useState({
    googleCalendar: { active: false, connected: false, email: "" },
    mercadoPago: { active: false, connected: false, accessToken: "" }
  });
  const [connectingIntegration, setConnectingIntegration] = useState(null);
  // const [disconnectingIntegration, setDisconnectingIntegration] = useState(null); // Removido: las integraciones se manejan desde las plataformas externas

  // Estados para errores de horarios
  const [scheduleError, setScheduleError] = useState("");
  
  // Estado para sincronización de datos
  const [syncStatus, setSyncStatus] = useState("");
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Estados para plan y facturación
  const [currentPlan, setCurrentPlan] = useState({
    name: "Plan Profesional",
    price: 2500,
    currency: "ARS",
    billingPeriod: "mensual",
    nextBilling: "2024-02-15",
    status: "active", // active, cancelled, expired
    features: [
      "Turnos ilimitados",
      "5 integraciones incluidas", 
      "Soporte por email",
      "Recordatorios automáticos",
      "Página personalizable"
    ]
  });
  
  // Estado para datos de suscripción real
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showTrialExpiredOverlay, setShowTrialExpiredOverlay] = useState(false);
  // Panel de pruebas: la lista de planes y cuál está elegido en el selector.
  // Se llenan desde /api/debug/planes, que sólo existe en desarrollo.
  const [planesDePrueba, setPlanesDePrueba] = useState([]);
  const [planElegido, setPlanElegido] = useState('');
  
  // DATOS REALES - Se inicializan vacíos y se cargan desde el backend
  const [appointments, setAppointments] = useState([]);

  /**
   * Cuándo se miró el panel de turnos por última vez, ANTES de esta visita.
   *
   * Se congela al montar y no se toca más mientras la pantalla está abierta.
   * Es lo que hace que el cartel de "Nuevo" no desaparezca en la cara de quien
   * lo está leyendo: si se actualizara en vivo, los turnos dejarían de estar
   * marcados en el mismo momento en que aparecen, que es igual a no marcarlos.
   *
   * El sello se adelanta recién al salir de la sección — ver el efecto de más
   * abajo.
   */
  const [ultimaVisitaATurnos, setUltimaVisitaATurnos] = useState(null);
  const [visitaLeida, setVisitaLeida] = useState(false);

  /**
   * Cuántos turnos nuevos hay desde la última visita a la pantalla de Turnos.
   *
   * Vive acá arriba, con el resto de los hooks, y no más abajo junto a las
   * funciones que lo usan: abajo quedaba DESPUÉS del `if (authLoading || loading)`
   * que corta el render mientras carga, así que en ese estado no se ejecutaba y
   * en el siguiente sí. React exige que los hooks corran siempre en el mismo
   * orden, y ESLint corta el build con `rules-of-hooks`.
   *
   * El comentario que tenía antes decía que estaba abajo para no chocar con la
   * zona muerta de `isAppointmentPast`. Eso dejó de ser cierto cuando esa
   * función se movió a nivel de módulo (arriba del componente): ya no hay nada
   * que esquivar.
   *
   * Da 0 hasta leer el sello de la visita anterior: sin eso el primer cuadro
   * contaría de más y se corregiría solo, que es un número parpadeando justo
   * donde se está buscando una novedad.
   */
  const cantidadDeNuevos = useMemo(
    () =>
      visitaLeida
        ? contarNuevos(
            appointments.filter((t) => !isAppointmentPast(t)),
            { ultimaVisita: ultimaVisitaATurnos }
          )
        : 0,

    [appointments, ultimaVisitaATurnos, visitaLeida]
  );



  // Por cuenta: dos negocios en el mismo navegador no comparten "la última vez
  // que miré".
  const claveDeVisita = isDemo ? 'demo' : currentUser?.uid || null;

  useEffect(() => {
    if (!claveDeVisita || visitaLeida) return;
    setUltimaVisitaATurnos(leerUltimaVisita(claveDeVisita));
    setVisitaLeida(true);
  }, [claveDeVisita, visitaLeida]);

  // Los planes del panel de pruebas. Salen de TIERS a través del backend en vez
  // de una lista escrita acá: una copia en el frontend sería justo la segunda
  // tabla de límites que causó el bug del trial con tope de 10 servicios.
  // `/api/debug/planes` contesta 404 fuera de desarrollo, así que en producción
  // la lista queda vacía y el selector no se dibuja aunque alguien fuerce el
  // hostname.
  useEffect(() => {
    if (window.location.hostname !== 'localhost' || !currentUser || isDemo) return;
    let vigente = true;
    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const r = await fetch(`${API_URL}/api/debug/planes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const { planes } = await r.json();
        if (vigente) setPlanesDePrueba(planes || []);
      } catch {
        // El panel de pruebas no molesta si el backend no está: se queda vacío.
      }
    })();
    return () => { vigente = false; };
  }, [currentUser, isDemo]);


  /**
   * Anota que el panel se miró, al SALIR de Turnos.
   *
   * En la limpieza del efecto y no al entrar: si se sellara al entrar, la
   * referencia se movería antes de que la pantalla llegue a pintar y no habría
   * nada marcado nunca.
   *
   * `pagehide` cubre cerrar la pestaña y recargar, que no desmontan nada. Sin
   * eso, recargar el panel dejaría el sello viejo y los mismos turnos seguirían
   * saliendo como nuevos una vez más.
   */
  useEffect(() => {
    if (!claveDeVisita || activeSection !== 'appointments') return undefined;

    const sellar = () => marcarVisita(claveDeVisita);
    window.addEventListener('pagehide', sellar);

    return () => {
      window.removeEventListener('pagehide', sellar);
      sellar();
    };
  }, [claveDeVisita, activeSection]);

  /**
   * Los turnos en vivo, por el listener de Firestore.
   *
   * No reemplaza al `GET /api/bookings` de más abajo: se suma. Mientras el
   * listener no haya contestado —o si falla, porque las reglas todavía no se
   * desplegaron o App Check rechaza la lectura— esto es `null` y el panel se
   * comporta exactamente como antes. Ver src/hooks/useTurnosEnVivo.js.
   *
   * En la demo no corre: no hay usuario ni reservas de verdad que escuchar.
   */
  const { turnos: turnosEnVivo } = useTurnosEnVivo(currentUser?.uid, { activo: !isDemo });

  /**
   * Los turnos que llegaron y todavía no se metieron en la lista.
   *
   * Se calcula al dibujar, comparando lo que hay en pantalla contra lo que trajo
   * el listener, en vez de guardarse en un estado aparte: así no puede quedar
   * desincronizado con lo que se está mostrando. De paso resuelve solo el caso
   * del turno que carga el propio negocio desde "Agregar turno" — su id ya está
   * en `appointments` cuando llega el snapshot, así que no se le avisa de un
   * turno nuevo que acaba de cargar él.
   */
  const idsMostrados = useMemo(
    () => new Set(appointments.map((t) => t.id)),
    [appointments]
  );
  const idsPendientes = turnosEnVivo ? turnosNuevos(idsMostrados, turnosEnVivo) : [];

  /**
   * Los cambios sobre turnos que YA están en pantalla se aplican solos.
   *
   * Una cancelación o una seña que se pagó no reordenan nada —el turno ya está
   * en su lugar, cambia de estado ahí mismo— y enterarse tarde de eso es
   * justamente el problema que esto viene a resolver. Los turnos NUEVOS, en
   * cambio, no se meten solos: se avisan, porque insertar una fila mientras
   * alguien está leyendo otra le mueve la pantalla debajo del dedo.
   *
   * Se conserva el orden que ya tenía la lista (`prev` manda) para que aplicar
   * un cambio no reacomode nada.
   */
  useEffect(() => {
    if (!turnosEnVivo) return;

    setAppointments((prev) => {
      // Todavía no cargó nada por la API: el listener es lo primero que llega.
      if (prev.length === 0) return turnosEnVivo;

      const vivos = new Map(turnosEnVivo.map((t) => [t.id, t]));
      return prev.filter((t) => vivos.has(t.id)).map((t) => vivos.get(t.id));
    });
  }, [turnosEnVivo]);

  /**
   * Los turnos que acaban de entrar a la lista, para poder animarlos.
   *
   * Es un estado aparte y transitorio: marca las tarjetas durante la animación y
   * se vacía sola. Sin vaciarlo, la clase quedaría puesta para siempre y
   * cualquier re-render podría volver a disparar la entrada de un turno que ya
   * hace rato que está.
   */
  const [idsRecienLlegados, setIdsRecienLlegados] = useState(() => new Set());

  useEffect(() => {
    if (idsRecienLlegados.size === 0) return undefined;
    const t = setTimeout(() => setIdsRecienLlegados(new Set()), 1200);
    return () => clearTimeout(t);
  }, [idsRecienLlegados]);

  /** Mete los turnos nuevos en la lista, ya ordenada, y los marca para animarlos. */
  const integrarTurnosNuevos = () => {
    if (!turnosEnVivo) return;
    setIdsRecienLlegados(new Set(idsPendientes));
    setAppointments(turnosEnVivo);
  };

  /**
   * Abre la ficha del profesional de un turno: la MISMA que abre el botón de
   * editar en Personal y Recursos (`setEditingStaff`), no una vista aparte.
   *
   * Lo único propio de acá es resolver de qué persona se trata. La reserva no
   * guarda el id del profesional: guarda una COPIA de sus datos tal como estaban
   * el día que se reservó, y esa copia puede haber quedado vieja. Se la busca en
   * el equipo de HOY, que es el que hay que abrir y editar.
   *
   * Si ya no está en el equipo no hay ficha que abrir —la edición escribe sobre
   * `staff`, y esa persona no está ahí—, así que se avisa en vez de abrir un
   * formulario que al guardar no haría nada.
   */
  const abrirFichaDeProfesional = (appointment) => {
    const referencia = appointment?.staff || appointment?.assignedStaff;
    if (!referencia) return;

    const actual = staff.find((persona) => mismoProfesional(persona, referencia));

    if (!actual) {
      toast.info('Esta persona ya no está en tu equipo', {
        description: 'El turno guarda sus datos de cuando se reservó, pero su ficha ya no existe.',
      });
      return;
    }

    setEditingStaff(actual);
  };
  /**
   * La hora actual, que avanza sola.
   *
   * Las reservas que esperan el pago de la seña vencen a los 30 minutos, y
   * cuando vencen tienen que irse de la lista. Sin este reloj eso sólo pasaría
   * al recargar la página: el negocio se quedaría mirando un turno vencido, o
   * peor, una cuenta regresiva congelada en "vence en 12 min" mientras el
   * horario ya está libre.
   *
   * Cada 30 segundos alcanza: la cuenta se muestra en minutos, así que un
   * intervalo más corto sería re-renderizar el panel para no cambiar nada.
   */
  const [ahora, setAhora] = useState(() => Date.now());

  useEffect(() => {
    const reloj = setInterval(() => setAhora(Date.now()), 30 * 1000);
    return () => clearInterval(reloj);
  }, []);
  const [services, setServices] = useState([]);
  const [schedule, setSchedule] = useState({});
  /**
   * El horario tal como está guardado en el servidor.
   *
   * Es la referencia contra la que se decide si hay cambios sin guardar:
   * `businessInfo.schedule` es la copia que edita la grilla, y se le escribe
   * encima en cada gesto. Se actualiza al cargar los datos y después de cada
   * guardado exitoso, que son los dos momentos en que las dos copias coinciden.
   */
  const [horarioGuardado, setHorarioGuardado] = useState({});
  /**
   * La configuración tal como está guardada en el servidor.
   *
   * El equivalente de `horarioGuardado` para la sección Configuración: es la
   * referencia contra la que se decide si hay cambios sin guardar. Se actualiza
   * en los dos únicos momentos en que las dos copias coinciden — al cargar los
   * datos y después de un guardado exitoso.
   */
  const [datosGuardados, setDatosGuardados] = useState({});
  const [guardandoHorarios, setGuardandoHorarios] = useState(false);
  /**
   * A dónde quería ir la persona cuando se le preguntó si guardaba, o `null` si
   * no se le está preguntando nada.
   *
   * Se guarda el destino y no sólo el hecho de haber preguntado: sin él, quien
   * elige "guardar" se queda igual en Horarios y tiene que volver a hacer el
   * gesto que ya hizo. Hay dos formas de irse, y se retoman distinto: cambiar de
   * sección es nuestro (`setActiveSection`) y cambiar de ruta es del router, que
   * deja el `proceed`/`reset` de `useBlocker` para seguir o quedarse.
   */
  const [salidaPendiente, setSalidaPendiente] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [businessInfo, setBusinessInfo] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    description: "",
    schedule: {
      monday: { enabled: false, ranges: [] },
      tuesday: { enabled: false, ranges: [] },
      wednesday: { enabled: false, ranges: [] },
      thursday: { enabled: false, ranges: [] },
      friday: { enabled: false, ranges: [] },
      saturday: { enabled: false, ranges: [] },
      sunday: { enabled: false, ranges: [] }
    }
  });

  // Lo que hay que escribir para confirmar el borrado de la cuenta. Se define acá
  // y no arriba porque depende de businessInfo.
  const nombreDelNegocio = businessInfo?.name || userData?.businessName || userData?.name || 'Mi Negocio';

  // El rubro elegido, para el encabezado de su sección plegable: cerrada tiene
  // que decir cuál está puesto, que es la mitad de lo que se viene a mirar.
  const rubroActual = businessTemplates.find((t) => t.id === businessInfo?.businessType);

  /**
   * Los roles que el rubro sugiere, para sembrar las etiquetas de personal.
   *
   * Salen de data/rubros.js, la misma lista que usa el onboarding: si acá se
   * escribiera otra, el mismo negocio vería un juego de atajos al registrarse y
   * otro distinto al agregar la segunda persona desde el panel. Son sólo
   * sugerencias — las que mandan son las que el equipo ya tiene puestas, y
   * `catalogoDeRoles` las pone primero.
   */
  const rolesSugeridosDelRubro =
    businessTemplates.find((t) => t.id === businessData?.businessType)?.template?.suggestedStaffRoles || [];

  /**
   * Borra un rol de todo el equipo.
   *
   * El catálogo de etiquetas se calcula a partir de los roles que la gente tiene
   * puestos (lib/roles.js), así que sacárselo a todos ES borrar la etiqueta: no
   * hay ninguna lista aparte de la que eliminarlo.
   *
   * Se ofrece deshacer porque el borrado toca a varias personas de una y no hay
   * forma de verlas a todas desde acá: quien se equivoca de etiqueta no tendría
   * manera de saber a quiénes se la sacó. Guardar el array anterior alcanza, y
   * es más barato que un cartel de confirmación que se aprende a aceptar sin
   * leer.
   *
   * Ojo: como el resto de esta pantalla, el cambio queda en memoria hasta que se
   * sincroniza el personal. Deshacer acá deshace lo mismo que se ve.
   */
  const borrarRolDelEquipo = async (rol) => {
    const anterior = staff;
    const cuantos = cuantosUsanElRol(anterior, rol);
    const nueva = quitarRolDelEquipo(anterior, rol);

    setStaff(nueva);
    setNewStaff((p) => ({
      ...p,
      roles: (p.roles || []).filter((r) => normalizarRol(r) !== normalizarRol(rol)),
    }));
    setEditingStaff((prev) =>
      prev ? { ...prev, roles: rolesDe(prev).filter((r) => normalizarRol(r) !== normalizarRol(rol)) } : prev
    );

    // Se guarda en el momento, como el resto de esta pantalla —prender, apagar
    // y borrar personal ya lo hacían—. Era la única acción que quedaba en
    // memoria, y por eso hacía falta una barra de "guardar" para la sección
    // entera: o sea que había que guardar dos veces, una adentro del detalle de
    // la persona y otra afuera. Guardando acá, no hay nada que guardar después.
    //
    // Se le pasa la lista nueva y no se confía en el estado: `setStaff` no es
    // inmediato y en la misma vuelta todavía vale el anterior.
    await saveStaffChanges(nueva);

    toast.success(`Rol "${rol}" borrado`, {
      description: cuantos > 0
        ? `Se lo sacamos a ${cuantos} ${cuantos === 1 ? 'persona' : 'personas'}.`
        : 'No lo tenía nadie puesto.',
      // Deshacer también guarda: si sólo volviera el estado, la pantalla
      // mostraría el rol de vuelta y el servidor lo tendría borrado.
      action: {
        label: 'Deshacer',
        onClick: async () => {
          setStaff(anterior);
          await saveStaffChanges(anterior);
        },
      },
    });
  };

  // Función auxiliar para obtener los horarios del negocio para un día específico
  const getBusinessHoursForDay = (day) => {
    console.log('🔍 Dashboard - getBusinessHoursForDay:', { day, businessDataSchedule: businessData?.schedule });
    if (businessData?.schedule && businessData.schedule[day] && businessData.schedule[day].enabled && businessData.schedule[day].ranges && businessData.schedule[day].ranges.length > 0) {
      console.log('✅ Dashboard - Usando horarios del negocio:', businessData.schedule[day].ranges);
      return businessData.schedule[day].ranges;
    }
    console.log('⚠️ Dashboard - Usando horarios por defecto');
    return [{ start: '09:00', end: '17:00' }]; // fallback por defecto
  };

  // Función para validar que los días habilitados tengan horarios
  const validateSchedule = (schedule) => {
    const enabledDaysWithoutRanges = Object.entries(schedule).filter(([day, dayData]) => 
      dayData.enabled && (!dayData.ranges || dayData.ranges.length === 0)
    );
    
    if (enabledDaysWithoutRanges.length > 0) {
      const dayNames = {
        monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', 
        thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
      };
      const invalidDayNames = enabledDaysWithoutRanges.map(([day]) => dayNames[day]);
      return {
        isValid: false,
        message: `❌ Los siguientes días están habilitados pero no tienen horarios configurados: ${invalidDayNames.join(', ')}\n\n📋 Para solucionarlo:\n1. Desactiva esos días, o\n2. Agrega horarios haciendo clic en "+ Agregar rango"`
      };
    }
    
    return { isValid: true };
  };

  // Mapeo de IDs a nombres para personal y lugares
  const staffMapping = {
    1: "María González",
    2: "Juan Pérez", 
    3: "Ana López",
    4: "Carlos Ruiz"
  };

  const locationMapping = {
    // Mapeo dinámico basado en recursos reales de la base de datos
    // Se carga automáticamente desde businessData.resources
  };

  // Estados para Personal y Recursos
  const [staff, setStaff] = useState([
    { id: 1, name: "María González", email: "maria@email.com", phone: "+549111234567", active: true, schedule: {
        monday: { enabled: true, ranges: [{ start: "09:00", end: "17:00" }] },
        tuesday: { enabled: true, ranges: [{ start: "09:00", end: "17:00" }] },
        wednesday: { enabled: true, ranges: [{ start: "09:00", end: "17:00" }] },
        thursday: { enabled: true, ranges: [{ start: "09:00", end: "17:00" }] },
        friday: { enabled: true, ranges: [{ start: "09:00", end: "17:00" }] },
        saturday: { enabled: false, ranges: [] },
        sunday: { enabled: false, ranges: [] }
      }
    },
    { id: 2, name: "Juan Pérez", email: "juan@email.com", phone: "+549111234568", active: true, schedule: {
        monday: { enabled: true, ranges: [{ start: "10:00", end: "18:00" }] },
        tuesday: { enabled: true, ranges: [{ start: "10:00", end: "18:00" }] },
        wednesday: { enabled: false, ranges: [] },
        thursday: { enabled: true, ranges: [{ start: "10:00", end: "18:00" }] },
        friday: { enabled: true, ranges: [{ start: "10:00", end: "18:00" }] },
        saturday: { enabled: true, ranges: [{ start: "09:00", end: "13:00" }] },
        sunday: { enabled: false, ranges: [] }
      }
    },
    { id: 3, name: "Ana López", email: "ana@email.com", phone: "+549111234569", active: true, schedule: {
        monday: { enabled: true, ranges: [{ start: "08:00", end: "16:00" }] },
        tuesday: { enabled: true, ranges: [{ start: "08:00", end: "16:00" }] },
        wednesday: { enabled: true, ranges: [{ start: "08:00", end: "16:00" }] },
        thursday: { enabled: true, ranges: [{ start: "08:00", end: "16:00" }] },
        friday: { enabled: true, ranges: [{ start: "08:00", end: "16:00" }] },
        saturday: { enabled: false, ranges: [] },
        sunday: { enabled: false, ranges: [] }
      }
    },
    { id: 4, name: "Carlos Ruiz", email: "carlos@email.com", phone: "+549111234570", active: false, schedule: {
        monday: { enabled: false, ranges: [] },
        tuesday: { enabled: false, ranges: [] },
        wednesday: { enabled: false, ranges: [] },
        thursday: { enabled: false, ranges: [] },
        friday: { enabled: false, ranges: [] },
        saturday: { enabled: false, ranges: [] },
        sunday: { enabled: false, ranges: [] }
      }
    }
  ]);

  const [resources, setResources] = useState([
    // Los recursos se cargan dinámicamente desde la base de datos
    // No más datos de prueba hardcodeados
  ]);

  const [showNewStaffForm, setShowNewStaffForm] = useState(false);
  const [showNewResourceForm, setShowNewResourceForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  
  // Estado para el nuevo personal con horarios
  const [newStaff, setNewStaff] = useState({
    name: '',
    // Los roles son una lista de etiquetas, igual que en el onboarding: una
    // persona puede hacer dos cosas. Ver lib/roles.js.
    roles: [],
    // Opcional: sin foto se muestran las iniciales, como hasta ahora.
    foto: null,
    email: '',
    phone: '',
    schedule: {
      monday: { enabled: false, ranges: [] },
      tuesday: { enabled: false, ranges: [] },
      wednesday: { enabled: false, ranges: [] },
      thursday: { enabled: false, ranges: [] },
      friday: { enabled: false, ranges: [] },
        saturday: { enabled: false, ranges: [] },
        sunday: { enabled: false, ranges: [] }
      }
  });

  // Estado para el nuevo recurso con horarios
  const [newResource, setNewResource] = useState({
    name: '',
    description: '',
    schedule: {
        monday: { enabled: false, ranges: [] },
        tuesday: { enabled: false, ranges: [] },
        wednesday: { enabled: false, ranges: [] },
        thursday: { enabled: false, ranges: [] },
        friday: { enabled: false, ranges: [] },
        saturday: { enabled: false, ranges: [] },
        sunday: { enabled: false, ranges: [] }
      }
  });

  // Estados para la gestión de turnos mejorada
  const [appointmentView, setAppointmentView] = useState("list"); // "list" or "calendar"
  const [appointmentFilters, setAppointmentFilters] = useState({
    date: "",
    status: "",
    service: "",
    dayOfWeek: "",
    timeRange: ""
  });
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"
  const [showPastAppointments, setShowPastAppointments] = useState(false); // mostrar turnos pasados
  // Vista calendario: cuántos días muestra y a cuántos días de hoy arranca la
  // ventana. El desplazamiento se mueve de a un rango completo.
  const [diasCalendario, setDiasCalendario] = useState(7);
  const [desplazamientoCalendario, setDesplazamientoCalendario] = useState(0);

  // Estados para personalización de marca
  /**
   * Las coberturas que el consultorio atiende. Sólo se muestran en los negocios
   * del rubro salud; para el resto la sección no existe.
   */
  const [obrasSocialesDelNegocio, setObrasSocialesDelNegocio] = useState([]);
  /**
   * Las coberturas que el negocio agregó a mano, `[{ id, nombre }]`.
   *
   * Van aparte de los ids tildados porque son otra cosa: los ids dicen QUÉ
   * atiende y esto dice CÓMO se llama algo que la lista maestra no conoce. La
   * página pública necesita las dos: con el id solo mostraría "osprera" en vez
   * de "OSPRERA".
   */
  const [obrasPropias, setObrasPropias] = useState([]);

  /* Buscador y orden de las listas de personal y recursos.
     Cada lista lleva los suyos: buscar una persona no tiene por qué filtrar
     también los consultorios, y son dos listas que se miran por separado. */
  const [buscarPersonal, setBuscarPersonal] = useState('');
  const [ordenPersonal, setOrdenPersonal] = useState(ORDEN_ALFABETICO);
  const [buscarRecurso, setBuscarRecurso] = useState('');
  const [ordenRecurso, setOrdenRecurso] = useState(ORDEN_ALFABETICO);

  /* Se busca por todo lo que la tarjeta muestra, no sólo por el nombre: quien
     tiene el mail a mano lo pega ahí, y quien busca "recepción" está buscando
     por rol. Un buscador que sólo mira el nombre obliga a saber de antemano
     cómo se llama lo que se está buscando. */
  const personalVisible = useMemo(
    () =>
      filtrarYOrdenar(staff, {
        texto: buscarPersonal,
        orden: ordenPersonal,
        camposDe: (p) => [p.name, p.email, p.phone, ...rolesDe(p)],
        etiquetasDe: rolesDe,
      }),
    [staff, buscarPersonal, ordenPersonal]
  );

  const recursosVisibles = useMemo(
    () =>
      filtrarYOrdenar(resources, {
        texto: buscarRecurso,
        orden: ordenRecurso,
        camposDe: (r) => [r.name, r.type, r.description],
        // Los recursos no tienen roles; su etiqueta es el tipo.
        etiquetasDe: (r) => [r.type],
      }),
    [resources, buscarRecurso, ordenRecurso]
  );
  const [nuevaObra, setNuevaObra] = useState('');

  /**
   * Agrega una cobertura escrita a mano y la deja tildada.
   *
   * Queda tildada sola porque nadie escribe una cobertura para no atenderla:
   * pedir un segundo click sobre algo que se acaba de crear es un paso que
   * sólo se puede olvidar.
   */
  const agregarObraSocial = () => {
    const nombre = nuevaObra.trim();
    if (!nombre) return;

    const id = idDeObraSocial(nombre);
    if (!id) {
      toast.error('Ese nombre no sirve', { description: 'Poné al menos una letra o un número.' });
      return;
    }

    // Si ya existe —en la lista maestra o entre las propias— no se crea otra: se
    // tilda la que hay. Es el caso de escribir "osde" teniendo OSDE en la lista,
    // y es exactamente el duplicado que el id normalizado está para evitar.
    const yaEsta =
      TODAS_LAS_OBRAS.some((o) => o.id === id) || obrasPropias.some((o) => o.id === id);

    if (!yaEsta) setObrasPropias((prev) => [...prev, { id, nombre }]);
    setObrasSocialesDelNegocio((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setNuevaObra('');

    if (yaEsta) {
      toast.info(`"${nombre}" ya estaba en la lista`, { description: 'La dejamos tildada.' });
    }
  };
  const [guardandoObras, setGuardandoObras] = useState(false);

  const [businessBranding, setBusinessBranding] = useState({
    primaryColor: '#D84315', // Naranja oscuro de Aturno - texto blanco
    secondaryColor: '#C2185B', // Magenta oscuro de Aturno - texto blanco
    accentColor: '#4A148C'   // Violeta oscuro de Aturno - texto blanco
  });
  const [showBrandPreview, setShowBrandPreview] = useState(false);
  /**
   * La imagen del negocio, la que se ve arriba de la página pública.
   *
   * Va aparte de `businessBranding` porque en Firestore vive aparte: los
   * colores están en `branding` y la imagen en el campo `logo` de la raíz del
   * documento, que es de donde la lee la página pública. Mezclarlas acá
   * obligaría a separarlas de nuevo antes de guardar.
   */
  const [logoDelNegocio, setLogoDelNegocio] = useState(null);

  // Estados para datos bancarios
  const [bankingData, setBankingData] = useState({
    cbu: '',
    alias: '',
    bankName: '',
    accountHolder: '',
    accountType: 'corriente'
  });
  const [savingBankingData, setSavingBankingData] = useState(false);
  
  // Estado para modal de confirmación de cancelación
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);

  // Borrado definitivo de un turno. Es otra cosa que cancelar —ver el comentario
  // de `borrarTurno`— y por eso tiene su propio estado y su propio diálogo.
  const [turnoABorrar, setTurnoABorrar] = useState(null);
  const [borrandoTurno, setBorrandoTurno] = useState(false);

  // Estados para secciones colapsables de configuración
  // Cuál de las secciones de Configuración está abierta, o null si están todas
  // cerradas.
  //
  // Antes era un objeto con un booleano por sección, así que se podían abrir
  // las cuatro a la vez y la pantalla quedaba con cuatro formularios largos
  // uno abajo del otro. Ahora es acordeón de verdad: abrir una cierra la que
  // estaba, que es como se comportan los pasos plegables del formulario de
  // servicio.
  const [seccionAbierta, setSeccionAbierta] = useState(null);

  const toggleSettingsSection = (section) => {
    setSeccionAbierta(actual => (actual === section ? null : section));
  };

  /**
   * Pinta el panel con los colores del negocio.
   *
   * Es `aplicarBranding` de lib/branding.js, el mismo que usa la página pública.
   * Acá había una copia escrita a mano que se había quedado corta en las dos
   * cosas que hacen que el color se vea: no escribía los tokens de shadcn
   * (`--primary`, `--ring`), así que todos los `<Button>` del panel seguían
   * naranjas, ni la paleta (`--aturno-orange` y compañía), que es lo que lee el
   * CSS a mano del calendario, la barra lateral y las pestañas. Escribía sólo
   * `--brand-*`, que en el panel lo leen cuatro reglas.
   */
  const applyBrandingColors = (branding) => aplicarBranding(branding || {});

  // Cargar colores guardados al inicializar
  useEffect(() => {
    const loadSavedBranding = async () => {
      try {
        // Intentar cargar desde la base de datos primero
        if (currentUser) {
          try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/api/business/branding`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const branding = await response.json();
              setBusinessBranding(branding);
              applyBrandingColors(branding);
              return; // Salir si se cargó exitosamente desde la base de datos
            }
          } catch (dbError) {
            console.warn('Error loading branding from DB, using fallback:', dbError);
          }
        }
        
        // Fallback: cargar desde localStorage si la base de datos falla
        const savedBranding = localStorage.getItem('aturno_branding');
        if (savedBranding) {
          const branding = JSON.parse(savedBranding);
          setBusinessBranding(branding);
          applyBrandingColors(branding);
        } else {
          // Aplicar colores por defecto
          applyBrandingColors(businessBranding);
        }
      } catch (error) {
        console.error('Error loading branding:', error);
        // Aplicar colores por defecto si hay error
        applyBrandingColors(businessBranding);
      }
    };

    // Solo cargar cuando el usuario esté disponible
    if (currentUser) {
      loadSavedBranding();
    }

    // Y se borra al salir del panel.
    //
    // Los colores se escriben inline sobre <html>, así que sin esta limpieza el
    // color del negocio se quedaba pegado al resto de la app: se volvía a la
    // landing o al login y estaban pintados con el color de su negocio hasta
    // recargar. Es la misma limpieza que ya hacía BusinessPage al desmontarse.
    return () => limpiarBranding();
  }, [currentUser]); // Se ejecuta cuando cambia currentUser

  /**
   * Repinta el panel cuando cambia el color guardado y cuando se cierra el modal.
   *
   * Lo del modal no es un detalle: BrandPreview limpia los tokens al cerrarse, y
   * tiene que hacerlo —si no, el color que alguien estuvo tanteando y descartó se
   * queda pegado a toda la app—. Pero esa limpieza también se lleva puesto el
   * color que el panel ya tenía aplicado por su cuenta. Sin esto, el negocio abre
   * "Cambiar colores", se arrepiente, cierra, y su panel vuelve a naranja hasta
   * recargar la página.
   *
   * Corre después de esa limpieza: React ejecuta primero todos los cleanups y
   * recién después los efectos.
   */
  useEffect(() => {
    if (!showBrandPreview && businessBranding) {
      applyBrandingColors(businessBranding);
    }
  }, [showBrandPreview, businessBranding]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar integraciones reales desde el backend
  const loadIntegrationsFromBackend = useCallback(async () => {
    if (!currentUser) return;
    
    // Código removido: ya no manejamos desconexiones localmente
    
    console.log('🔄 [INTEGRATIONS DEBUG] ========== CARGANDO INTEGRACIONES ==========');
    
    try {
      const token = await currentUser.getIdToken();
      console.log('🎫 [INTEGRATIONS DEBUG] Token obtenido:', token ? 'SÍ' : 'NO');
      
      const endpoint = `${API_URL}/api/integrations`;
      console.log('📡 [INTEGRATIONS DEBUG] Llamando a:', endpoint);
      
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📨 [INTEGRATIONS DEBUG] Response status:', response.status);
      
      if (response.ok) {
        const backendIntegrations = await response.json();
        console.log('📦 [INTEGRATIONS DEBUG] Datos del backend:', backendIntegrations);
        
        // Actualizar estado con los datos del backend
        setIntegrations(prev => {
          console.log('🔍 [INTEGRATIONS DEBUG] Estado anterior:', prev);
          const newState = { ...prev };
          
          Object.keys(backendIntegrations).forEach(key => {
            console.log(`🔧 [INTEGRATIONS DEBUG] Actualizando ${key}:`, {
              connected: backendIntegrations[key].connected,
              email: backendIntegrations[key].email || '',
              user_id: backendIntegrations[key].user_id || ''
            });
            
            newState[key] = {
              ...prev[key],
              connected: backendIntegrations[key].connected,
              email: backendIntegrations[key].email || '',
              user_id: backendIntegrations[key].user_id || '',
              active: backendIntegrations[key].connected
            };
          });
          
          console.log('✅ [INTEGRATIONS DEBUG] Nuevo estado final:', newState);
          return newState;
        });
      } else {
        console.error('❌ [INTEGRATIONS DEBUG] Error en response:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ [INTEGRATIONS DEBUG] Error body:', errorText);
      }
    } catch (error) {
      console.error('❌ [INTEGRATIONS DEBUG] Error al cargar integraciones:', error);
    }
  }, [currentUser]); // Removido: disconnectingIntegration

  // Cargar integraciones del backend cuando el usuario esté listo
  useEffect(() => {
    loadIntegrationsFromBackend();
  }, [loadIntegrationsFromBackend]);

  // Escuchar mensajes de ventanas OAuth
  useEffect(() => {
    const handleWindowMessage = (event) => {
      console.log('📨 [OAUTH DEBUG] Mensaje de ventana recibido:', {
        data: event.data,
        origin: event.origin,
        source: event.source
      });

      // Manejar mensajes de Google Calendar
      if (event.data === 'google_calendar_connected') {
        console.log('✅ [OAUTH DEBUG] Google Calendar conectado via postMessage!');
        
        setIntegrations(prev => {
          console.log('🔄 [OAUTH DEBUG] Estado antes (postMessage):', prev);
          const newState = {
            ...prev,
            googleCalendar: {
              ...prev.googleCalendar,
              connected: true,
              active: true
            }
          };
          console.log('✅ [OAUTH DEBUG] Estado después (postMessage):', newState);
          return newState;
        });

        // Recargar integraciones
        setTimeout(() => {
          console.log('🔄 [OAUTH DEBUG] Recargando integraciones después de postMessage...');
          loadIntegrationsFromBackend();
        }, 1500);
      } else if (event.data === 'google_calendar_error') {
        console.error('❌ [OAUTH DEBUG] Error en Google Calendar via postMessage');
      }
    };

    window.addEventListener('message', handleWindowMessage);
    console.log('👂 [OAUTH DEBUG] Listener de mensajes añadido');

    return () => {
      window.removeEventListener('message', handleWindowMessage);
      console.log('🔇 [OAUTH DEBUG] Listener de mensajes removido');
    };
  }, [loadIntegrationsFromBackend]);

  // Cargar datos bancarios al inicializar
  useEffect(() => {
    const loadBankingData = async () => {
      if (!currentUser) return;
      
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_URL}/api/business/banking-data`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setBankingData(data.bankingData);
        }
      } catch (error) {
        console.error('Error loading banking data:', error);
      }
    };

    // Solo cargar cuando el usuario esté disponible
    if (currentUser) {
      loadBankingData();
    }
  }, [currentUser]);

  // ===============================================
  // INICIO: Lógica para cargar datos del backend
  // ===============================================

  // --- NUEVA LÓGICA DE HORARIOS ---
  // Función para inicializar los horarios con el nuevo formato de rangos
  const initializeSchedule = (scheduleData) => {
    const newSchedule = {};
    
    for (const day of Object.keys(dayNames)) {
      if (scheduleData && scheduleData[day] && typeof scheduleData[day] === 'object') {
        const dayData = scheduleData[day];
        
        // PRIORIDAD 1: Si ya tiene el formato de rangos, lo usa directamente
        if (dayData.ranges && Array.isArray(dayData.ranges)) {
          newSchedule[day] = {
            enabled: dayData.enabled || false,
            ranges: dayData.ranges.filter(range => range && range.start && range.end)
          };
        }
        // PRIORIDAD 2: Si tiene formato antiguo (start/end), lo convierte SOLO si no hay ranges
        else if (dayData.start && dayData.end && !dayData.ranges) {
          newSchedule[day] = {
            enabled: dayData.enabled || false,
            ranges: [{ start: dayData.start, end: dayData.end }]
          };
        }
        // PRIORIDAD 3: Si solo tiene enabled, ranges vacío
        else if (dayData.hasOwnProperty('enabled')) {
          newSchedule[day] = {
            enabled: dayData.enabled || false,
            ranges: []
          };
        }
        // Fallback
        else {
          newSchedule[day] = { enabled: false, ranges: [] };
        }
      } else {
        // Si no hay datos, lo inicializa vacío
        newSchedule[day] = { enabled: false, ranges: [] };
      }
    }
    
    return newSchedule;
  };

  useEffect(() => {
    // En modo demo, usar datos hardcodeados
    if (isDemo) {
      const demoData = {
        name: 'Peluquería StyleDemo',
        slug: 'demo-business',
        email: 'demo@aturno.com',
        phone: '+54 11 1234-5678',
        address: 'Av. Demo 123, Buenos Aires',
        description: 'Negocio de demostración',
        services: [
          {
            id: 'service-1',
            name: 'Corte de Pelo',
            duration: 30,
            price: 1500,
            description: 'Corte de pelo profesional'
          },
          {
            id: 'service-2',
            name: 'Corte + Barba',
            duration: 45,
            price: 2000,
            description: 'Corte de pelo y arreglo de barba'
          },
          {
            id: 'service-3',
            name: 'Coloración',
            duration: 120,
            price: 5000,
            description: 'Coloración completa'
          }
        ],
        staff: [
          { id: 'staff-1', name: 'Juan Demo', active: true },
          { id: 'staff-2', name: 'María Demo', active: true }
        ],
        resources: [],
        schedule: {
          monday: { enabled: true, ranges: [{start: '09:00', end: '20:00'}] },
          tuesday: { enabled: true, ranges: [{start: '09:00', end: '20:00'}] },
          wednesday: { enabled: true, ranges: [{start: '09:00', end: '20:00'}] },
          thursday: { enabled: true, ranges: [{start: '09:00', end: '20:00'}] },
          friday: { enabled: true, ranges: [{start: '09:00', end: '20:00'}] },
          saturday: { enabled: true, ranges: [{start: '10:00', end: '18:00'}] },
          sunday: { enabled: false, ranges: [] }
        },
        /* Ver la nota en BusinessPage.jsx: la misma terna, importada del único
           lugar donde vive el color de Aturno. Escrita a mano acá, el panel de
           la demo salía violeta mientras la página pública del mismo negocio
           salía naranja. */
        branding: { ...COLORES_POR_DEFECTO }
      };
      
      const infoDemo = {
        name: demoData.name,
        email: demoData.email,
        slug: demoData.slug,
        address: demoData.address,
        phone: demoData.phone,
        description: demoData.description,
        businessType: 'beauty',
        schedule: demoData.schedule
      };
      setBusinessInfo(infoDemo);
      setHorarioGuardado(demoData.schedule);
      // La demo también necesita su referencia. Sin esto `datosGuardados` queda
      // en `{}`, y comparar el negocio de demostración contra un objeto vacío da
      // "tenés cambios sin guardar" apenas se abre: la demo quedaría preguntando
      // si guardar cada vez que se cambia de sección, sin que nadie haya tocado
      // nada.
      setDatosGuardados(infoDemo);
      setBusinessData(demoData);
      setServices(demoData.services);
      setStaff(demoData.staff);
      setResources(demoData.resources);
      setSchedule(demoData.schedule);
      setBusinessBranding(demoData.branding);
      applyBrandingColors(demoData.branding);
      
      // Usar reservas del contexto demo
      const processedBookings = (demoBookings || []).map(booking => ({
        id: booking.id,
        time: booking.time,
        date: booking.date,
        customerName: booking.customerName,
        email: booking.customerEmail,
        phone: booking.customerPhone,
        service: booking.serviceName,
        duration: booking.duration,
        price: booking.price,
        status: booking.status || 'confirmed',
        staff: booking.staffName,
        assignedStaff: booking.staffId,
        // El lugar elegido no se mapeaba, así que el panel mostraba
        // "Cualquier lugar" aunque el cliente hubiera elegido uno. El nombre
        // del campo importa: el renderizado busca `resourceName`.
        resourceName: booking.resourceName,
        code: booking.code,
        // Sin estos cinco la demo nunca enciende las ramas de la tarjeta: no
        // sale el "Reservado hace…", ni la línea de paciente, ni la retención
        // de la seña. La demo es donde se revisa esta pantalla sin una cuenta
        // real, así que tiene que poder mostrarlas.
        createdAt: booking.createdAt,
        dni: booking.dni,
        obraSocial: booking.obraSocial,
        numeroDeAfiliado: booking.numeroDeAfiliado,
        holdExpiresAt: booking.holdExpiresAt
      }));
      setAppointments(processedBookings);
      
      setBusinessDataLoaded(true);
      setLoading(false);
      return;
    }
    
    if (!authLoading && currentUser && userData) {
      const fetchDashboardData = async () => {
        // Verificar si el plan fue actualizado
        const planUpdated = localStorage.getItem('planUpdated');
        if (planUpdated) {
          console.log('🔄 Plan actualizado detectado, refrescando datos...');
          localStorage.removeItem('planUpdated');
        }
        
        setLoading(true);
        setError("");
        try {
          // Get business data and bookings in parallel
          const token = await currentUser.getIdToken();
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };
          
          console.log('🔄 DASHBOARD: Cargando datos del negocio y reservas...');
          
          // Make parallel requests to get business data and bookings
          const [businessResponse, bookingsResponse, subscriptionResponse, blockedSlotsResponse] = await Promise.all([
            fetch(`${API_URL}/api/business`, { headers }),
            fetch(`${API_URL}/api/bookings?businessId=${currentUser.uid}`, { headers }),
            fetch(`${API_URL}/api/subscriptions/current`, { headers }),
            fetch(`${API_URL}/api/blocked-slots?businessId=${currentUser.uid}`, { headers })
          ]);
          
          if (!businessResponse.ok) {
            throw new Error(`HTTP ${businessResponse.status}: ${businessResponse.statusText}`);
          }
          
          const dashboardData = await businessResponse.json();
          console.log('📊 Dashboard data received:', dashboardData);
          
          // Process business data
          if (dashboardData.business) {
            const businessData = dashboardData.business;
            
            // Set basic data
            //
            // Estos servicios son los de la subcolección `businesses/{uid}/services`,
            // que es donde escriben el alta, la edición y el borrado. Cuando esta
            // lista venía del array del documento, los ids no coincidían con los de
            // la subcolección y editar contestaba 404 (ver `leerServiciosDelNegocio`
            // en el backend). El log deja a mano los ids, que es lo que hay que
            // comparar si vuelve a aparecer un 404 al guardar.
            console.log(`📋 Servicios cargados: ${(businessData.services || []).length}`,
              (businessData.services || []).map(s => `${s.id}:${s.name}`));
            setServices(businessData.services || []);
            setSchedule(businessData.schedule || {});
            setBusinessData(businessData);
            
            // Set business info
            const businessInfo = businessData.businessInfo || {};
            const infoInicial = {
              name: businessInfo.name || businessData.name || "",
              email: businessInfo.email || businessData.email || "",
              slug: businessInfo.slug || businessData.slug || "mi-negocio",  // Priorizar businessInfo.slug
              address: businessInfo.address || businessData.address || "Añadir dirección",
              // Los campos SUELTOS de la dirección, que son los que dibuja
              // BusinessInfoForm: calle, piso, ciudad, código postal, provincia
              // y país. `address` es el string armado a partir de ellos, y es lo
              // único que se copiaba: el formulario del panel leía `value.street`
              // y compañía, que acá no existían, así que la sección Dirección
              // aparecía vacía aunque el negocio la hubiera cargado entera en el
              // onboarding — se veía bien en su página pública y en blanco en su
              // propio panel.
              //
              // Y no era sólo visual: en cuanto tocaba UNO de esos campos, el
              // efecto de BusinessInfoForm rearma `address` con lo poco que hay
              // cargado y pisa la dirección completa que estaba guardada.
              street: businessInfo.street || "",
              floor: businessInfo.floor || "",
              city: businessInfo.city || "",
              postalCode: businessInfo.postalCode || "",
              province: businessInfo.province || "",
              country: businessInfo.country || "Argentina",
              phone: businessInfo.phone || businessData.phone || "Añadir teléfono",
              userType: businessData.userType || "business",
              description: businessInfo.description || businessData.description || "",
              mensaje: businessInfo.mensaje || businessData.mensaje || "",
              // El rubro elegido en el onboarding. Se lee de las dos formas por
              // el mismo motivo que el resto: el onboarding lo dejó en la raíz y
              // desde que el panel lo puede editar también vive en
              // `businessInfo` (ver backend/src/contacto.js).
              businessType: businessInfo.businessType || businessData.businessType || "",
              schedule: initializeSchedule(businessData.schedule || {})
            };
            setBusinessInfo(infoInicial);
            // La referencia para "¿hay cambios?" se toma ya normalizada, igual
            // que la copia editable: si acá se guardara el horario crudo del
            // servidor, un negocio con el formato viejo (`start`/`end` sin
            // `ranges`) figuraría con cambios sin guardar apenas se abre el panel.
            setHorarioGuardado(initializeSchedule(businessData.schedule || {}));
            // Y la misma referencia para la configuración. Se guarda el MISMO
            // objeto que se acaba de poner en pantalla, no lo que vino crudo del
            // servidor: los valores de relleno ("Añadir teléfono") y los
            // fallbacks los pone esta función, así que comparar contra el crudo
            // daría cambios pendientes apenas se abre el panel.
            //
            // Se guarda tal cual, sin normalizar: además de servir para comparar
            // es lo que repone "Salir sin guardar", y ahí sí hacen falta los
            // rellenos —restaurar `''` dejaría el formulario en blanco en vez de
            // como estaba. Normalizar es cosa de la comparación, y de eso se
            // encarga `mismosDatosDelNegocio`.
            setDatosGuardados(infoInicial);

            // Set staff and resources
            let staffWithSchedule = (businessData.staff || []).map(person => ({
              ...person,
              schedule: person.schedule || {
                monday: { enabled: false, ranges: [] },
                tuesday: { enabled: false, ranges: [] },
                wednesday: { enabled: false, ranges: [] },
                thursday: { enabled: false, ranges: [] },
                friday: { enabled: false, ranges: [] },
                saturday: { enabled: false, ranges: [] },
                sunday: { enabled: false, ranges: [] }
              }
            }));
            
            let resourcesWithSchedule = (businessData.resources || []).map(resource => ({
              ...resource,
              schedule: resource.schedule || {
                monday: { enabled: false, ranges: [] },
                tuesday: { enabled: false, ranges: [] },
                wednesday: { enabled: false, ranges: [] },
                thursday: { enabled: false, ranges: [] },
                friday: { enabled: false, ranges: [] },
                saturday: { enabled: false, ranges: [] },
                sunday: { enabled: false, ranges: [] }
              }
            }));

            // 🔧 LÓGICA PARA CUENTAS PERSONALES: Auto-crear staff y lugar si no existen
            // Determinar si es cuenta individual basado en el plan, no en userType
            const planType = userPlan?.planType || userPlan?.plan?.id;
            const hasBusinessPlan = planType && planType.startsWith('business');
            const isIndividualAccount = businessData.userType === 'individual' && !hasBusinessPlan;
            if (isIndividualAccount) {
              const fullDaySchedule = {
                monday: { enabled: true, ranges: [{ start: '00:00', end: '23:59' }] },
                tuesday: { enabled: true, ranges: [{ start: '00:00', end: '23:59' }] },
                wednesday: { enabled: true, ranges: [{ start: '00:00', end: '23:59' }] },
                thursday: { enabled: true, ranges: [{ start: '00:00', end: '23:59' }] },
                friday: { enabled: true, ranges: [{ start: '00:00', end: '23:59' }] },
                saturday: { enabled: true, ranges: [{ start: '00:00', end: '23:59' }] },
                sunday: { enabled: true, ranges: [{ start: '00:00', end: '23:59' }] }
              };

              // Auto-crear staff personal si no existe
              if (staffWithSchedule.length === 0) {
                const personalStaff = {
                  id: 'personal-staff',
                  name: businessInfo.name || currentUser.displayName || 'Yo',
                  active: true,
                  schedule: fullDaySchedule,
                  isPersonal: true
                };
                staffWithSchedule = [personalStaff];
                console.log('🏠 Staff personal auto-creado para cuenta personal');
              }

              // Auto-crear lugar personal si no existe
              if (resourcesWithSchedule.length === 0) {
                const personalLocation = {
                  id: 'personal-location',
                  name: 'Mi lugar',
                  type: 'location',
                  active: true,
                  schedule: fullDaySchedule,
                  isPersonal: true
                };
                resourcesWithSchedule = [personalLocation];
                console.log('🏠 Lugar personal auto-creado para cuenta personal');
              }
            }
            
            setStaff(staffWithSchedule);
            setResources(resourcesWithSchedule);
            setBusinessBranding(businessData.branding || { primaryColor: '#007bff', logo: null });
            // Las coberturas que ya tenía cargadas. Sin esto los tildes arrancan
            // vacíos aunque el negocio tenga cuatro guardadas, y el primer
            // "Guardar coberturas" se las borra todas.
            setObrasSocialesDelNegocio(
              Array.isArray(businessData.obrasSociales) ? businessData.obrasSociales : []
            );
            setObrasPropias(
              Array.isArray(businessData.obrasSocialesPropias) ? businessData.obrasSocialesPropias : []
            );

            // Load real branding from onboarding
            const brandingData = businessData?.branding || {
              primaryColor: '#D84315',
              secondaryColor: '#C2185B',
              accentColor: '#4A148C'
            };
            setBusinessBranding(brandingData);
            applyBrandingColors(brandingData);

            // Las dos claves porque la página pública también lee las dos
            // (`profileImage` primero): así el panel muestra la misma imagen
            // que se ve afuera, sea cual sea la que tenga guardada.
            setLogoDelNegocio(businessData.profileImage || businessData.logo || null);
            
            // Mark that business data has been loaded successfully
            setBusinessDataLoaded(true);
          } else {
            throw new Error("No se pudo cargar la información del negocio.");
          }
          
          // Process bookings data from separate endpoint
          if (bookingsResponse.ok) {
            const bookingsData = await bookingsResponse.json();
            console.log('📊 Bookings data received:', bookingsData);
            
            /* El remapeo vive en lib/turnos.js porque ahora los turnos entran
               por dos caminos —este fetch y el listener de Firestore— y los dos
               tienen que dejarlos en la misma forma. Escrito acá adentro, un
               turno que llegara en vivo se dibujaba sin nombre de cliente y con
               un objeto donde va el servicio. */
            const processedBookings = ordenarTurnos(bookingsData.map(aTurnoDePanel));
            setAppointments(processedBookings);
            console.log('✅ Bookings processed and set:', processedBookings.length, 'appointments');
          } else {
            console.log('⚠️ Could not load bookings:', bookingsResponse.status);
            setAppointments([]);
          }
          
          // Process blocked slots data
          if (dashboardData.blockedSlots) {
            setBlockedSlots(dashboardData.blockedSlots);
          } else if (blockedSlotsResponse.ok) {
            const blockedSlotsData = await blockedSlotsResponse.json();
            setBlockedSlots(blockedSlotsData);
            console.log('🔒 Horarios bloqueados cargados:', blockedSlotsData);
          }
          
          // Process branding data
          if (dashboardData.branding) {
            setBusinessBranding(dashboardData.branding);
            applyBrandingColors(dashboardData.branding);
          }
          
          // Process integrations data
          if (dashboardData.integrations) {
            setIntegrations(prev => ({
              ...prev,
              ...Object.keys(dashboardData.integrations).reduce((acc, key) => {
                acc[key] = {
                  ...prev[key],
                  connected: dashboardData.integrations[key].connected,
                  email: dashboardData.integrations[key].email
                };
                return acc;
              }, {})
            }));
          }
          
          // Process banking data
          if (dashboardData.bankingData) {
            setBankingData(dashboardData.bankingData);
          }
          
          // Load user plan for feature access control
          try {
            const token = await currentUser.getIdToken();
            const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };
            const planResponse = await fetch(`${API_URL}/api/plan/current`, { headers });
            
            if (planResponse.ok) {
              const planData = await planResponse.json();
              setUserPlan(planData);
            } else {
              setUserPlan({ plan: { name: 'Free' } });
            }
          } catch (planError) {
            console.error('❌ DASHBOARD: Error cargando plan del usuario:', planError);
            setUserPlan({ plan: { name: 'Free' } });
          }
          
          // Process subscription data
          if (subscriptionResponse.ok) {
            const subscriptionData = await subscriptionResponse.json();
            console.log('📊 Subscription data received:', subscriptionData);
            
            // Verificar si hay información temporal de display en localStorage
            const tempPlanDisplay = localStorage.getItem('temp_plan_display');
            if (tempPlanDisplay) {
              try {
                const planDisplayInfo = JSON.parse(tempPlanDisplay);
                // Solo usar si es reciente (menos de 5 minutos)
                if (Date.now() - planDisplayInfo.timestamp < 5 * 60 * 1000) {
                  console.log('🔄 Usando display name temporal:', planDisplayInfo.displayName);
                  subscriptionData.planName = planDisplayInfo.displayName;
                  subscriptionData.originalPlanName = subscriptionData.planName; // Backup
                }
                // Limpiar después de usar
                localStorage.removeItem('temp_plan_display');
              } catch (error) {
                console.warn('⚠️ Error parsing temp plan display:', error);
                localStorage.removeItem('temp_plan_display');
              }
            }
            
            setSubscriptionData(subscriptionData);
            
            // Verificar si necesitamos mostrar el overlay de pago
            const now = new Date();
            let shouldShowPaymentOverlay = false;
            let overlayReason = '';
            
            // CASO 1: Trial expirado sin plan pagado
            if (subscriptionData?.freeTrialEndDate) {
              const trialEndDate = new Date(subscriptionData.freeTrialEndDate);
              const isTrialExpired = now >= trialEndDate;
              
              const hasValidNextBilling = subscriptionData.nextBillingDate && 
                subscriptionData.nextBillingDate !== 'null' && 
                subscriptionData.nextBillingDate !== null &&
                subscriptionData.nextBillingDate !== 'No disponible';
              
              const hasRealSubscription = subscriptionData.subscriptionId && 
                !subscriptionData.subscriptionId.includes('test') &&
                !subscriptionData.subscriptionId.includes('sim') &&
                !subscriptionData.subscriptionId.includes('demo');
              
              const hasPaidPlan = subscriptionData.planType && 
                subscriptionData.planType !== null &&
                hasValidNextBilling && hasRealSubscription;
              
              if (isTrialExpired && !hasPaidPlan) {
                shouldShowPaymentOverlay = true;
                overlayReason = 'trial_expired';
              }
            }
            
            // CASO 2: Suscripción cancelada con período expirado  
            if (subscriptionData?.planStatus === 'cancelled' && subscriptionData?.nextBillingDate) {
              const accessEndDate = new Date(subscriptionData.nextBillingDate);
              const hasAccessExpired = now >= accessEndDate;
              
              if (hasAccessExpired) {
                shouldShowPaymentOverlay = true;
                overlayReason = 'subscription_expired';
              }
            }
            
            console.log('🔍 Payment overlay check:', {
              trialEndDate: subscriptionData?.freeTrialEndDate,
              planStatus: subscriptionData?.planStatus,
              nextBillingDate: subscriptionData?.nextBillingDate,
              now: now.toISOString(),
              shouldShowPaymentOverlay,
              overlayReason
            });
            
            if (shouldShowPaymentOverlay) {
              if (overlayReason === 'trial_expired') {
                console.log('🚫 Trial expirado y sin plan pagado válido, mostrando overlay');
              } else if (overlayReason === 'subscription_expired') {
                console.log('🚫 Suscripción cancelada y período expirado, mostrando overlay');
              }
              setShowTrialExpiredOverlay(true);
            } else {
              console.log('✅ Usuario tiene acceso válido');
            }
          } else {
            console.warn('⚠️ Could not load subscription data:', subscriptionResponse.status);
            setSubscriptionData(null);
          }
          setSubscriptionLoading(false);
          
          setLoading(false);
        } catch (error) {
          console.error('❌ DASHBOARD: Error en carga de datos:', error);
          setError(error.message || "Error al cargar los datos del dashboard");
          setLoading(false);
          
          // Handle rate limiting errors
          if (error.status === 429) {
            const retryAfter = error.retryAfter || 15000;
            console.warn(`Rate limited. Retrying in ${retryAfter / 1000} seconds...`);
            
            setTimeout(() => {
              fetchDashboardData();
            }, retryAfter);
          }
        }
      };
      
      fetchDashboardData();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [currentUser, userData, authLoading, isDemo, demoBookings, updateBookingStatus]);

  // Handle payment success from plans page
  useEffect(() => {
    const handlePaymentSuccess = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      
      if (paymentStatus === 'success') {
        // Show success message
        setMessage('¡Pago exitoso! Tu plan ha sido actualizado. Todas las nuevas funciones están disponibles.');
        
        // Clear URL parameters
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        
        // Refresh plan data to ensure new features are available
        setTimeout(async () => {
          if (currentUser) {
            try {
              const token = await currentUser.getIdToken();
              const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };
              const planResponse = await fetch(`${API_URL}/api/plan/current`, { headers });
              
              if (planResponse.ok) {
                const planData = await planResponse.json();
                setUserPlan(planData);
                console.log('🔄 Plan data refreshed after payment:', planData);
              }
            } catch (error) {
              console.error('Error refreshing plan data:', error);
            }
          }
        }, 1000);
        
        // Clear message after 5 seconds
        setTimeout(() => {
          setMessage('');
        }, 5000);
      }
    };
    
    if (currentUser) {
      handlePaymentSuccess();
    }
  }, [currentUser]);


  // Sistema responsive avanzado - Detección automática de pantalla
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      
      setIsMobile(mobile);
      
      // Auto-cerrar sidebar en pantallas grandes
      if (!mobile && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    // Detectar tamaño inicial
    handleResize();
    
    // Escuchar cambios de tamaño
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isSidebarOpen]);

  /**
   * Diagnóstico de la grilla de servicios (sólo en desarrollo).
   *
   * Imprime cuántas columnas está resolviendo la grilla y de qué ancho, para
   * poder verificar el tope de 3 sin abrir el inspector. `gridTemplateColumns`
   * computado devuelve un ancho por columna ("482px 482px 482px"), así que
   * contar los valores es contar las columnas.
   *
   * Se mide el ancho de la grilla y no el de la ventana: al lado hay un menú
   * de 280px que existe arriba de 1024px y desaparece abajo, así que la misma
   * ventana da dos anchos de grilla distintos según la pantalla.
   */
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (activeSection !== 'services') return;

    const medirGrilla = () => {
      const grilla = document.querySelector('.services-grid');
      if (!grilla) return;

      const columnas = getComputedStyle(grilla)
        .gridTemplateColumns
        .split(' ')
        .filter(Boolean);
      const anchoGrilla = Math.round(grilla.getBoundingClientRect().width);

      console.log(
        `📐 Grilla de servicios: ${columnas.length} columna(s) de ${columnas[0]} · ` +
        `grilla ${anchoGrilla}px · ventana ${window.innerWidth}px · ` +
        `menú lateral ${window.innerWidth > 1024 ? 'visible (280px)' : 'oculto'}`
      );
    };

    medirGrilla();
    window.addEventListener('resize', medirGrilla);
    return () => window.removeEventListener('resize', medirGrilla);
  }, [activeSection, services.length]);

  // Qué modal está abierto, para el diagnóstico de abajo.
  const modalAbierto =
    (showEditableCard && (editingService ? 'editar servicio' : 'agregar servicio')) ||
    (showNewStaffForm && 'agregar personal') ||
    (showNewResourceForm && 'agregar recurso') ||
    (editingStaff && 'editar horarios de personal') ||
    (editingResource && 'editar horarios de recurso') ||
    (showBrandPreview && 'vista previa de marca') ||
    null;

  /**
   * Diagnóstico de la animación de apertura (sólo en desarrollo).
   *
   * Lee del estilo computado si la caja del modal quedó con una animación o si
   * alguna regla se la apagó. Existe porque el panel tiene un bloque en
   * Dashboard.css llamado "ELIMINAR TODOS LOS POP-UP EFFECTS" que pone
   * `animation: none !important` sobre todo lo que cuelgue de
   * `.dashboard-container`: si alguien agrega una animación nueva y no la
   * escribe con más especificidad que ese bloque, no se va a ver, y desde el
   * código va a parecer que está bien puesta.
   */
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (!modalAbierto) return;

    // El modal se monta en este mismo render: antes del próximo cuadro todavía
    // no está en el documento.
    const cuadro = requestAnimationFrame(() => {
      const caja = document.querySelector(
        '.modal-overlay .modal-content, .brand-preview-overlay .brand-preview-container'
      );

      if (!caja) {
        console.warn(`🎬 "${modalAbierto}": no encontré la caja del modal en el DOM`);
        return;
      }

      const estilo = getComputedStyle(caja);
      if (estilo.animationName === 'none') {
        console.warn(
          `🎬 Modal "${modalAbierto}" → ❌ sin animación. ` +
          `Alguna regla la está apagando (mirá el bloque anti pop-up de Dashboard.css).`
        );
      } else {
        console.log(
          `🎬 Modal "${modalAbierto}" → ✅ ${estilo.animationName} ${estilo.animationDuration}`
        );
      }
    });

    return () => cancelAnimationFrame(cuadro);
  }, [modalAbierto]);

  // Cerrar sidebar al hacer clic fuera (solo en móvil)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isSidebarOpen) {
        const sidebar = document.querySelector('.dashboard-sidebar');
        const hamburger = document.querySelector('.hamburger-btn');
        
        if (sidebar && !sidebar.contains(event.target) && 
            hamburger && !hamburger.contains(event.target)) {
          setIsSidebarOpen(false);
        }
      }
    };

    if (isMobile && isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobile, isSidebarOpen]);

  // Función para validar y limpiar datos de servicios (sin normalizar precios)
  const validateServiceData = (services) => {
    let hasIssues = false;
    const validatedServices = services.map(service => {
      // Solo validar que el precio sea un número válido, no normalizarlo
      if (service.price && (isNaN(service.price) || service.price < 0)) {
        // Invalid price detected
        hasIssues = true;
        return {
          ...service,
          price: 0 // Solo establecer en 0 si es realmente inválido
        };
      }
      return service;
    });
    
    // Solo mostrar notificación si hay problemas reales
    if (hasIssues) {
      // Invalid prices were detected and corrected
    }
    
    return validatedServices;
  };

  // 🧪 Auto-crear turno pasado para testing del botón y estadísticas
  useEffect(() => {
    // Solo crear el turno de prueba si no existe ya y hay appointments cargados
    if (appointments.length > 0 && !appointments.find(a => a.id === 'test-past-appointment')) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      // Buscar un servicio existente para usar su precio real
      const existingService = services.find(s => s.active && s.price);
      const serviceName = existingService ? existingService.name : 'Servicio de Prueba';
      
      const testPastAppointment = {
        id: 'test-past-appointment',
        date: aFechaISO(yesterday),
        time: '14:30',
        customerName: 'Cliente de Prueba (Pasado)',
        service: serviceName,
        phone: '+5491234567890',
        status: 'confirmed',
        staff: 'Personal de Prueba',
        resource: 'Recurso de Prueba'
      };
      
      setAppointments(prev => [...prev, testPastAppointment]);
              // Test past appointment created
    }
  }, [appointments.length, services]);
  
  // --- Fin de carga de datos ---

  // useEffect para sincronizar staff y resources cuando cambien
  useEffect(() => {
    const syncAfterUpdate = async () => {
      if (currentUser && (staff.length > 0 || resources.length > 0)) {
        try {
          const token = await currentUser.getIdToken();
                await syncStaffAndResources(token);
    } catch (error) {
          console.error('Error syncing staff and resources:', error);
        }
      }
    };
    
    // Solo sincronizar si ya se cargaron los datos iniciales
    if (!loading && currentUser) {
      syncAfterUpdate();
    }
  }, [staff, resources, currentUser, loading]);

  // Función para forzar sincronización manual
  const forceSyncData = async () => {
    if (currentUser) {
      try {
        setSyncStatus("syncing");
        const token = await currentUser.getIdToken();
        await syncStaffAndResources(token);
        setSyncStatus("success");
        setLastSyncTime(new Date());
        
        // Limpiar mensaje de éxito después de 3 segundos
        setTimeout(() => setSyncStatus(""), 3000);
      } catch (error) {
        console.error('Error in force sync:', error);
        setSyncStatus("error");
        setTimeout(() => setSyncStatus(""), 3000);
      }
    }
  };

  // Función para sincronizar staff y resources con el backend
  const syncStaffAndResources = async (token) => {
    try {
      const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };
      
      // Sincronizar staff siempre (aunque esté vacío)
      const staffResponse = await fetch(`${API_URL}/api/sync/staff`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ staff })
      });

      if (staffResponse.ok) {
        // Staff synced successfully
      } else {
        const errorText = await staffResponse.text();
        console.error('Failed to sync staff:', errorText);
      }

      // Sincronizar resources siempre (aunque esté vacío)
      const resourcesResponse = await fetch(`${API_URL}/api/sync/resources`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ resources })
      });

      if (resourcesResponse.ok) {
        // Resources synced successfully
      } else {
        const errorText = await resourcesResponse.text();
        console.error('Failed to sync resources:', errorText);
      }

    } catch (error) {
      console.error('Error in syncStaffAndResources:', error);
    }
  };

  /**
   * Guarda el personal y devuelve si salió bien.
   *
   * Devuelve un booleano —antes no devolvía nada— porque de eso depende
   * "Guardar y salir": si el guardado falla, irse igual sería tirar los cambios
   * justo después de que alguien pidió explícitamente que se guarden.
   *
   * `lista` es opcional y sirve para guardar algo que todavía no está en el
   * estado: `setStaff` no es inmediato, así que el que agrega una persona y
   * llama a guardar en la misma vuelta mandaría la lista sin ella.
   */
  const saveStaffChanges = async (lista) => {
    if (savingStaff) return false; // Evitar múltiples llamadas simultáneas

    const aGuardar = lista || staff;

    // En la demo no hay servidor: se acepta el cambio de local para que la
    // pantalla se comporte igual que la de verdad.
    if (isDemo) return true;

    setSavingStaff(true);
    try {
      const token = await currentUser.getIdToken();
      const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };

      const response = await fetch(`${API_URL}/api/sync/staff`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ staff: aGuardar })
      });

      if (response.ok) {
        return true;
      }

      console.error('❌ DASHBOARD: Error al guardar staff:', response.status, response.statusText);
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ DASHBOARD: Error details:', errorData);
      toast.error('No pudimos guardar el personal', {
        description: errorData.message || errorData.error || 'Probá de nuevo en un momento.',
      });
      return false;
    } catch (error) {
      console.error('❌ DASHBOARD: Error de conexión al guardar staff:', error);
      toast.error('No pudimos guardar el personal', {
        description: 'Revisá tu conexión y probá de nuevo.',
      });
      return false;
    } finally {
      setSavingStaff(false);
    }
  };

  // Función para guardar cambios de resources en el backend
  const saveResourceChanges = async () => {
    if (savingResources) return; // Evitar múltiples llamadas simultáneas
    
    setSavingResources(true);
    try {
      const token = await currentUser.getIdToken();
      const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };
      
      const response = await fetch(`${API_URL}/api/sync/resources`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ resources })
      });

      if (response.ok) {
        // Resources saved successfully
      } else {
        console.error('❌ DASHBOARD: Error al guardar resources:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ DASHBOARD: Error de conexión al guardar resources:', error);
    } finally {
      setSavingResources(false);
    }
  };

  // Función para toggle del sidebar
  const toggleSidebar = () => {
    const newState = !isSidebarOpen;
    setIsSidebarOpen(newState);
  };

  // Función para cambiar sección y cerrar sidebar en móvil
  const handleSectionChange = (section) => {
    // Changing to section
    
    // Verificar acceso a Personal y Recursos
    if (section === "resources" && !canAccessStaffAndResources()) {
      // Access denied to Staff and Resources - redirecting to Services
      setActiveSection("services");
      return;
    }
    
    // Salir de Horarios con cambios sin guardar es la forma más fácil de
    // perderlos, porque el panel lateral está siempre a la vista. Se pregunta y
    // el cambio de sección espera la respuesta: el destino queda anotado en
    // `salidaPendiente` y lo retoma `seguirSalida`.
    if (hayCambiosSinGuardar && section !== activeSection) {
      setSalidaPendiente({ tipo: 'seccion', seccion: section });
      return;
    }

    setActiveSection(section);
    // Section changed

    // Si estamos en móvil y el sidebar está abierto, cerrarlo
    if (isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveSection(tab);
    }
  }, [location.search]);

  // Verificar si estamos en modo debug
  const isDebugMode = () => {
    const params = new URLSearchParams(location.search);
    return params.get('debug') === 'true';
  };

  // Manejar callbacks de OAuth (MercadoPago y Google Calendar)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔍 [GOOGLE DEBUG] URL Params:', Object.fromEntries(urlParams));
    
    // Check for Google Calendar callback
    const googleStatus = urlParams.get('google');
    const googleCalendarStatus = urlParams.get('googleCalendar');
    
    if (googleStatus === 'connected' || googleCalendarStatus === 'connected') {
      console.log('✅ [GOOGLE DEBUG] Google Calendar conectado via URL callback!');
      
      // Actualizar estado de integraciones
      setIntegrations(prev => {
        console.log('🔄 [GOOGLE DEBUG] Estado antes de actualizar (callback):', prev);
        const newState = {
          ...prev,
          googleCalendar: {
            ...prev.googleCalendar,
            connected: true,
            active: true
          }
        };
        console.log('✅ [GOOGLE DEBUG] Estado después de actualizar (callback):', newState);
        return newState;
      });
      
      // Mostrar notificación de éxito
      alert('¡Google Calendar conectado exitosamente!');
      
      // Limpiar URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Recargar datos de integraciones
      setTimeout(() => {
        console.log('🔄 [GOOGLE DEBUG] Recargando integraciones después de callback...');
        loadIntegrationsFromBackend();
      }, 1000);
    }
    
    // Check for MercadoPago callback
    const mercadoPagoStatus = urlParams.get('mercadopago');
    const mercadoPagoUserId = urlParams.get('user_id');
    
    if (mercadoPagoStatus === 'connected' && mercadoPagoUserId) {
      console.log('✅ [MERCADOPAGO DEBUG] MercadoPago conectado exitosamente. User ID:', mercadoPagoUserId);
      
      // Mostrar notificación de éxito
      alert(`¡MercadoPago conectado exitosamente! ID de usuario: ${mercadoPagoUserId}`);
      
      // Actualizar estado de integraciones
      setIntegrations(prev => ({
        ...prev,
        mercadoPago: {
          ...prev.mercadoPago,
          connected: true,
          user_id: mercadoPagoUserId
        }
      }));
      
      // Limpiar URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Recargar datos de integraciones
      loadIntegrationsFromBackend();
    } else if (mercadoPagoStatus === 'error') {
      const errorMessage = urlParams.get('message') || 'Error desconocido';
      console.error('❌ [MERCADOPAGO DEBUG] Error conectando MercadoPago:', errorMessage);
      alert(`Error al conectar MercadoPago: ${errorMessage}`);
      
      // Limpiar URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // INICIO: cambios sin guardar en Horarios
  /**
   * Los horarios son la única pantalla del panel que se edita con gestos y no
   * con un formulario: se arrastra sobre la grilla y el cambio queda en pantalla
   * al instante, igual que si ya estuviera guardado. Por eso acá hace falta
   * decir en voz alta lo que en un formulario se da por sabido —que falta
   * guardar—, y no dejar salir sin preguntar: el que se va sin apretar nada
   * cree que dejó su horario cambiado, y el negocio sigue atendiendo con el
   * anterior sin que nadie se entere hasta que entra un turno a deshora.
   */
  const hayCambiosEnHorarios = useMemo(
    () => !mismoHorario(businessInfo?.schedule, horarioGuardado),
    [businessInfo?.schedule, horarioGuardado]
  );

  /**
   * Lo mismo para Configuración, y por el mismo motivo.
   *
   * Esta sección tiene el botón "Guardar cambios" arriba a la derecha, lejos de
   * los campos y fuera de la vista apenas se scrollea un poco. El nombre, la
   * dirección, el mensaje de la página pública y ahora el rubro se editan en
   * pantalla y quedan a la vista como si ya estuvieran guardados, igual que la
   * grilla de horarios: quien cambia el rubro y toca "Turnos" en el panel
   * lateral cree que lo cambió, y su página pública sigue diciendo lo de antes.
   *
   * Compara sólo los campos que se mandan al servidor, con los rellenos
   * normalizados — ver lib/negocio.js.
   */
  const hayCambiosEnConfiguracion = useMemo(
    () => !mismosDatosDelNegocio(businessInfo, datosGuardados),
    [businessInfo, datosGuardados]
  );

  /**
   * Qué se cambió, en castellano, para el aviso de salida.
   *
   * Nombrar el cambio es la diferencia entre un aviso que se lee y uno que se
   * aprende a descartar: "cambiaste el rubro" manda derecho a mirar el rubro,
   * "tenés cambios sin guardar" manda a buscar por toda la pantalla. Cuando son
   * varios no se listan los diez campos —eso es una pantalla, no un aviso—: se
   * nombran los dos primeros y el resto se resume.
   */
  const resumenDeCambiosEnConfiguracion = useMemo(() => {
    const nombres = {
      name: 'el nombre',
      email: 'el email',
      phone: 'el teléfono',
      address: 'la dirección',
      street: 'la dirección',
      floor: 'la dirección',
      city: 'la dirección',
      postalCode: 'la dirección',
      province: 'la dirección',
      country: 'la dirección',
      description: 'la descripción',
      mensaje: 'el mensaje para tus clientes',
      businessType: 'el rubro',
    };

    // Se comparan los dos ya normalizados, para que los rellenos
    // ("Añadir teléfono") no cuenten como cambio — la misma regla que usa
    // `mismosDatosDelNegocio` para decidir si avisar.
    const ahora = datosComparables(businessInfo);
    const antes = datosComparables(datosGuardados);

    const cambiados = [...new Set(
      CAMPOS_DEL_NEGOCIO
        .filter((campo) => ahora[campo] !== antes[campo])
        .map((campo) => nombres[campo])
        .filter(Boolean)
    )];

    if (cambiados.length === 0) return 'datos de tu negocio';
    if (cambiados.length === 1) return cambiados[0];
    if (cambiados.length === 2) return `${cambiados[0]} y ${cambiados[1]}`;
    return `${cambiados[0]}, ${cambiados[1]} y algún dato más`;
  }, [businessInfo, datosGuardados]);

  /**
   * El guardián de salida mira los horarios y la configuración.
   *
   * El personal se guarda solo en cada acción, así que no puede quedar nada sin
   * guardar al salir de esa sección. Lo que sí puede perderse es una edición a
   * medio hacer adentro del modal, y de eso se ocupa `pedirCerrarEdicion` unas
   * líneas más abajo — ahí y no acá, porque cerrar el modal no es salir del
   * panel.
   */
  const hayCambiosSinGuardar = hayCambiosEnHorarios || hayCambiosEnConfiguracion;

  // INICIO: cambios sin guardar DENTRO del modal de personal
  /**
   * El modal de editar personal es un caso aparte, y es el que más trabajo
   * hacía perder.
   *
   * Lo que se edita ahí no vive en `staff`: vive en `editingStaff`, una copia
   * suelta. Cerrar el modal hacía `setEditingStaff(null)` y esa copia se perdía
   * entera, sin pasar nunca por `staff` — así que el guardián de "cambios sin
   * guardar" de la sección no tenía nada que detectar y no avisaba nada. Se
   * agregaba un rol, se cerraba la ventana, y no pasaba absolutamente nada:
   * ni se guardaba ni avisaba.
   *
   * Se compara con el mismo comparador que la sección, así que vale lo mismo
   * que ahí: ignora el orden de las claves y de los roles, las tildes y los
   * timestamps del backend.
   */
  const hayCambiosEnLaEdicion = useMemo(() => {
    if (!editingStaff) return false;
    const original = staff.find((s) => String(s.id) === String(editingStaff.id));
    return !mismoPersonal([editingStaff], [original || {}]);
  }, [editingStaff, staff]);

  /** Lo mismo para el alta: se considera empezada si tiene algo cargado. */
  const hayCambiosEnElAlta = useMemo(() => {
    if (!showNewStaffForm) return false;
    return Boolean(
      newStaff.name?.trim() ||
      newStaff.email?.trim() ||
      newStaff.phone?.trim() ||
      newStaff.foto ||
      (newStaff.roles || []).length > 0
    );
  }, [showNewStaffForm, newStaff]);

  /**
   * `'edicion' | 'alta' | null`: qué modal está preguntando si descartar.
   *
   * La pregunta se hace adentro del propio modal y no con otro encima: apilar
   * un segundo modal para preguntar por el primero es donde la gente se pierde
   * de cuál está cerrando.
   */
  const [descartePendiente, setDescartePendiente] = useState(null);

  const pedirCerrarEdicion = () => {
    if (hayCambiosEnLaEdicion) return setDescartePendiente('edicion');
    setEditingStaff(null);
  };

  const pedirCerrarAlta = () => {
    if (hayCambiosEnElAlta) return setDescartePendiente('alta');
    setShowNewStaffForm(false);
  };
  // FIN: cambios sin guardar DENTRO del modal de personal

  /** El horario en blanco del que parte una persona nueva: los siete días cerrados. */
  const horarioVacio = () => ({
    monday: { enabled: false, ranges: [] },
    tuesday: { enabled: false, ranges: [] },
    wednesday: { enabled: false, ranges: [] },
    thursday: { enabled: false, ranges: [] },
    friday: { enabled: false, ranges: [] },
    saturday: { enabled: false, ranges: [] },
    sunday: { enabled: false, ranges: [] }
  });

  /**
   * Da de alta a una persona del equipo.
   *
   * Lo dispara el botón del último paso de `FormularioDePersona`, igual que en
   * el onboarding.
   *
   * DE PASO ARREGLA UN GUARDADO QUE NO GUARDABA. El código que estaba en el
   * botón hacía `setStaff(prev => [...prev, persona])` y enseguida
   * `saveStaffChanges()` SIN argumentos. `setStaff` es asíncrono, así que cuando
   * `saveStaffChanges` caía en su valor por defecto —el estado `staff`— leía la
   * lista VIEJA, sin la persona recién agregada: aparecía en pantalla y el POST
   * mandaba la lista sin ella, o sea que se perdía al recargar. Ahora la lista
   * se arma primero y se pasa explícita a las dos.
   */
  const agregarPersona = () => {
    const name = (newStaff.name || '').trim();
    if (!name) return;

    const persona = {
      id: Date.now(),
      name,
      roles: newStaff.roles || [],
      // Opcional: sin foto se muestran las iniciales.
      foto: newStaff.foto || null,
      // El campo viejo se sigue escribiendo: ver rolParaGuardar.
      rol: rolParaGuardar(newStaff.roles),
      email: (newStaff.email || '').trim(),
      phone: (newStaff.phone || '').trim(),
      active: true,
      // Copia propia: si se pasara la misma referencia, editar el horario de una
      // persona cambiaría el de todas. Y si eligió heredar el del negocio, es de
      // ahí de donde se copia — no del horario en blanco del formulario, que la
      // dejaría sin ningún día abierto.
      schedule: JSON.parse(JSON.stringify(
        newStaff.horarioPropio && newStaff.schedule
          ? newStaff.schedule
          : (businessInfo?.schedule || {})
      ))
    };

    const lista = [...staff, persona];
    setStaff(lista);
    setNewStaff({ name: '', roles: [], foto: null, email: '', phone: '', schedule: horarioVacio() });
    setShowNewStaffForm(false);
    saveStaffChanges(lista);
  };

  /**
   * Guarda la persona que se está editando.
   *
   * Está acá afuera y no escrito dentro del JSX porque ahora lo dispara el botón
   * del último paso de `FormularioDePersona` —el mismo recorrido del
   * onboarding—, y no un botón del pie del modal. El cuerpo es el que ya tenía
   * ese botón, sin cambios de comportamiento.
   */
  const guardarPersonaEditada = async () => {
    const scheduleValidation = validateSchedule(editingStaff.schedule);
    if (!scheduleValidation.isValid) {
      alert(scheduleValidation.message);
      return;
    }

    setSavingStaff(true);

    // `rol` se recalcula y no se arrastra el viejo: si no, quien edita sus roles
    // queda con la lista nueva y el texto de antes, y cada campo dice una cosa
    // distinta.
    const updatedStaff = staff.map((s) =>
      s.id === editingStaff.id
        ? { ...editingStaff, rol: rolParaGuardar(rolesDe(editingStaff)) }
        : s
    );
    setStaff(updatedStaff);

    try {
      const token = await currentUser.getIdToken();
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const response = await fetch(`${API_URL}/api/sync/staff`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ staff: updatedStaff })
      });

      if (response.ok) {
        setEditingStaff(null);
      } else {
        console.error('❌ Error al guardar el personal:', response.status, await response.text());
        alert('Error al guardar los cambios. Por favor, intenta nuevamente.');
      }
    } catch (error) {
      console.error('❌ Error de conexión al guardar staff:', error);
      alert('Error de conexión. Por favor, intenta nuevamente.');
    } finally {
      setSavingStaff(false);
    }
  };

  const guardarHorarios = async () => {
    const aGuardar = businessInfo?.schedule || {};

    // En la demo no hay servidor al que mandarlo: se acepta el cambio de local
    // para que la pantalla se comporte igual que la de verdad.
    if (isDemo) {
      setHorarioGuardado(aGuardar);
      setSchedule(aGuardar);
      return true;
    }

    setGuardandoHorarios(true);
    setScheduleError('');
    try {
      const token = await currentUser.getIdToken();
      const respuesta = await fetch(`${API_URL}/api/schedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(aGuardar),
      });

      if (!respuesta.ok) {
        throw new Error('No se pudieron guardar los horarios. Probá de nuevo.');
      }

      // Recién ahora las dos copias coinciden, así que el aviso se apaga.
      setHorarioGuardado(aGuardar);
      // `schedule` y `businessData.schedule` son de dónde lee el resto del panel
      // (`getBusinessHoursForDay`, entre otros). Sin esto, bloquear un horario
      // justo después de guardar se ofrecería contra la jornada vieja.
      setSchedule(aGuardar);
      setBusinessData(prev => (prev ? { ...prev, schedule: aGuardar } : prev));
      return true;
    } catch (err) {
      setScheduleError(err.message);
      return false;
    } finally {
      setGuardandoHorarios(false);
    }
  };

  const descartarHorarios = () => {
    setBusinessInfo(prev => ({ ...prev, schedule: horarioGuardado }));
    setScheduleError('');
  };

  /**
   * Repone la configuración como está en el servidor.
   *
   * Sólo los campos guardables: `schedule` no se toca acá porque lo repone
   * `descartarHorarios`, que sabe contra qué compararlo.
   */
  const descartarConfiguracion = () => {
    setBusinessInfo(prev => {
      const repuesto = { ...prev };
      for (const campo of CAMPOS_DEL_NEGOCIO) repuesto[campo] = datosGuardados?.[campo] ?? '';
      return repuesto;
    });
    setError('');
  };

  /**
   * Lleva a la persona a donde iba cuando se le preguntó.
   *
   * Las dos salidas se retoman distinto: la sección la manejamos nosotros, la
   * ruta la tiene frenada el router y hay que soltarla con `proceed`.
   */
  const seguirSalida = (salida) => {
    if (!salida) return;
    if (salida.tipo === 'seccion') {
      setActiveSection(salida.seccion);
      if (isMobile && isSidebarOpen) setIsSidebarOpen(false);
    } else {
      salida.continuar();
    }
  };

  const guardarYSalir = async () => {
    // `handleSaveChanges` manda la configuración Y el horario en el mismo
    // guardado, así que cuando hay cambios en Configuración alcanza con él: usar
    // los dos guardaría el horario dos veces. Si lo único pendiente es el
    // horario, se usa el guardado chico.
    const guardado = hayCambiosEnConfiguracion
      ? await handleSaveChanges({ avisar: false })
      : await guardarHorarios();

    // Si el guardado falla no se va a ningún lado: irse igual sería tirar los
    // cambios justo después de haber pedido explícitamente que se guarden. El
    // error queda a la vista, en la pantalla donde está el horario.
    if (!guardado) {
      if (salidaPendiente?.tipo === 'ruta') salidaPendiente.cancelar();
      setSalidaPendiente(null);
      return;
    }
    const salida = salidaPendiente;
    setSalidaPendiente(null);
    seguirSalida(salida);
  };

  const salirSinGuardar = () => {
    descartarHorarios();
    descartarConfiguracion();
    const salida = salidaPendiente;
    setSalidaPendiente(null);
    seguirSalida(salida);
  };

  const seguirEditando = () => {
    if (salidaPendiente?.tipo === 'ruta') salidaPendiente.cancelar();
    setSalidaPendiente(null);
  };

  /**
   * Irse del panel entero —cerrar sesión, tocar el logo, el botón de atrás—
   * también es salir de Horarios. Eso no pasa por `handleSectionChange` sino por
   * el router, que con `useBlocker` deja frenar la navegación y decidir después.
   */
  const bloqueoDeRuta = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        hayCambiosSinGuardar && currentLocation.pathname !== nextLocation.pathname,
      [hayCambiosSinGuardar]
    )
  );

  useEffect(() => {
    if (bloqueoDeRuta.state !== 'blocked') return;
    setSalidaPendiente({
      tipo: 'ruta',
      continuar: () => bloqueoDeRuta.proceed(),
      cancelar: () => bloqueoDeRuta.reset(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloqueoDeRuta.state]);

  useEffect(() => {
    if (!hayCambiosSinGuardar) return;
    // Cerrar la pestaña o recargar no pasa por el router y no hay forma de
    // frenarlo desde la aplicación: el único aviso posible es el del navegador,
    // que además elige él el texto. Por eso es el respaldo y no el mecanismo
    // principal.
    const avisarAlSalir = (evento) => {
      evento.preventDefault();
      evento.returnValue = '';
    };
    window.addEventListener('beforeunload', avisarAlSalir);
    return () => window.removeEventListener('beforeunload', avisarAlSalir);
  }, [hayCambiosSinGuardar]);

  useEffect(() => {
    if (!salidaPendiente) return;
    // Escape cierra la pregunta sin decidir nada, como tocar afuera. Nunca
    // descarta: la tecla que se aprieta para "sacame esto de encima" no puede
    // ser la que tira el trabajo.
    const alApretarTecla = (evento) => {
      if (evento.key === 'Escape' && !guardandoHorarios) seguirEditando();
    };
    window.addEventListener('keydown', alApretarTecla);
    return () => window.removeEventListener('keydown', alApretarTecla);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salidaPendiente, guardandoHorarios]);
  // FIN: cambios sin guardar en Horarios

  if (authLoading || loading) {
    return <LoadingSpinner type="js" />;
  }

  const stats = {
    todayAppointments: appointments.filter(a => a.date === new Date().toISOString().slice(0, 10)).length,
    weekAppointments: appointments.length,
    monthRevenue: appointments.reduce((acc, app) => {
      const service = services.find(s => s.name === app.service);
      return acc + (service ? service.price : 0);
    }, 0),
    popularService: "Corte de cabello"
  };

  // Función para manejar cancelación con confirmación
  const handleCancelAppointment = (appointment) => {
    setAppointmentToCancel(appointment);
    setShowCancelModal(true);
  };

  // Función para confirmar la cancelación
  const confirmCancelAppointment = async () => {
    if (!appointmentToCancel) return;
    
    try {
      await handleStatusChange(appointmentToCancel.id, 'cancelled');
      setShowCancelModal(false);
      setAppointmentToCancel(null);
    } catch (error) {
      console.error('Error al cancelar turno:', error);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/bookings/${appointmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        // Actualizar el estado local después de éxito en el backend
        setAppointments(prev =>
          prev.map(app =>
            app.id === appointmentId ? { ...app, status: newStatus } : app
          )
        );
        // Estado actualizado exitosamente
      } else {
        const errorData = await response.json();

        setError(errorData.error || 'No se pudo actualizar el estado de la reserva');
      }
    } catch (error) {

      setError('Error de conexión al actualizar el estado');
    }
  };

  /**
   * Borra un turno de verdad, de la base de datos.
   *
   * No es "cancelar más fuerte". Cancelar libera el horario y deja el registro:
   * el turno existió, no se cumplió, y queda en las estadísticas explicando por
   * qué ese hueco quedó libre. Esto es para lo que nunca tendría que haber
   * estado en la agenda —una carga con el dedo pegado, una prueba, un
   * duplicado— y no deja rastro.
   *
   * Por eso pregunta antes y por eso el aviso dice que no se puede deshacer: es
   * la única acción del panel de turnos que no tiene vuelta atrás.
   */
  const borrarTurno = async () => {
    if (!turnoABorrar) return;

    const idABorrar = turnoABorrar.id;

    // En la demo no hay servidor: se saca de la lista en memoria para que la
    // pantalla se comporte igual que la de verdad.
    if (isDemo) {
      borrarDemoBooking(idABorrar);
      setAppointments(prev => prev.filter(app => app.id !== idABorrar));
      setTurnoABorrar(null);
      toast.success('Turno borrado');
      return;
    }

    setBorrandoTurno(true);

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/bookings/${idABorrar}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setBorrandoTurno(false);
        toast.error('No pudimos borrar el turno', {
          description: data.error || 'Probá de nuevo en un momento.',
        });
        return;
      }

      /* La lista se vuelve a leer del servidor en vez de filtrarla en memoria.
         Sacar el turno de la lista local es más rápido y parece equivalente,
         pero deja que la pantalla y la base digan cosas distintas: si el
         borrado no llegó a persistir por cualquier motivo, el turno desaparece
         igual y sólo reaparece al recargar la página —o peor, en otra pantalla
         que lea de la base—, que es como se descubre días después que había
         veinte turnos que uno creía borrados.

         Releyendo, lo que ves es lo que hay. Si el turno sigue en la lista
         después de borrarlo, algo falló y te enterás en el momento. */
      const listaFresca = await fetch(`${API_URL}/api/bookings?businessId=${currentUser.uid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (listaFresca.ok) {
        setAppointments(ordenarTurnos((await listaFresca.json()).map(aTurnoDePanel)));
      } else {
        // Si la relectura falla, al menos no dejar el turno borrado en pantalla.
        setAppointments(prev => prev.filter(app => app.id !== idABorrar));
      }

      setBorrandoTurno(false);
      setTurnoABorrar(null);
      toast.success('Turno borrado');
    } catch (error) {
      console.error('Error al borrar el turno:', error);
      setBorrandoTurno(false);
      toast.error('No pudimos borrar el turno', {
        description: 'Revisá tu conexión y probá de nuevo.',
      });
    }
  };

  // Función para verificar si un turno ya pasó

  // Función para ordenar turnos cronológicamente
  const getSortedAppointments = () => {
    const filtered = appointments.filter(appointment => {
      /* Una retención vencida no es un turno.

         Cuando alguien elige un horario con seña, se le aparta 30 minutos
         mientras paga. Si no paga, el horario se libera solo del lado de la
         disponibilidad —para eso está `holdExpiresAt`— pero la reserva sigue
         existiendo en la base, y el panel la mostraba igual: quedaba un turno
         a nombre de alguien que nunca pagó, ocupando la vista y tapando los
         que sí valen.

         Se descarta acá, en la lista, y no se borra de la base: el negocio
         puede querer ver después cuántos abandonaron el pago, y borrar
         reservas es irreversible. Simplemente deja de estar en la agenda,
         igual que dejó de estar en la disponibilidad. */
      if (appointment.status === 'pending_deposit'
          && !retencionVigente(appointment.holdExpiresAt, ahora)) {
        return false;
      }
      // Filtrar por fecha si está seleccionada
      if (appointmentFilters.date && appointment.date !== appointmentFilters.date) {
        return false;
      }
      // Filtrar por estado si está seleccionado
      if (appointmentFilters.status && appointment.status !== appointmentFilters.status) {
        return false;
      }
      // Filtrar por servicio si está seleccionado
      if (appointmentFilters.service && appointment.service !== appointmentFilters.service) {
        return false;
      }
      // Filtrar por día de semana si está seleccionado
      if (appointmentFilters.dayOfWeek) {
        const appointmentDate = new Date(appointment.date);
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const appointmentDay = dayNames[appointmentDate.getDay()];
        if (appointmentDay !== appointmentFilters.dayOfWeek) {
          return false;
        }
      }
      // Filtrar por rango de hora si está seleccionado
      if (appointmentFilters.timeRange) {
        const appointmentHour = parseInt(appointment.time.split(':')[0]);
        let hourMatch = false;
        
        switch (appointmentFilters.timeRange) {
          case 'morning':
            hourMatch = appointmentHour >= 6 && appointmentHour < 12;
            break;
          case 'afternoon':
            hourMatch = appointmentHour >= 12 && appointmentHour < 18;
            break;
          case 'evening':
            hourMatch = appointmentHour >= 18 && appointmentHour < 24;
            break;
          default:
            hourMatch = true;
        }
        
        if (!hourMatch) {
          return false;
        }
      }
      // Filtrar turnos pasados si no se quieren mostrar
      if (!showPastAppointments && isAppointmentPast(appointment)) {
        return false;
      }
      return true;
    });

    // Ordenar por fecha y hora según el orden seleccionado
    return filtered.sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}:00`);
      const dateB = new Date(`${b.date}T${b.time}:00`);
      
      if (sortOrder === "asc") {
        return dateA - dateB; // Más cercano primero (ascendente)
      } else {
        return dateB - dateA; // Más lejano primero (descendente)
      }
    });
  };

  // Función para obtener los días de la semana actual
  // Nombre del personal y del lugar de un turno. El dato puede venir en el
  // turno mismo (con varias formas históricas) o sólo en el servicio, y de ahí
  // hay que resolverlo contra los mapas de personal y lugares.
  const nombrePersonalDeTurno = (appointment) => {
    const staffName = appointment.staff?.name || appointment.assignedStaff?.name || appointment.staff || appointment.assignedStaff;
    if (staffName) return staffName;

    const relatedService = services.find(s => s.name === appointment.service);
    if (relatedService?.assignedStaff && relatedService.assignedStaff.length > 0) {
      return relatedService.assignedStaff.map(id => staffMapping[id]).filter(Boolean)[0] || 'Personal';
    }

    return 'Personal';
  };

  const nombreLugarDeTurno = (appointment) => {
    const resourceName = appointment.resource?.name || appointment.assignedResource?.name || appointment.resourceName || appointment.resource || appointment.assignedResource;
    if (resourceName) return resourceName;

    const relatedService = services.find(s => s.name === appointment.service);
    if (relatedService?.assignedLocation && relatedService.assignedLocation.length > 0) {
      return relatedService.assignedLocation.map(id => locationMapping[id]).filter(Boolean)[0] || 'Lugar';
    }

    return 'Lugar';
  };

  // Función para obtener turnos de un día específico
  const getAppointmentsForDay = (date) => {
    return getSortedAppointments().filter(appointment => appointment.date === date);
  };

  // Función para formatear precios con puntos como separadores de miles
  const formatPrice = (price) => {
    if (!price) return '0';
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };


  const toggleServiceActive = async (serviceId, currentStatus) => {
    const service = services.find(s => s.id === serviceId);
    
    // Si está intentando activar el servicio (currentStatus = false, nuevo = true)
    // Determinar si es cuenta individual basado en el plan
    const planType = userPlan?.planType || userPlan?.plan?.id;
    const hasBusinessPlan = planType && planType.startsWith('business');
    const isIndividualAccount = businessData?.userType === 'individual' && !hasBusinessPlan;
    if (!currentStatus && !isIndividualAccount) {
      // Validar que tenga staff asignado para cuentas business
      if (!service.assignedStaff || service.assignedStaff.length === 0) {
        alert('❌ No puedes activar este servicio porque no tiene personal asignado.\n\n📋 Para activar el servicio:\n1. Haz clic en "Editar" en el servicio\n2. Ve a la sección "Personal y Recursos Asignados"\n3. Selecciona al menos un miembro del personal\n4. Guarda los cambios\n5. Activa el servicio');
        return;
      }
      
      // Validar que el staff asignado todavía existe
      const hasValidStaff = service.assignedStaff.some(staffId => 
        staff.some(s => s.id === staffId)
      );
      
      if (!hasValidStaff) {
        alert('❌ No puedes activar este servicio porque el personal asignado ya no está disponible.\n\n📋 Para solucionarlo:\n1. Haz clic en "Editar" en el servicio\n2. Ve a la sección "Personal y Recursos Asignados"\n3. Selecciona personal válido que esté disponible\n4. Guarda los cambios\n5. Activa el servicio');
        return;
      }
    }
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ active: !currentStatus }),
      });
  
      if (!response.ok) {
        throw new Error('No se pudo actualizar el estado del servicio.');
      }
  
      const updatedService = await response.json();
  
      setServices(prev =>
        prev.map(service =>
          service.id === serviceId ? { ...service, active: updatedService.active } : service
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const updateService = (serviceId, field, value) => {
    setServices(prev =>
      prev.map(service => {
        if (service.id === serviceId) {
          // Manejar valores numéricos
          if (field === 'duration' || field === 'price') {
            const numValue = parseInt(value);
            if (isNaN(numValue)) {
              return service;
            }
            return { ...service, [field]: numValue };
          }
          return { ...service, [field]: value };
        }
        return service;
      })
    );
  };

  const updateScheduleDay = async (day, field, value) => {
    setScheduleError(""); // Limpiar errores previos
    
    let newBusinessInfo;
    setBusinessInfo(prev => {
      newBusinessInfo = {
        ...prev,
        schedule: {
          ...prev.schedule,
          [day]: {
            ...prev.schedule[day],
            [field]: value
          }
        }
      };
      return newBusinessInfo;
    });

    // Guardar automáticamente al servidor
    if (newBusinessInfo) {
      try {
        const token = await currentUser.getIdToken();
        await fetch(`${API_URL}/api/schedule`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newBusinessInfo.schedule)
        });
      } catch (error) {
        setScheduleError('No se pudo guardar el cambio. Intenta de nuevo.');
      }
    }
  };

  const handleTimeRangeChange = (day, rangeIndex, part, type, value) => { // part: 'start'|'end', type: 'hour'|'minute'
    setScheduleError(""); // Limpiar errores previos
    
    const newBusinessInfo = { ...businessInfo };
    
    // Validar que existe la estructura del schedule
    if (!newBusinessInfo?.schedule?.[day]?.ranges) {
      setScheduleError('Error: No se encontró la configuración de horarios para este día.');
      return;
    }
    
    const ranges = newBusinessInfo.schedule[day].ranges;
    const rangeToUpdate = ranges[rangeIndex];
    
    if (!rangeToUpdate) {
      setScheduleError('Error: No se encontró la franja horaria especificada.');
      return;
    }
    
    let [currentHour, currentMinute] = rangeToUpdate[part].split(':');

    if (type === 'hour') {
      currentHour = value;
    } else {
      currentMinute = value;
    }

    const newTime = `${currentHour}:${currentMinute}`;
    
    // Crear una copia temporal para validar
    const tempRange = { ...rangeToUpdate, [part]: newTime };
    
    // Validaciones
    if (tempRange.start >= tempRange.end) {
      setScheduleError('❌ La hora de inicio debe ser anterior a la hora de fin. Revisa los horarios de esta franja.');
      return;
    }

    // Validar que esta franja no se solape con otras franjas
          // Validating time range overlaps
    
    const overlapCheck = checkTimeOverlap(tempRange, ranges, rangeIndex);
    if (overlapCheck.hasOverlap) {
      // Blocking edit - overlap detected
      setScheduleError(`❌ Esta franja horaria se solapa con otra franja (${overlapCheck.conflictRange.start} - ${overlapCheck.conflictRange.end}). Los horarios no pueden solaparse.`);
        return;
      }
          // Allowing edit - no overlaps

    // Si todas las validaciones pasan, aplicar el cambio
    newBusinessInfo.schedule[day].ranges[rangeIndex][part] = newTime;
    setBusinessInfo(newBusinessInfo);

    // Guardar automáticamente con un pequeño delay para evitar demasiadas peticiones
    clearTimeout(window.scheduleUpdateTimeout);
    window.scheduleUpdateTimeout = setTimeout(async () => {
      try {
        const token = await currentUser.getIdToken();
        await fetch(`${API_URL}/api/schedule`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newBusinessInfo.schedule)
        });
      } catch (error) {
        setScheduleError('No se pudo guardar el cambio. Intenta de nuevo.');
      }
    }, 1000); // Esperar 1 segundo después del último cambio antes de guardar
  };

  // Función auxiliar para calcular el siguiente horario disponible
  const getNextAvailableTime = (ranges) => {
    if (ranges.length === 0) {
      return { start: '09:00', end: '18:00' };
    }

    // Ordenar las franjas por hora de inicio
    const sortedRanges = [...ranges].sort((a, b) => a.start.localeCompare(b.start));

    // Buscar el primer hueco disponible de al menos 1 hora
    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const currentEnd = sortedRanges[i].end;
      const nextStart = sortedRanges[i + 1].start;
      
      // Calcular si hay al menos 1 hora de diferencia
      const [currentEndHour, currentEndMin] = currentEnd.split(':').map(Number);
      const [nextStartHour, nextStartMin] = nextStart.split(':').map(Number);
      
      const currentEndMinutes = currentEndHour * 60 + currentEndMin;
      const nextStartMinutes = nextStartHour * 60 + nextStartMin;

      // Si hay al menos 60 minutos de diferencia, podemos insertar una franja aquí
      if (nextStartMinutes - currentEndMinutes >= 60) {
        const newEndHour = Math.min(nextStartHour, currentEndHour + 1);
        const newEndTime = `${newEndHour.toString().padStart(2, '0')}:${currentEndMin.toString().padStart(2, '0')}`;
        
        return { start: currentEnd, end: newEndTime };
      }
    }

    // Si no hay huecos, agregar al final
    const lastRange = sortedRanges[sortedRanges.length - 1];
    const startTime = lastRange.end;
    
    // Calcular una hora después para el fin
    const [startHour, startMinute] = startTime.split(':').map(Number);
    let endHour = startHour + 1;
    let endMinute = startMinute;

    // Manejar el cambio de día (si pasa de 23:XX)
    if (endHour >= 24) {
      endHour = 23;
      endMinute = 59;
    }

    const endTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;

    return { start: startTime, end: endTime };
  };

  // Función auxiliar para convertir tiempo HH:MM a minutos
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Función auxiliar para validar solapamientos
  const checkTimeOverlap = (newRange, existingRanges, excludeIndex = -1) => {
    
    console.log('  Nueva franja:', newRange);
    console.log('  Franjas existentes:', existingRanges);
    console.log('  Excluir índice:', excludeIndex);
    
    // Convertir la nueva franja a minutos para comparación precisa
    const newStartMinutes = timeToMinutes(newRange.start);
    const newEndMinutes = timeToMinutes(newRange.end);
    
    console.log(`  Nueva franja en minutos: ${newStartMinutes} - ${newEndMinutes}`);
    
    for (let i = 0; i < existingRanges.length; i++) {
      if (i === excludeIndex) {
        console.log(`  Saltando franja ${i} (es la que estamos editando)`);
        continue; // Saltar la franja que estamos editando
      }
      
      const otherRange = existingRanges[i];
      const otherStartMinutes = timeToMinutes(otherRange.start);
      const otherEndMinutes = timeToMinutes(otherRange.end);
      
      console.log(`  Comparando con franja ${i}:`, otherRange);
      console.log(`    Franja ${i} en minutos: ${otherStartMinutes} - ${otherEndMinutes}`);
      
      // Verificar solapamiento usando minutos
      const condition1 = newStartMinutes >= otherStartMinutes && newStartMinutes < otherEndMinutes;
      const condition2 = newEndMinutes > otherStartMinutes && newEndMinutes <= otherEndMinutes;
      const condition3 = newStartMinutes <= otherStartMinutes && newEndMinutes >= otherEndMinutes;
      
      console.log(`    Condición 1 (inicio dentro): ${newStartMinutes} >= ${otherStartMinutes} && ${newStartMinutes} < ${otherEndMinutes} = ${condition1}`);
      console.log(`    Condición 2 (fin dentro): ${newEndMinutes} > ${otherStartMinutes} && ${newEndMinutes} <= ${otherEndMinutes} = ${condition2}`);
      console.log(`    Condición 3 (envuelve): ${newStartMinutes} <= ${otherStartMinutes} && ${newEndMinutes} >= ${otherEndMinutes} = ${condition3}`);
      
      if (condition1 || condition2 || condition3) {
        console.log('  ❌ SOLAPAMIENTO DETECTADO!');
        return {
          hasOverlap: true,
          conflictRange: otherRange
        };
      }
    }
    console.log('  ✅ No hay solapamientos');
    return { hasOverlap: false };
  };

  const addTimeRange = async (day) => {
    setScheduleError(""); // Limpiar errores previos
    
    let newBusinessInfo;
    setBusinessInfo(prev => {
      const currentRanges = prev.schedule[day].ranges;
      const nextTimeRange = getNextAvailableTime(currentRanges);
      
      // Verificar que la nueva franja no exceda las 23 horas
      if (nextTimeRange.start >= '23:00') {
        setScheduleError('No se pueden agregar más franjas horarias. La última franja disponible debe empezar antes de las 23:00.');
        return prev;
      }

      // Verificar que hay tiempo suficiente para al menos 1 hora
      const [startHour] = nextTimeRange.start.split(':').map(Number);
      if (startHour >= 23) {
        setScheduleError('No hay tiempo suficiente para agregar otra franja horaria en este día.');
        return prev;
      }

      // Validar que la nueva franja no se solape con las existentes
      console.log('🔄 AGREGANDO NUEVA FRANJA - Validando solapamientos...');
      const overlapCheck = checkTimeOverlap(nextTimeRange, currentRanges);
      if (overlapCheck.hasOverlap) {
        console.log('❌ BLOQUEANDO ADICIÓN - Solapamiento detectado');
        setScheduleError(`❌ No se puede agregar la franja horaria (${nextTimeRange.start} - ${nextTimeRange.end}). Se solaparía con la franja existente (${overlapCheck.conflictRange.start} - ${overlapCheck.conflictRange.end}). Los horarios no pueden solaparse.`);
        return prev;
      }
      console.log('✅ PERMITIENDO ADICIÓN - No hay solapamientos');

      const newRanges = [...currentRanges, nextTimeRange];
      newBusinessInfo = {
        ...prev,
        schedule: {
          ...prev.schedule,
          [day]: { ...prev.schedule[day], ranges: newRanges }
        }
      };
      
      return newBusinessInfo;
    });

    // Guardar automáticamente al servidor
    if (newBusinessInfo) {
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_URL}/api/schedule`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newBusinessInfo.schedule)
        });
      } catch (error) {
        setScheduleError('No se pudo guardar la nueva franja horaria. Intenta de nuevo.');
      }
    }
  };

  const removeTimeRange = async (day, rangeIndex) => {
    setScheduleError(""); // Limpiar errores previos
    
    let newBusinessInfo;
    setBusinessInfo(prev => {
      const newRanges = prev.schedule[day].ranges.filter((_, index) => index !== rangeIndex);
      newBusinessInfo = {
        ...prev,
        schedule: {
          ...prev.schedule,
          [day]: { ...prev.schedule[day], ranges: newRanges }
        }
      };
      return newBusinessInfo;
    });

    // Guardar automáticamente al servidor
    if (newBusinessInfo) {
      try {
        const token = await currentUser.getIdToken();
        await fetch(`${API_URL}/api/schedule`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newBusinessInfo.schedule)
        });
      } catch (error) {
        setScheduleError('No se pudo guardar el cambio. Intenta de nuevo.');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Las funciones de validación y manejo de recursos ahora están en EditableServiceCard

  const openEditableServiceCard = () => {
    setModalError("");
    setEditingService(null);
    setShowEditableCard(true);
    // Scroll suave al card cuando aparezca
    setTimeout(() => {
      document.querySelector('.editable-service-card')?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  };

  const openEditServiceCard = (service) => {
    setModalError("");
    setEditingService(service);
    setShowEditableCard(true);
    // Scroll suave al card cuando aparezca
    setTimeout(() => {
      document.querySelector('.editable-service-card')?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  };

  const closeEditableServiceCard = () => {
    setShowEditableCard(false);
    setEditingService(null);
  };

  const handleSaveEditableService = async (serviceData) => {
    try {
      setModalError("");
      setLoading(true);
      
      const token = await currentUser.getIdToken();
      const isEditing = editingService !== null;

      const url = isEditing 
        ? `${API_URL}/api/services/${editingService.id}`
        : `${API_URL}/api/services`;
      
      const method = isEditing ? "PUT" : "POST";

      const servicePayload = {
        name: serviceData.name,
        description: serviceData.description,
        // `parseInt`/`parseFloat` sobre un campo vacío dan `NaN`, y
        // `JSON.stringify(NaN)` es `null` — o sea que el backend recibía un
        // precio nulo y lo rechazaba con un 400. Un servicio sin precio cargado
        // vale 0, que es lo que ya trae el formulario en blanco.
        duration: numeroO(serviceData.duration, 30),
        price: numeroO(serviceData.price, 0),
        /* SIN ESTO, UN SERVICIO "A CONSULTAR" NO SE PUEDE GUARDAR.
         *
         * La cadena estaba cortada en el medio: `EditableServiceCard` tiene el
         * botón de precio a consultar y su propia validación lo respeta (deja
         * pasar sin precio), el backend destructura `priceOnRequest` y decide
         * con él (`const sinPrecio = priceOnRequest === true`)... y este payload
         * no lo mandaba. Entonces el servidor recibía un servicio sin precio y
         * sin la bandera, y contestaba 400 "Faltan campos obligatorios: nombre,
         * duración y precio" — mencionando justo el campo que la pantalla te
         * había dejado dejar vacío.
         *
         * Es el mismo tipo de bug que el de `serviceName` en las reservas: el
         * campo existe en las dos puntas y se pierde en el traslado. Cuando
         * agregues un campo al formulario, seguí el viaje entero — formulario,
         * payload, destructure del POST, objeto que se escribe— y comprobá que
         * vuelve al recargar. */
        priceOnRequest: Boolean(serviceData.priceOnRequest),
        maxAdvanceBookingDays: parseInt(serviceData.maxAdvanceBookingDays) || 30,
        allowWaitingList: Boolean(serviceData.allowWaitingList),
        allowCancellation: Boolean(serviceData.allowCancellation),
        assignedStaff: serviceData.assignedStaff && serviceData.assignedStaff.length > 0 ? serviceData.assignedStaff : null,
        assignedResources: serviceData.assignedResources && serviceData.assignedResources.length > 0 ? serviceData.assignedResources : null,
        staffSelectionMode: serviceData.staffSelectionMode || 'automatic',
        resourceSelectionMode: serviceData.resourceSelectionMode || 'automatic',
        // Mapear staffSelectionMode a requireStaffSelection para compatibilidad con BusinessPage
        requireStaffSelection: serviceData.staffSelectionMode === 'required',
        requireResourceSelection: serviceData.resourceSelectionMode === 'required',
        // Agregar campos adicionales para manejar los 3 modos correctamente
        allowStaffSelection: serviceData.staffSelectionMode === 'required' || serviceData.staffSelectionMode === 'optional',
        // Igual que `priceOnRequest`: el formulario lo edita y el backend lo
        // escribe (`alwaysShowStaff: Boolean(alwaysShowStaff)`), pero el payload
        // no lo mandaba, así que la elección se perdía en cada guardado.
        alwaysShowStaff: Boolean(serviceData.alwaysShowStaff),
        allowResourceSelection: serviceData.resourceSelectionMode === 'required' || serviceData.resourceSelectionMode === 'optional',
        // Campos de señas/depósitos
        requiresDeposit: Boolean(serviceData.requiresDeposit),
        depositConfig: serviceData.requiresDeposit ? {
          type: serviceData.depositConfig?.type || 'percentage',
          amount: parseFloat(serviceData.depositConfig?.amount) || 50,
          paymentMethods: serviceData.depositConfig?.paymentMethods || ['mercadopago', 'efectivo', 'transferencia'],
          cancellationPolicy: serviceData.depositConfig?.cancellationPolicy || 'flexible',
          confirmationInstructions: serviceData.depositConfig?.confirmationInstructions || ''
        } : null,
        // Campos de precios dinámicos
        enableDynamicPricing: Boolean(serviceData.enableDynamicPricing),
        priceVariations: serviceData.priceVariations || [],
        promotions: serviceData.promotions || []
      };

      // Creating/editing service
      
      console.log('💰 Precio que se está enviando:', servicePayload.price, typeof servicePayload.price);
      console.log(`💰 Datos de señas enviados:`, {
        requireDeposit: servicePayload.requireDeposit,
        depositType: servicePayload.depositType,
        depositAmount: servicePayload.depositAmount,
        paymentMethods: servicePayload.paymentMethods,
        depositPolicy: servicePayload.depositPolicy
      });
      // Dynamic pricing data

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(servicePayload)
      });

      console.log(`🛠️ Servicio → ${method} ${url}`, { isEditing, respuesta: response.status });

      if (!response.ok) {
        let errorMessage = `Error ${response.status}: ${response.statusText}`;
        try {
        const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          console.error('❌ No se pudo parsear el error del servidor');
        }

        console.error('❌ El backend rechazó el servicio:', errorMessage);
        throw new Error(errorMessage);
      }

      const serviceResponse = await response.json();
      console.log('✅ Servicio guardado:', serviceResponse?.id, serviceResponse?.name);

      if (isEditing) {
        setServices(prev => prev.map(s => s.id === editingService.id ? serviceResponse : s));
        // IMPORTANTE: Actualizar también el servicio que se está editando para que el modal se actualice
        setEditingService(serviceResponse);
      } else {
        setServices(prev => [...prev, serviceResponse]);
      }

      // El modal se cierra al guardar bien.
      //
      // Antes se quedaba abierto a propósito ("NO cerrar el modal después de
      // guardar"), y como no había ningún cartel de éxito, apretar "Guardar"
      // no producía ningún cambio visible: el servicio se creaba pero parecía
      // que no pasaba nada. Peor todavía, al no cerrarse y seguir con
      // editingService en null, volver a apretar Guardar mandaba otro POST y
      // creaba un duplicado.
      setModalError("");
      closeEditableServiceCard();
      toast.success(isEditing ? 'Servicio actualizado' : 'Servicio creado');

    } catch (error) {
      // Este catch guardaba el mensaje en `modalError`, que no se renderiza en
      // ninguna parte del dashboard, y encima no volvía a lanzar: la tarjeta
      // creía que había guardado bien. Resultado: un error del backend se veía
      // exactamente igual que un guardado exitoso, o sea, no se veía.
      console.error('❌ Error guardando el servicio:', error);
      setModalError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar este servicio? Esta acción no se puede deshacer.")) {
        return;
    }
    setError('');
    try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'No se pudo eliminar el servicio.');
        }

        setServices(prev => prev.filter(s => s.id !== serviceId));
    } catch (err) {
        setError(err.message);
    }
  };

  // Funciones para manejar descuentos y promociones
  const openDiscountsModal = (service) => {
    setDiscountsService(service);
    setShowDiscountsModal(true);
  };

  const closeDiscountsModal = () => {
    setShowDiscountsModal(false);
    setDiscountsService(null);
  };

  const handleSaveDiscounts = async (discountsData) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/services/${discountsData.serviceId}/discounts`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discounts: discountsData.discounts,
          packages: discountsData.packages
        })
      });

      if (response.ok) {
        const updatedService = await response.json();
        setServices(services.map(s => 
          s.id === updatedService.id ? updatedService : s
        ));
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Error al guardar descuentos");
      }
    } catch (error) {
      console.error('Error:', error);
      setError("Error al guardar descuentos y promociones");
    }
  };

  const closeBlockTimeModal = () => {
    setError('');
    setShowBlockTimeModal(false);
  };

  const openBlockTimeModal = () => {
    setError('');
    setShowBlockTimeModal(true);
  };

  /**
   * Manda todos los bloqueos que se juntaron en la ventana en un solo POST.
   *
   * `bloqueos` ya viene con el shape del endpoint: un ítem por fecha suelta y
   * uno por rango, cada uno con su propio horario. El error del backend se
   * muestra tal cual porque ahora dice algo útil (rango inválido, hora de fin
   * anterior a la de inicio, demasiados días); antes cualquier fallo terminaba
   * en el mismo cartel genérico.
   */
  /**
   * Guarda los turnos que entraron por fuera de la app.
   *
   * Van a `POST /api/bookings/manual`, que los crea como turnos de verdad: por
   * eso después aparecen en la lista y en el calendario como cualquier otro, y
   * la app deja de ofrecer ese horario sola.
   */
  const handleAgregarTurnos = async ({ turnos, enviarMail }, alTerminar) => {
    setError('');
    setGuardandoTurnos(true);

    try {
      const token = await currentUser.getIdToken();

      const response = await fetch(`${API_URL}/api/bookings/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ turnos, enviarMail }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || data?.error || 'No se pudieron agregar los turnos');
      }

      // Se agregan a la lista con la misma forma que usa el resto del panel
      // (`booking.customer.name` → `customerName`), para que se vean sin
      // recargar.
      setAppointments(prev => [
        ...prev,
        ...data.map(turno => ({
          id: turno.id,
          time: turno.time,
          date: turno.date,
          customerName: turno.customer.name,
          phone: turno.customer.phone || 'No especificado',
          email: turno.customer.email || '',
          service: turno.service.name,
          status: turno.status,
          staff: turno.staff || null,
          assignedStaff: turno.staff || null,
        })),
      ]);

      toast.success(data.length === 1 ? 'Turno agregado' : `${data.length} turnos agregados`);

      if (alTerminar) alTerminar();
      setMostrarAgregarTurno(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardandoTurnos(false);
    }
  };

  const handleBlockTime = async (bloqueos, alTerminar) => {
    setError('');
    setBloqueandoHorarios(true);

    try {
      const token = await currentUser.getIdToken();

      const response = await fetch(`${API_URL}/api/blocked-slots/multiple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bloqueos),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || 'No se pudieron crear los horarios bloqueados');
      }

      setBlockedSlots(prev => [...prev, ...data]);
      toast.success(
        data.length === 1
          ? 'Horario bloqueado'
          : `${data.length} horarios bloqueados`
      );

      if (alTerminar) alTerminar();
      setShowBlockTimeModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBloqueandoHorarios(false);
    }
  };

  const handleUnblockTime = async (blockId) => {
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/blocked-slots/${blockId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('No se pudo desbloquear el horario.');
      }
      setBlockedSlots(prev => prev.filter(b => b.id !== blockId));
    } catch (err) {
      setError(err.message);
    }
  };

  /**
   * Guarda la sección Configuración (y el horario, que viaja en el mismo pedido).
   *
   * Devuelve `true`/`false` porque además del botón "Guardar cambios" lo usa
   * `guardarYSalir`, que necesita saber si salió bien: si falla no se navega a
   * ningún lado, porque irse igual sería tirar los cambios justo después de que
   * alguien pidió explícitamente guardarlos.
   */
  const handleSaveChanges = async ({ avisar = true } = {}) => {
    console.log('🐛 [DEBUG] handleSaveChanges iniciado');
    console.log('🐛 [DEBUG] businessInfo:', businessInfo);

    if (!businessInfo) {
      setError("No hay datos para guardar.");
      return false;
    }

    // En la demo no hay servidor ni sesión: `currentUser.getIdToken()` sería
    // null y el guardado explotaría. Se acepta el cambio de local para que la
    // pantalla se comporte igual que la de verdad, como hace `guardarHorarios`.
    if (isDemo) {
      setDatosGuardados(businessInfo);
      setHorarioGuardado(businessInfo?.schedule || {});
      if (avisar) toast.success('Cambios guardados');
      return true;
    }

    setError('');
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // CORRECTO: Usar businessInfo.schedule, que contiene los cambios
      const scheduleToSave = businessInfo?.schedule || {};


      // Preparar el objeto businessInfo sin incluir el slug si es un negocio existente
      // Ojo: esto es una lista blanca. Un campo nuevo del formulario que no esté
      // acá se descarta en silencio al guardar.
      const businessInfoToSend = {
        name: businessInfo.name,
        email: businessInfo.email,
        phone: businessInfo.phone,
        address: businessInfo.address,
        // Los campos sueltos viajan además del string armado: si se guardara
        // sólo `address`, la próxima vez que el negocio abriera la sección
        // Dirección la vería vacía de nuevo y estaríamos donde empezamos.
        street: businessInfo.street,
        floor: businessInfo.floor,
        city: businessInfo.city,
        postalCode: businessInfo.postalCode,
        province: businessInfo.province,
        country: businessInfo.country,
        description: businessInfo.description,
        mensaje: businessInfo.mensaje,
        businessType: businessInfo.businessType
      };
      
      
      // Solo incluir el slug si es un negocio nuevo o si necesita actualizarse
      const isExistingBusiness = businessData?.slug && businessData.slug !== 'mi-negocio';
      if (!isExistingBusiness && businessInfo.slug && businessInfo.slug !== 'mi-negocio') {
        businessInfoToSend.slug = businessInfo.slug;
        console.log('📝 Incluyendo slug en el envío:', businessInfo.slug);
      } else {
      }
      
      const bodyToSend = {
        ...scheduleToSave,
        businessInfo: businessInfoToSend
      };


      console.log('🐛 [DEBUG] Enviando requests...');
      console.log('🐛 [DEBUG] scheduleToSave:', scheduleToSave);
      console.log('🐛 [DEBUG] businessInfoToSend:', businessInfoToSend);

      const responses = await Promise.all([
        // Guardar horarios
        fetch(`${API_URL}/api/schedule`, {
          method: 'PUT',
          headers: headers,
          body: JSON.stringify(scheduleToSave)
        }),
        // Guardar información del negocio por separado
        fetch(`${API_URL}/api/business/info`, {
          method: 'PUT',
          headers: headers,
          body: JSON.stringify(businessInfoToSend)
        }),
        // El guardado de servicios se mantiene igual
        ...services.map(service =>
          fetch(`${API_URL}/api/services/${service.id}`, {
            method: 'PUT',
            headers: headers,
            body: JSON.stringify(service)
          })
        )
      ]);

      console.log('📥 Respuestas recibidas:', responses.map(r => ({ ok: r.ok, status: r.status })));

      const allOk = responses.every(res => res.ok);

      if (!allOk) {
        const failedResponses = responses.filter(r => !r.ok);
        console.error('❌ Respuestas fallidas:', failedResponses);
        throw new Error('Algunos cambios no se pudieron guardar. Intente de nuevo.');
      }

      // Actualizar inmediatamente el estado local con los valores que enviamos
      setBusinessInfo(prev => ({
        ...prev,
        ...businessInfoToSend
      }));

      // Este guardado manda el horario junto con el resto (`scheduleToSave`), así
      // que a partir de acá tampoco hay cambios pendientes en Horarios. Sin esto,
      // guardar desde Configuración dejaría el aviso de "sin guardar" encendido
      // sobre un horario que ya está en el servidor.
      setHorarioGuardado(scheduleToSave);

      // Guardar también los colores de marca
      saveBrandingChanges();

      // `avisar: false` cuando el guardado lo pidió el diálogo de "cambios sin
      // guardar": ahí el aviso es que la pantalla se va, y un toast de éxito
      // encima de la navegación es ruido.
      if (avisar) toast.success('Cambios guardados');
      console.log('✅ Cambios guardados exitosamente');
      
      // Recargar los datos del negocio para asegurar que el estado esté sincronizado
      const businessResponse = await fetch(`${API_URL}/api/business`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (businessResponse.ok) {
        const updatedBusinessData = await businessResponse.json();
        
        // Actualizar el estado con los datos frescos del servidor
        setBusinessData(updatedBusinessData);
        const businessData = updatedBusinessData.business || updatedBusinessData;
        
        // Se lee igual que en la carga inicial: `businessInfo` primero y la raíz
        // de respaldo. Acá se leía SÓLO la raíz, así que apretar "Guardar"
        // vaciaba en pantalla todo lo que el negocio había cargado en el
        // onboarding —que vive en `businessInfo`—: el nombre se iba al que puso
        // al registrarse y la dirección volvía a "Añadir dirección", como si el
        // guardado hubiera borrado los datos.
        const infoFresca = businessData.businessInfo || {};
        setBusinessInfo(prev => ({
          ...prev,
          name: infoFresca.name || businessData.name || "",
          email: infoFresca.email || businessData.email || "",
          slug: infoFresca.slug || businessData.slug || prev.slug || "mi-negocio",  // Mantener el slug actual si no viene del servidor
          address: infoFresca.address || businessData.address || "Añadir dirección",
          street: infoFresca.street || "",
          floor: infoFresca.floor || "",
          city: infoFresca.city || "",
          postalCode: infoFresca.postalCode || "",
          province: infoFresca.province || "",
          country: infoFresca.country || "Argentina",
          phone: infoFresca.phone || businessData.phone || "Añadir teléfono",
          description: infoFresca.description || businessData.description || "",
          mensaje: infoFresca.mensaje || businessData.mensaje || "",
          businessType: infoFresca.businessType || businessData.businessType || "",
          schedule: businessData.schedule || prev.schedule
        }));

        // La referencia de "¿hay cambios?" se reajusta con lo que contestó el
        // servidor y no con lo que se mandó: si el backend normalizó algo, la
        // pantalla queda con lo suyo y comparar contra lo enviado dejaría el
        // aviso encendido para siempre sobre datos que ya están guardados.
        setDatosGuardados(prev => ({
          ...prev,
          ...businessInfoToSend,
          name: infoFresca.name || businessData.name || "",
          address: infoFresca.address || businessData.address || "Añadir dirección",
          phone: infoFresca.phone || businessData.phone || "Añadir teléfono",
          businessType: infoFresca.businessType || businessData.businessType || "",
        }));
      } else {
        // Sin recarga, la referencia igual tiene que avanzar: lo que se mandó ya
        // está guardado, y dejarla vieja mostraría "cambios sin guardar" sobre
        // algo que el servidor aceptó.
        setDatosGuardados(prev => ({ ...prev, ...businessInfoToSend }));
      }

      return true;
    } catch (err) {
      console.error('❌ Error al guardar:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Funciones para la sección de ayuda
  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };


  // Datos FAQ
  const faqData = [
    {
      question: "¿Cómo empiezo a usar Aturno?",
      answer: "Primero, configura tu información básica en 'Configuración', luego agrega tus servicios en la sección 'Servicios', establece tus horarios de atención, y finalmente comparte tu página pública con tus clientes."
    },
    {
      question: "¿Cómo agrego y gestiono mis servicios?",
      answer: "Ve a la sección 'Servicios' y haz clic en '+ Agregar servicio'. Puedes configurar nombre, descripción, duración, precio, y asignar personal o recursos específicos. Cada servicio tiene un interruptor para activarlo/desactivarlo temporalmente."
    },
    {
      question: "¿Cómo manejo los turnos de mis clientes?",
      answer: "En la sección 'Turnos' puedes ver todos los turnos en formato lista o calendario. Puedes confirmar turnos pendientes, cancelar si es necesario, y contactar a tus clientes directamente desde la plataforma."
    },
    {
      question: "¿Cómo configuro mi equipo de trabajo?",
      answer: "En 'Personal' puedes agregar miembros de tu equipo, configurar sus horarios individuales, y asignarlos a servicios específicos. También puedes gestionar recursos como salas o equipos en la sección 'Recursos'."
    },
    {
      question: "¿Cómo personalizo mi página de reservas?",
      answer: "En 'Configuración' puedes cambiar el nombre de tu negocio, la URL personalizada, dirección, teléfono, y los colores de tu marca. Usa 'Ver mi página' para ver cómo se ve públicamente."
    },
    {
      question: "¿Qué integraciones están disponibles?",
      answer: "Actualmente puedes conectar Google Calendar para sincronizar turnos con tu calendario personal, y Mercado Pago para recibir pagos online. Más integraciones estarán disponibles próximamente."
    },
    {
      question: "¿Cómo bloqueo horarios cuando no estoy disponible?",
      answer: "En la sección 'Turnos', usa el botón '+ Bloquear horario' para reservar franjas específicas. Puedes bloquear días completos, horarios específicos, o rangos de fechas cuando no puedas atender clientes."
    },
    {
      question: "¿Cómo veo el rendimiento de mi negocio?",
      answer: "La sección 'Estadísticas' te muestra métricas clave como ingresos mensuales, turnos confirmados, servicios más populares, personal más activo, y horarios pico. Ideal para tomar decisiones informadas."
    },
    {
      question: "¿Puedo gestionar múltiples ubicaciones o recursos?",
      answer: "Sí, en la sección 'Recursos' puedes agregar salas, equipos, o ubicaciones específicas. Luego puedes asignar estos recursos a servicios particulares para una mejor organización."
    },
    {
      question: "¿Cómo cambio mi plan o veo mi facturación?",
      answer: "En la sección 'Plan' puedes ver tu plan actual, cambiar a otro plan, ver tu historial de facturación, y gestionar tu método de pago. También puedes cancelar tu suscripción si es necesario."
    }
  ];


   // Definición de planes disponibles
   const availablePlans = [
     {
       id: 'profesional',
       name: 'Profesional',
       description: 'Para profesionales independientes',
       monthlyPrice: 2500,
       yearlyPrice: 2500 * 12 * 0.85, // 15% descuento
       features: [
         'Servicios ilimitados',
         'Calendario y reservas',
         'Página pública personalizada',
         'Notificaciones automáticas'
       ],
       recommended: false,
       maxServices: '3',
       maxAppointments: 'Ilimitados',
       teamSize: '1 persona'
     },
     {
       id: 'negocios',
       name: 'Negocios',
       description: 'Para equipos y negocios con personal',
       monthlyPrice: 4500,
       yearlyPrice: 4500 * 12 * 0.85, // 15% descuento
       features: [
         'Todo lo del plan Profesional',
         'Gestión de personal y equipo',
         'Asignación de lugares/recursos',
         'Integraciones avanzadas'
       ],
       recommended: true,
       maxServices: 'Ilimitados',
       maxAppointments: 'Ilimitados',
       teamSize: 'Equipo completo'
     }
   ];

   const getCurrentPlan = () => {
     // Por ahora simulamos que tiene el plan profesional
     return availablePlans.find(plan => plan.id === 'profesional');
   };

   // Función para cargar suscripción actual desde el backend
   const loadCurrentSubscription = async () => {
     try {
       console.log('🔄 Recargando datos de suscripción...');
       
       const token = await currentUser.getIdToken();
       const response = await fetch(`${API_URL}/api/subscriptions/current`, {
         headers: {
           'Authorization': `Bearer ${token}`
         }
       });

       if (response.ok) {
         const data = await response.json();
         console.log('📊 Datos de suscripción recargados:', data);
         
         // Actualizar el estado con la suscripción actual
         if (data.success && data.planStatus) {
           console.log('✅ Actualizando estado de suscripción a:', data.planStatus);
           
           setSubscriptionData(prev => ({
             ...prev,
             planStatus: data.planStatus,
             planName: data.planName || prev.planName,
             planType: data.planType || prev.planType,
             nextBillingDate: data.nextBillingDate || prev.nextBillingDate,
             subscriptionCancelledAt: data.subscriptionCancelledAt || prev.subscriptionCancelledAt
           }));
           
           // También actualizar currentPlan si es necesario
           setCurrentPlan(prev => ({
             ...prev,
             status: data.planStatus === 'cancelled' ? 'cancelled' : prev.status
           }));
           
           console.log('🔄 Estado local actualizado');
         }
       } else {
         console.error('❌ Error al cargar suscripción:', response.status);
       }
     } catch (error) {
       console.error('❌ Error al cargar suscripción:', error);
     }
   };

   // Funciones para gestión de planes
   const handleChangePlan = () => {
     navigate('/admin/plans');
   };

   const handleCancelPlan = async () => {
     console.log('=== INICIANDO PROCESO DE CANCELACIÓN ===');
     console.log('Fecha/Hora:', new Date().toISOString());
     console.log('Usuario:', currentUser?.email);
     console.log('Subscription ID:', subscriptionData?.subscriptionId);
     
     // Verificar que existe una suscripción activa
     if (!subscriptionData?.subscriptionId) {
       console.error('No hay subscriptionId disponible');
       alert("No tienes una suscripción activa para cancelar");
       return;
     }

     // Confirmar cancelación con el usuario
     if (!window.confirm(
       "¿Estás seguro de que quieres cancelar tu suscripción?\n\n" +
       "• Tu suscripción será cancelada en MercadoPago\n" +
       "• No se realizarán más cobros automáticos\n" +
       "• Mantendrás acceso hasta el final del período pagado\n" +
       "• Podrás volver a suscribirte cuando quieras"
     )) {
       console.log('Cancelación abortada por el usuario');
       return;
     }

     try {
       setLoading(true);
       console.log('Obteniendo token de autenticación...');
       
       // Obtener token de autenticación
       const token = await currentUser.getIdToken();
       console.log('Token obtenido');
       
       // Llamar al endpoint de cancelación
       const url = `${API_URL}/api/subscriptions/cancel/${subscriptionData.subscriptionId}`;
       console.log('Llamando a:', url);
       
       const response = await fetch(url, {
         method: 'POST',
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         }
       });

       const data = await response.json();
       console.log('Respuesta del servidor:', {
         status: response.status,
         ok: response.ok,
         data: data
       });

       if (response.ok && data.success) {
         console.log('Cancelación exitosa, actualizando estado local...');
         // Actualizar el estado local
         setSubscriptionData(prev => ({
           ...prev,
           planStatus: 'cancelled',
           cancelledAt: new Date().toISOString()
         }));
         
         setCurrentPlan(prev => ({
           ...prev,
           status: "cancelled"
         }));
         
         // Calcular fecha de fin del período actual
         const endDate = subscriptionData.nextBillingDate || 
                        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('es-AR');
         
         alert(
           `✅ Suscripción cancelada exitosamente\n\n` +
           `Mantendrás acceso completo hasta: ${endDate}\n` +
           `No se realizarán más cobros automáticos.`
         );
         
         // Recargar datos de suscripción
         await loadCurrentSubscription();
         
       } else if (data.message === 'Esta suscripción ya está cancelada') {
         // Si la suscripción ya está cancelada, sincronizar el estado local
         console.log('Suscripción ya cancelada, sincronizando estado local...');
         
         setSubscriptionData(prev => ({
           ...prev,
           planStatus: 'cancelled',
           cancelledAt: prev.cancelledAt || new Date().toISOString()
         }));
         
         setCurrentPlan(prev => ({
           ...prev,
           status: "cancelled"
         }));
         
         // Informar al usuario de manera más amigable
         alert(
           `ℹ️ Tu suscripción ya está cancelada\n\n` +
           `No se realizarán más cobros automáticos.\n` +
           `Mantendrás acceso hasta el final del período pagado.`
         );
         
         // Recargar datos para asegurar sincronización
         await loadCurrentSubscription();
         
         // Forzar recarga completa después de sincronizar
         setTimeout(() => {
           console.log('🔄 Recargando página tras sincronización...');
           window.location.reload();
         }, 2000);
         
       } else {
         throw new Error(data.message || 'Error al cancelar la suscripción');
       }
       
     } catch (error) {
       console.error('Error al cancelar suscripción:', error);
       
       // Manejar específicamente el caso de suscripción ya cancelada
       if (error.message === 'Esta suscripción ya está cancelada') {
         setSubscriptionData(prev => ({
           ...prev,
           planStatus: 'cancelled'
         }));
         
         setCurrentPlan(prev => ({
           ...prev,
           status: "cancelled"
         }));
         
         alert(
           `ℹ️ Tu suscripción ya está cancelada\n\n` +
           `No se realizarán más cobros automáticos.`
         );
         
         // Forzar recarga completa para mostrar el estado actualizado
         setTimeout(() => {
           console.log('🔄 Recargando página para mostrar estado actualizado...');
           window.location.reload();
         }, 2000);
         
       } else {
         alert(
           `❌ Error al cancelar la suscripción\n\n` +
           `${error.message}\n\n` +
           `Por favor, intenta nuevamente o contacta a soporte.`
         );
       }
     } finally {
       setLoading(false);
     }
   };

   const handleSelectPlan = async (planId) => {
     const selectedPlan = availablePlans.find(plan => plan.id === planId);
     const currentPlan = getCurrentPlan();
     
     if (selectedPlan.id === currentPlan.id) {
       alert('Ya tienes este plan activo');
       return;
     }
     
     const billingType = isAnnualBilling ? 'anual' : 'mensual';
     const price = isAnnualBilling ? selectedPlan.yearlyPrice : selectedPlan.monthlyPrice;
     
     try {
       setProcessingSubscription(true);
       
       // Crear suscripción con Mercado Pago
       const token = await currentUser.getIdToken();
       const response = await fetch(`${API_URL}/api/subscriptions/create-preapproval`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`
         },
         body: JSON.stringify({
           planId: planId,
           isAnnual: isAnnualBilling,
           planData: {
             name: selectedPlan.name,
             monthlyPrice: selectedPlan.monthlyPrice,
             yearlyPrice: selectedPlan.yearlyPrice
           }
         })
       });

       if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.message || 'Error al crear la suscripción');
       }

       const data = await response.json();
       
       if (data.isDemoMode) {
         // Modo demo: mostrar mensaje y esperar activación automática
         alert('¡Suscripción creada exitosamente en modo demo! Tu plan se activará automáticamente en unos segundos.');
         
         // Esperar 6 segundos y recargar la página para mostrar el plan activado
         setTimeout(() => {
           window.location.reload();
         }, 6000);
       } else {
         // Verificar si está en modo incógnito antes de proceder
         const isIncognito = await checkAndWarnIncognito();
         
         if (isIncognito) {
           const userConfirms = window.confirm(
             '⚠️ ADVERTENCIA: Modo Incógnito Detectado\n\n' +
             'Estás navegando en modo incógnito/privado. Esto puede causar problemas con los pagos de MercadoPago debido a restricciones del navegador.\n\n' +
             'RECOMENDACIÓN: Abre una ventana normal (no incógnito) para realizar el pago.\n\n' +
             '¿Quieres continuar de todas formas?'
           );
           
           if (!userConfirms) {
             console.log('👤 Usuario canceló el pago debido a modo incógnito');
             return;
           }
         }
         
         // Modo producción: abrir Mercado Pago en nueva pestaña para evitar problemas de Storage Access
         console.log('🚀 Abriendo MercadoPago en nueva pestaña...');
         
         // Opción 1: Abrir en nueva pestaña (recomendado)
         window.open(data.initPoint, '_blank', 'noopener,noreferrer');
         
         // Mostrar mensaje de confirmación
         alert('Se abrió una nueva pestaña para completar el pago en MercadoPago. Una vez finalizado, serás redirigido automáticamente.');
         
         // Opcional: También puedes usar redirección en la misma ventana como fallback
         // window.location.href = data.initPoint;
       }
       
     } catch (error) {
       console.error('Error al crear suscripción:', error);
       alert(`Error al procesar la suscripción: ${error.message}`);
     } finally {
       setProcessingSubscription(false);
     }
   };

   const formatDate = (dateString) => {
     const date = new Date(dateString);
     return date.toLocaleDateString('es-AR', {
       year: 'numeric',
       month: 'long',
       day: 'numeric'
     });
   };

   // Funciones para integraciones
   const toggleIntegration = (integrationKey) => {
     setIntegrations(prev => ({
       ...prev,
       [integrationKey]: {
         ...prev[integrationKey],
         active: !prev[integrationKey].active
       }
     }));
   };

               const handleConnect = async (integrationKey) => {
      // Iniciar proceso de conexión
      setConnectingIntegration(integrationKey);
      
      try {
        // Lógica específica por tipo de integración
        if (integrationKey === 'googleCalendar') {
          await handleGoogleCalendarConnect();
        } else if (integrationKey === 'mercadoPago') {
          await handleMercadoPagoConnect();
        } else {
          // Fallback para integraciones no implementadas aún
          await new Promise(resolve => setTimeout(resolve, 2000));
          setIntegrations(prev => ({
            ...prev,
            [integrationKey]: { ...prev[integrationKey], connected: true }
          }));
          alert(`¡Conexión exitosa con ${getIntegrationName(integrationKey)}!`);
        }
        
      } catch (error) {
        console.error('Error en handleConnect:', error);
        alert(`Error al conectar con ${getIntegrationName(integrationKey)}: ${error.message}`);
      } finally {
        setConnectingIntegration(null);
      }
    };

    // Conectar Google Calendar con OAuth2 real
    const handleGoogleCalendarConnect = async () => {
      console.log('🚀 [GOOGLE DEBUG] ========== INICIANDO CONEXIÓN CON GOOGLE CALENDAR ==========');
      try {
        const token = await currentUser.getIdToken();
        // Obtener el businessId actual (puede venir de userData, businessInfo, o currentUser.uid)
        const businessId = (businessInfo && businessInfo.id) || (userData && userData.uid) || currentUser.uid;
        console.log('🔑 [GOOGLE DEBUG] Business ID:', businessId);
        console.log('🎫 [GOOGLE DEBUG] Token obtenido:', token ? 'SÍ' : 'NO');
        console.log('🌐 [GOOGLE DEBUG] API URL:', API_URL);
        
        // SOLUCIÓN TEMPORAL: Si el backend devuelve 403, usar URL directa
        try {
          // Intentar primero con el backend
          const authEndpoint = `${API_URL}/api/oauth/google/auth?state=${businessId}`;
          console.log('📡 [GOOGLE DEBUG] Llamando a endpoint:', authEndpoint);
          
          const response = await fetch(authEndpoint, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log('📨 [GOOGLE DEBUG] Response status:', response.status);
          
          if (!response.ok) {
            // Si falla, construir URL directamente (temporal)
            console.log('⚠️ [GOOGLE DEBUG] Backend devolvió error, usando URL directa temporal');
            
            const GOOGLE_CLIENT_ID = '468861449729-d2tvcrfthfvhm1lmcfvs2trq8jnd8qgc.apps.googleusercontent.com';
            const REDIRECT_URI = 'https://aturno-backend.onrender.com/api/oauth/google/callback';
            
            const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` + 
              `client_id=${GOOGLE_CLIENT_ID}&` +
              `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
              `response_type=code&` +
              `scope=${encodeURIComponent('https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar')}&` +
              `access_type=offline&` +
              `prompt=consent&` +
              `state=${businessId}`;
            
            // Abrir ventana de autorización
            const authWindow = window.open(authUrl, 'Google Calendar Authorization', 'width=500,height=600');
            
            // Escuchar mensajes de la ventana
            return new Promise((resolve, reject) => {
              const handleMessage = (event) => {
                console.log('📨 [GOOGLE DEBUG] Mensaje recibido:', event.data);
                console.log('🌍 [GOOGLE DEBUG] Origen del mensaje:', event.origin);
                
                if (event.data === 'google_calendar_connected') {
                  console.log('✅ [GOOGLE DEBUG] GOOGLE CALENDAR CONECTADO!');
                  window.removeEventListener('message', handleMessage);
                  authWindow.close();
                  
                  console.log('🔄 [GOOGLE DEBUG] Actualizando estado local...');
                  setIntegrations(prev => {
                    console.log('🔍 [GOOGLE DEBUG] Estado anterior:', prev);
                    const newState = {
                      ...prev,
                      googleCalendar: { ...prev.googleCalendar, connected: true }
                    };
                    console.log('✅ [GOOGLE DEBUG] Nuevo estado:', newState);
                    return newState;
                  });
                  
                  alert('¡Google Calendar conectado exitosamente!');
                  console.log('🔄 [GOOGLE DEBUG] Recargando integraciones del backend...');
                  loadIntegrationsFromBackend();
                  resolve();
                } else if (event.data === 'google_calendar_error') {
                  console.error('❌ [GOOGLE DEBUG] ERROR AL CONECTAR GOOGLE CALENDAR');
                  window.removeEventListener('message', handleMessage);
                  authWindow.close();
                  reject(new Error('Error al conectar Google Calendar'));
                }
              };
              
              const timeoutId = setTimeout(() => {
                window.removeEventListener('message', handleMessage);
                reject(new Error('Proceso de autorización cancelado o timeout'));
              }, 60000);
              
              const enhancedHandleMessage = (event) => {
                clearTimeout(timeoutId);
                handleMessage(event);
              };
              
              window.addEventListener('message', enhancedHandleMessage);
            });
          }
        } catch (error) {
          // Si todo falla, usar URL directa
          console.log('⚠️ [GOOGLE DEBUG] Usando fallback directo para Google OAuth');
          console.error('❌ [GOOGLE DEBUG] Error en el primer intento:', error);
          
          const GOOGLE_CLIENT_ID = '468861449729-d2tvcrfthfvhm1lmcfvs2trq8jnd8qgc.apps.googleusercontent.com';
          const REDIRECT_URI = 'https://aturno-backend.onrender.com/api/oauth/google/callback';
          
          const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` + 
            `client_id=${GOOGLE_CLIENT_ID}&` +
            `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
            `response_type=code&` +
            `scope=${encodeURIComponent('https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar')}&` +
            `access_type=offline&` +
            `prompt=consent&` +
            `state=${businessId}`;
          
          // Abrir ventana de autorización
          const authWindow = window.open(authUrl, 'Google Calendar Authorization', 'width=500,height=600');
          
          // Escuchar mensajes de la ventana
          return new Promise((resolve, reject) => {
            const handleMessage = (event) => {
              console.log('📨 [GOOGLE DEBUG FALLBACK] Mensaje recibido:', event.data);
              console.log('🌍 [GOOGLE DEBUG FALLBACK] Origen del mensaje:', event.origin);
              
              if (event.data === 'google_calendar_connected') {
                console.log('✅ [GOOGLE DEBUG FALLBACK] GOOGLE CALENDAR CONECTADO!');
                window.removeEventListener('message', handleMessage);
                authWindow.close();
                
                console.log('🔄 [GOOGLE DEBUG FALLBACK] Actualizando estado local...');
                setIntegrations(prev => {
                  console.log('🔍 [GOOGLE DEBUG FALLBACK] Estado anterior:', prev);
                  const newState = {
                    ...prev,
                    googleCalendar: { ...prev.googleCalendar, connected: true }
                  };
                  console.log('✅ [GOOGLE DEBUG FALLBACK] Nuevo estado:', newState);
                  return newState;
                });
                
                alert('¡Google Calendar conectado exitosamente!');
                console.log('🔄 [GOOGLE DEBUG FALLBACK] Recargando integraciones del backend...');
                loadIntegrationsFromBackend();
                resolve();
              } else if (event.data === 'google_calendar_error') {
                console.error('❌ [GOOGLE DEBUG FALLBACK] ERROR AL CONECTAR GOOGLE CALENDAR');
                window.removeEventListener('message', handleMessage);
                authWindow.close();
                reject(new Error('Error al conectar Google Calendar'));
              }
            };
            
            const timeoutId = setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              reject(new Error('Proceso de autorización cancelado o timeout'));
            }, 60000);
            
            const enhancedHandleMessage = (event) => {
              clearTimeout(timeoutId);
              handleMessage(event);
            };
            
            window.addEventListener('message', enhancedHandleMessage);
          });
        }
        
        // Si llegamos aquí, el backend respondió OK (obtener response de nuevo)
        const backendResponse = await fetch(`${API_URL}/api/oauth/google/auth?state=${businessId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const { authUrl } = await backendResponse.json();
        
        // Abrir ventana de OAuth
        const authWindow = window.open(authUrl, 'google-oauth', 'width=500,height=600');
        
        // Escuchar el resultado
        return new Promise((resolve, reject) => {
          const handleMessage = (event) => {
            if (event.data === 'google_calendar_connected') {
              window.removeEventListener('message', handleMessage);
              authWindow.close();
              
              // Actualizar estado
              setIntegrations(prev => ({
                ...prev,
                googleCalendar: { ...prev.googleCalendar, connected: true }
              }));
              
              alert('¡Google Calendar conectado exitosamente!');
              loadIntegrationsFromBackend(); // Recargar integraciones
              resolve();
            } else if (event.data === 'google_calendar_error') {
              window.removeEventListener('message', handleMessage);
              authWindow.close();
              reject(new Error('Error al conectar Google Calendar'));
            }
          };
          
          // Timeout si la ventana se cierra manualmente o no responde
          const timeoutId = setTimeout(() => {
            window.removeEventListener('message', enhancedHandleMessage);
            reject(new Error('Proceso de autorización cancelado o timeout'));
          }, 60000); // 60 segundos timeout
          
          // Crear una versión mejorada del handleMessage que limpia el timeout
          const enhancedHandleMessage = (event) => {
            clearTimeout(timeoutId);
            handleMessage(event);
          };
          
          // Usar la versión mejorada
          window.addEventListener('message', enhancedHandleMessage);
        });
        
      } catch (error) {
        throw new Error(`Error en OAuth Google: ${error.message}`);
      }
    };
    
    // Conectar Mercado Pago con OAuth
    const handleMercadoPagoConnect = async () => {
              // Starting MercadoPago OAuth connection
      
      try {
        console.log('🔍 Iniciando conexión con Mercado Pago...');
        console.log('🔍 API_URL:', API_URL);
        console.log('🔍 Current User:', currentUser?.uid);
        
        // Intentar usar el endpoint del backend primero
        try {
          const token = await currentUser.getIdToken();
          console.log('🔑 Token obtenido:', token ? 'Sí' : 'No');
          
          const authEndpoint = `${API_URL}/api/oauth/mercadopago/auth`;
          console.log('🌐 Llamando a:', authEndpoint);
          
          const response = await fetch(authEndpoint, {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log('📡 Response status:', response.status);
          console.log('📡 Response ok:', response.ok);
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ URL de autorización obtenida del backend:', data.authUrl);
            
            // Verificar que tenemos una URL válida
            if (data.authUrl) {
              console.log('🚀 Redirigiendo a MercadoPago en 1 segundo...');
              // Pequeño delay para asegurar que los logs se muestren
              setTimeout(() => {
                console.log('🔄 Ejecutando redirección ahora...');
                // Intentar múltiples métodos de redirección
                try {
                  // Método 1: Redirección directa
                  window.location.href = data.authUrl;
                } catch (e) {
                  console.error('Error en redirección, intentando método alternativo:', e);
                  // Método 2: Abrir en nueva ventana si falla
                  window.open(data.authUrl, '_self');
                }
              }, 1000);
              return;
            } else {
              console.error('❌ No se recibió URL de autorización del backend');
              throw new Error('URL de autorización no recibida');
            }
          } else {
            const errorText = await response.text();
            console.error('❌ Error response del backend:', response.status, errorText);
            throw new Error(`Backend respondió con error: ${response.status}`);
          }
        } catch (backendError) {
          console.log('⚠️ Backend endpoint no disponible, usando método directo');
          console.error('Error del backend:', backendError);
        }
        
        // Fallback: construir URL directamente (método alternativo)
        const businessId = currentUser.uid;
        const authParams = new URLSearchParams({
          response_type: 'code',
          client_id: MERCADO_PAGO_CONFIG.CLIENT_ID,
          redirect_uri: 'https://aturno-backend.onrender.com/api/oauth/mercadopago/callback',
          state: businessId
        });
        
        const authUrl = `${MERCADO_PAGO_CONFIG.AUTH_URL}?${authParams.toString()}`;
        console.log('🔗 URL de autorización construida directamente:', authUrl);
        
        // Redireccionar al usuario a Mercado Pago para autorización
        window.location.href = authUrl;
        
      } catch (error) {
        console.error('Error al conectar con MercadoPago:', error);
        alert('Error al conectar con Mercado Pago. Por favor, intenta nuevamente.');
        throw new Error(`Error en OAuth MercadoPago: ${error.message}`);
      }
    };
    

    
    

   // FUNCIÓN REMOVIDA: handleDisconnect 
   // Las integraciones ahora se manejan directamente desde las plataformas externas
   // (Google, MercadoPago, etc.) por lo que no necesitamos botones de desconectar
   /*
   const handleDisconnect = async (integrationKey) => {
     // ... función comentada ya que las integraciones se manejan externamente
   };
   */

       const getIntegrationName = (key) => {
      const names = {
        googleCalendar: "Google Calendar",
        mercadoPago: "Mercado Pago"
      };
      return names[key] || key;
    };

    // Logo de Google Calendar
    const GoogleLogo = ({ isConnected }) => (
      <svg width="20" height="20" viewBox="0 0 24 24">
        <path fill={isConnected ? "white" : "#4285F4"} d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill={isConnected ? "white" : "#34A853"} d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill={isConnected ? "white" : "#FBBC05"} d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill={isConnected ? "white" : "#EA4335"} d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    );

    const MercadoPagoLogo = ({ isConnected }) => (
        <img 
            src={mercadoPagoLogo} 
            alt="Mercado Pago" 
            style={{ 
                width: '20px', 
                height: '20px',
                opacity: isConnected ? 1 : 0.7,
                filter: isConnected ? 'none' : 'grayscale(30%)'
            }} 
        />
    );

       // Integraciones disponibles y funcionales
    const availableIntegrations = [
      {
        key: "googleCalendar", 
        name: "Google Calendar",
        description: "Sincroniza turnos con tu calendario personal",
        icon: <GoogleLogo isConnected={false} />,
        color: "#4285F4",
        category: "Calendario",
        benefits: ["Sincronización automática", "Recordatorios", "Disponibilidad en tiempo real"]
      },
      {
        key: "mercadoPago",
        name: "Mercado Pago",
        description: "Recibe pagos online de forma segura",
        icon: <MercadoPagoLogo isConnected={false} />,
        color: "#009EE3",
        category: "Pagos",
        benefits: ["Pagos online", "Múltiples métodos", "Seguridad garantizada"]
      }
    ];

  // ===============================================
  // ANALYTICS FUNCTIONS REMOVED
  // ===============================================

  // Funciones para personalización de marca
  const handleBrandingChange = (newBranding) => {
    setBusinessBranding(newBranding);
    // Aplicar colores inmediatamente durante la personalización
    applyBrandingColors(newBranding);
  };

  const saveBrandingChanges = async () => {
    try {
      setLoading(true);
      
      // Aplicar los colores a las variables CSS globales inmediatamente
      applyBrandingColors(businessBranding);
      
      // Guardar en la base de datos
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/business/branding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(businessBranding)
      });
      
      if (response.ok) {

        
        // También guardar en localStorage como backup para carga rápida
        localStorage.setItem('aturno_branding', JSON.stringify(businessBranding));
        
        alert('¡Colores de marca guardados exitosamente! Se aplicarán automáticamente en tu página de reservas.');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar en el servidor');
      }
    } catch (error) {
;
      
      // Guardar en localStorage como fallback si falla la base de datos
      localStorage.setItem('aturno_branding', JSON.stringify(businessBranding));
      
      alert('⚠️ Hubo un problema guardando en el servidor, pero se aplicaron localmente. Los cambios se sincronizarán cuando vuelva la conexión.');
    } finally {
      setLoading(false);
    }
  };

  // En modo demo (/demo/dashboard) no hay usuario y no tiene que haberlo: es la
  // vista que se le muestra a alguien que todavía no se registró. Sin esta
  // excepción el guard la mandaba al login y el botón "Ver Dashboard" de la
  // página de demo no llevaba a ningún lado.
  if (!currentUser && !isDemo) {
    // La ruta es /auth/login, no /login. Con /login se caía en el catch-all
    // /:slug de App.js, que intentaba abrir la página pública de un negocio
    // llamado "login" y mostraba "El negocio que buscas no existe": a cualquiera
    // que se le venciera la sesión lo mandaba a una pantalla rota.
    navigate('/auth/login', { replace: true });
    return null;
  }

  // ===============================================
  // FUNCIONES DE ESTADÍSTICAS
  // ===============================================

  // Función para calcular estadísticas básicas
  const calculateBasicStats = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Filtrar turnos del mes actual confirmados
    const currentMonthAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate.getMonth() === currentMonth && 
             aptDate.getFullYear() === currentYear && 
             apt.status === 'confirmed';
    });

    // Calcular ingresos del mes
    const monthlyRevenue = currentMonthAppointments.reduce((total, apt) => {
      const service = services.find(s => s.name === apt.service);
      return total + (service?.price || 0);
    }, 0);

    // Turnos confirmados del mes
    const confirmedAppointments = currentMonthAppointments.length;

    // Todos los turnos del mes (confirmados + pendientes)
    const allMonthAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate.getMonth() === currentMonth && 
             aptDate.getFullYear() === currentYear && 
             (apt.status === 'confirmed' || apt.status === 'pending');
    });

    // Servicio más popular (basado en todos los turnos)
    const serviceCount = {};
    allMonthAppointments.forEach(apt => {
      serviceCount[apt.service] = (serviceCount[apt.service] || 0) + 1;
    });
    const mostPopularService = Object.keys(serviceCount).reduce((a, b) => 
      serviceCount[a] > serviceCount[b] ? a : b, 'Ninguno'
    );

    // Tasa de confirmación (confirmados vs total del mes)
    const confirmationRate = allMonthAppointments.length > 0 
      ? Math.round((confirmedAppointments / allMonthAppointments.length) * 100) 
      : 0;
      
      return {
      monthlyRevenue,
      confirmedAppointments,
      totalAppointments: allMonthAppointments.length,
      mostPopularService,
      confirmationRate
    };
  };

  // Función para calcular estadísticas de rendimiento
  const calculatePerformanceStats = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Filtrar turnos del mes actual confirmados
    const currentMonthAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate.getMonth() === currentMonth && 
             aptDate.getFullYear() === currentYear && 
             apt.status === 'confirmed';
    });

    // Personal más activo
    const staffCount = {};
    currentMonthAppointments.forEach(apt => {
      const staffName = apt.staff?.name || apt.staff || apt.assignedStaff?.name || apt.assignedStaff || 'Sin asignar';
      staffCount[staffName] = (staffCount[staffName] || 0) + 1;
    });
    const mostActiveStaff = Object.keys(staffCount).reduce((a, b) => 
      staffCount[a] > staffCount[b] ? a : b, 'Ninguno'
    );

    // Recurso más utilizado
    const resourceCount = {};
    currentMonthAppointments.forEach(apt => {
      const resourceName = apt.resource?.name || apt.resource || apt.assignedResource?.name || apt.assignedResource || 'Sin asignar';
      resourceCount[resourceName] = (resourceCount[resourceName] || 0) + 1;
    });
    const mostUsedResource = Object.keys(resourceCount).reduce((a, b) => 
      resourceCount[a] > resourceCount[b] ? a : b, 'Ninguno'
    );

    // Horario pico
    const hourCount = {};
    currentMonthAppointments.forEach(apt => {
      const hour = parseInt(apt.time.split(':')[0]);
      const hourRange = `${hour}:00-${hour + 1}:00`;
      hourCount[hourRange] = (hourCount[hourRange] || 0) + 1;
    });
    const peakHour = Object.keys(hourCount).reduce((a, b) => 
      hourCount[a] > hourCount[b] ? a : b, 'Sin datos'
    );

    // Día más ocupado
    const dayCount = {};
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    currentMonthAppointments.forEach(apt => {
      const dayOfWeek = new Date(apt.date).getDay();
      const dayName = dayNames[dayOfWeek];
      dayCount[dayName] = (dayCount[dayName] || 0) + 1;
    });
    const busiestDay = Object.keys(dayCount).reduce((a, b) => 
      dayCount[a] > dayCount[b] ? a : b, 'Sin datos'
    );
    
    return {
      mostActiveStaff,
      mostUsedResource,
      peakHour,
      busiestDay
    };
  };

  // Función para calcular estadísticas operativas
  const calculateOperationalStats = () => {
    const now = new Date();
    
    // Turnos pendientes
    const pendingAppointments = appointments.filter(apt => apt.status === 'pending').length;

    // Turnos cancelados del mes
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const cancelledAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate.getMonth() === currentMonth && 
             aptDate.getFullYear() === currentYear && 
             apt.status === 'cancelled';
    }).length;

    // Próximos 7 días (incluye confirmados y pendientes)
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    const upcomingAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate >= now && aptDate <= nextWeek && (apt.status === 'confirmed' || apt.status === 'pending');
    }).length;

    // Duración promedio de servicios - CORREGIDO: 60 minutos fijos
    const averageDuration = 60;

    // Ticket promedio
    const confirmedAppointments = appointments.filter(apt => apt.status === 'confirmed');
    const totalRevenue = confirmedAppointments.reduce((total, apt) => {
      const service = services.find(s => s.name === apt.service);
      return total + (service?.price || 0);
          }, 0);
    const averageTicket = confirmedAppointments.length > 0 
      ? Math.round(totalRevenue / confirmedAppointments.length)
      : 0;

    // Nuevas métricas adicionales
    // Clientes únicos del mes
    const uniqueClients = new Set(
      appointments
        .filter(apt => {
          const aptDate = new Date(apt.date);
          return aptDate.getMonth() === currentMonth && 
                 aptDate.getFullYear() === currentYear && 
                 apt.status === 'confirmed';
        })
        .map(apt => apt.customerName || apt.customer)
    ).size;

    // Tasa de ocupación (turnos confirmados vs capacidad total estimada)
    const totalSlotsPerDay = 8; // 8 horas de trabajo por día
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const totalCapacity = totalSlotsPerDay * daysInMonth;
    const confirmedThisMonth = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate.getMonth() === currentMonth && 
             aptDate.getFullYear() === currentYear && 
             apt.status === 'confirmed';
    }).length;
    const occupancyRate = totalCapacity > 0 ? Math.round((confirmedThisMonth / totalCapacity) * 100) : 0;
    
    return {
      pendingAppointments,
      cancelledAppointments,
      upcomingAppointments,
      averageDuration,
      averageTicket,
      uniqueClients,
      occupancyRate
    };
  };

  // Función para obtener datos de gráfico de ingresos (últimos 30 días)
  const getRevenueChartData = () => {
    const days = [];
    const revenues = [];
    const now = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      // `date` arrastra la hora actual, así que con `toISOString` cada día del
      // gráfico se corría uno a partir de las 21:00: los ingresos quedaban
      // atribuidos a la fecha equivocada.
      const dateString = aFechaISO(date);
      
      // Calcular ingresos del día
      const dayRevenue = appointments
        .filter(apt => apt.date === dateString && apt.status === 'confirmed')
        .reduce((total, apt) => {
          const service = services.find(s => s.name === apt.service);
          return total + (service?.price || 0);
        }, 0);

      days.push(date.getDate());
      revenues.push(dayRevenue);
    }

    return { days, revenues };
  };

  // Función para obtener distribución de servicios
  const getServicesDistribution = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const serviceStats = {};
    
    appointments
      .filter(apt => {
        const aptDate = new Date(apt.date);
        return aptDate.getMonth() === currentMonth && 
               aptDate.getFullYear() === currentYear && 
               apt.status === 'confirmed';
      })
      .forEach(apt => {
        if (!serviceStats[apt.service]) {
          serviceStats[apt.service] = { count: 0, revenue: 0 };
        }
        serviceStats[apt.service].count++;
        
        const service = services.find(s => s.name === apt.service);
        serviceStats[apt.service].revenue += (service?.price || 0);
      });

    return Object.entries(serviceStats)
      .map(([name, stats]) => ({
        name,
        count: stats.count,
        revenue: stats.revenue,
        percentage: 0 // Se calculará después
      }))
      .sort((a, b) => b.count - a.count);
  };

  // Función para guardar datos bancarios
  const saveBankingData = async () => {
    setSavingBankingData(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/business/banking-data`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bankingData)
      });

      if (response.ok) {
        alert('✅ Datos bancarios guardados correctamente');
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.message || 'No se pudieron guardar los datos'}`);
      }
    } catch (error) {
      console.error('Error saving banking data:', error);
      alert('❌ Error inesperado al guardar los datos bancarios');
    } finally {
      setSavingBankingData(false);
    }
  };

  // Función para verificar si el usuario puede acceder a Personal y Recursos
  /**
   * Si este negocio contrató el módulo de seguimiento de pacientes.
   *
   * NO sale de `features`, y esa es la diferencia con canAccessStaffAndResources:
   * `features` es lo que declara el TIER, y este módulo no lo declara ningún
   * tier porque se vende aparte. Si estuviera adentro del tier, bajar de plan se
   * lo apagaría de rebote a alguien que lo pagó por separado.
   *
   * Esconder la sección es comodidad, no seguridad: el backend aplica el mismo
   * interruptor en las 18 rutas, así que un panel que lo mostrara de más no
   * daría acceso a nada.
   */
  const tieneModuloSalud = () => {
    // En la demo se muestra todo, igual que el resto de las secciones de pago:
    // es para enseñar el producto a quien todavía no compró.
    if (isDemo) return true;
    return userPlan?.moduloSalud === true;
  };

  const canAccessStaffAndResources = () => {
    // En la demo se muestra todo: es para enseñarle el producto completo a
    // alguien que todavía no compró, así que no tiene plan contra el cual
    // comparar. En una cuenta real el límite por plan sigue igual.
    if (isDemo) return true;

    // Se decide por los límites que el backend efectivamente autoriza, no por
    // el nombre del plan.
    //
    // Antes esto era una lista escrita a mano
    // (['business-starter', 'business-pro', 'business-pro-plus']) que no
    // coincidía con lo que el backend permite: durante el trial se otorgan 3
    // empleados y 5 recursos, y los planes personal también los incluyen (hasta
    // 15 y 30 en personal-pro-plus), pero ningún id de trial ni personal está
    // en esa lista. Resultado: dos secciones enteras escondidas para cuentas
    // que sí tienen la función, incluido cualquiera probando el trial.
    //
    // `features` es el mismo objeto que usa el backend para autorizar, y ya
    // viene en la respuesta de /api/plan/current que guardamos en userPlan.
    const features = userPlan?.features;
    if (!features) return false; // sin datos del plan, no mostramos de más

    const permite = (limite) => limite === -1 || limite > 0;
    return permite(features.maxStaff) || permite(features.maxResources);
  };

  // Function to get plan limits and current usage
  const getPlanLimits = () => {
    if (!userPlan || !userPlan.plan) return null;
    const planId = userPlan.plan.id;
    
    const limits = {
      'trial': { 
        monthlyReservations: 100,
        hasWhatsAppBot: false,
        hasWhatsAppAI: false,
        hasStaffAndResources: true
      },
      'personal': { 
        monthlyReservations: null, // unlimited
        hasWhatsAppBot: false,
        hasWhatsAppAI: false,
        hasStaffAndResources: false
      },
      'personal-pro': { 
        monthlyReservations: null,
        hasWhatsAppBot: true,
        hasWhatsAppAI: false,
        hasStaffAndResources: false
      },
      'personal-pro-plus': { 
        monthlyReservations: null,
        hasWhatsAppBot: true,
        hasWhatsAppAI: true,
        hasStaffAndResources: false
      },
      'business-starter': { 
        monthlyReservations: null,
        hasWhatsAppBot: false,
        hasWhatsAppAI: false,
        hasStaffAndResources: true
      },
      'business-pro': { 
        monthlyReservations: null,
        hasWhatsAppBot: true,
        hasWhatsAppAI: false,
        hasStaffAndResources: true
      },
      'business-pro-plus': { 
        monthlyReservations: null,
        hasWhatsAppBot: true,
        hasWhatsAppAI: true,
        hasStaffAndResources: true
      }
    };
    
    return limits[planId] || null; // Sin fallback, si no hay plan válido = null
  };

  // Function to check if user is approaching or exceeded limits
  const checkPlanLimits = () => {
    const limits = getPlanLimits();
    if (!limits) return { withinLimits: true };
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const monthlyAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate.getMonth() === currentMonth && aptDate.getFullYear() === currentYear;
    });
    
    const result = {
      withinLimits: true,
      currentUsage: monthlyAppointments.length,
      limit: limits.monthlyReservations,
      hasWhatsAppBot: limits.hasWhatsAppBot,
      hasWhatsAppAI: limits.hasWhatsAppAI,
      hasStaffAndResources: limits.hasStaffAndResources
    };
    
    if (limits.monthlyReservations && monthlyAppointments.length >= limits.monthlyReservations) {
      result.withinLimits = false;
      result.exceeded = true;
    } else if (limits.monthlyReservations && monthlyAppointments.length >= limits.monthlyReservations * 0.8) {
      result.withinLimits = true;
      result.nearLimit = true;
    }
    
    return result;
  };

  // FUNCIONES DE NOTIFICACIONES PARA BADGES
  const getScheduleNotificationCount = () => {
    if (!businessInfo?.schedule) return 1; // Si no hay horarios configurados
    
    const schedule = businessInfo.schedule;
    const hasActiveDay = Object.keys(schedule).some(day => 
      schedule[day]?.enabled && schedule[day]?.ranges?.length > 0
    );
    
    return hasActiveDay ? 0 : 1; // 1 si no hay días activos, 0 si hay al menos uno
  };

  const getServicesNotificationCount = () => {
    const incompleteServices = services.filter(service => {
      // Un servicio está incompleto si:
      // 1. No tiene nombre
      // 2. No tiene duración
      // 3. No tiene precio
      // 4. No está activo
      const hasBasicInfo = service.name && service.duration && (service.price || service.price === 0);
      const isActive = service.active !== false; // Por defecto activo si no se especifica
      
      return !hasBasicInfo || !isActive;
    });
    
    return incompleteServices.length;
  };

  const getStaffNotificationCount = () => {
    if (!canAccessStaffAndResources()) return 0; // No mostrar notificación si no tiene acceso
    
    const staffWithoutSchedule = staff.filter(person => {
      if (!person.active) return false; // No contar personal inactivo
      
      if (!person.schedule) return true; // Sin horarios configurados
      
      // Verificar si tiene al menos un día con horarios
      const hasActiveSchedule = Object.keys(person.schedule).some(day => 
        person.schedule[day]?.enabled && person.schedule[day]?.ranges?.length > 0
      );
      
      return !hasActiveSchedule;
    });
    
    return staffWithoutSchedule.length;
  };

  const getResourcesNotificationCount = () => {
    if (!canAccessStaffAndResources()) return 0; // No mostrar notificación si no tiene acceso
    
    const resourcesWithoutSchedule = resources.filter(resource => {
      if (!resource.active) return false; // No contar recursos inactivos
      
      if (!resource.schedule) return true; // Sin horarios configurados
      
      // Verificar si tiene al menos un día con horarios
      const hasActiveSchedule = Object.keys(resource.schedule).some(day => 
        resource.schedule[day]?.enabled && resource.schedule[day]?.ranges?.length > 0
      );
      
      return !hasActiveSchedule;
    });
    
    return resourcesWithoutSchedule.length;
  };

  // Mostrar pantalla de carga mientras se cargan los datos del negocio
  if (loading || !businessDataLoaded) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <h2>Cargando tu negocio...</h2>
          <p>Estamos obteniendo los datos de tu onboarding</p>
        </div>
      </div>
    );
  }

  // BLOQUEO COMPLETO: Si el trial expiró y no hay plan pagado, mostrar solo overlay y no dashboard
  // A menos que estemos en modo debug
  // SALE DEL PLAN, NO DE LA SUSCRIPCIÓN, y ahí estaba el bug.
  //
  // Quien bloquea es el backend, y decide mirando `businesses.freeTrialEndDate`
  // en Firestore: cuando el trial venció pone todas las features en cero y
  // `plan.status` en 'trial_expired'. El panel, en cambio, decidía si avisar
  // leyendo `subscriptionData`, que viene de `/api/subscriptions/current` — una
  // ruta que CONSULTA A MERCADOPAGO. Para una cuenta que nunca se suscribió
  // MercadoPago no tiene nada que devolver, la consulta falla, el panel hace
  // `setSubscriptionData(null)` y esta condición quedaba en falsy.
  //
  // O sea: el bloqueo se aplicaba igual (las features venían en cero, así que
  // desaparecían las nueve secciones de pago) y el cartel que lo explica no
  // aparecía nunca. La cuenta veía un panel a medias, sin decir por qué ni
  // ofrecer el link para pagar — que es exactamente el síntoma reportado.
  //
  // La regla: el aviso tiene que leer la MISMA fuente que el bloqueo. Un fallo
  // de MercadoPago puede dejar sin datos a la pantalla de facturación; no puede
  // dejar a alguien encerrado sin explicación.
  const isTrialExpiredWithoutPlan = userPlan?.plan?.status === 'trial_expired';

  if ((isTrialExpiredWithoutPlan || showTrialExpiredOverlay) && !isDebugMode()) {
    return (
      <div className="dashboard-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f8f9fa' 
      }}>
        <TrialExpiredOverlay 
          onClose={() => {
            // Redirigir a planes en lugar de cerrar
            navigate('/admin/plans');
          }}
        />
      </div>
    );
  }

  // Las 9 secciones del panel. Antes eran 9 bloques de JSX casi idénticos
  // repetidos a mano (78 líneas), cada uno con su emoji y su contador.
  const SECCIONES = [
    { id: 'appointments', label: 'Turnos', Icono: Calendar },
    { id: 'schedule', label: 'Horarios', Icono: Clock, badge: getScheduleNotificationCount() },
    { id: 'services', label: 'Servicios', Icono: Scissors, badge: getServicesNotificationCount() },
    {
      id: 'resources',
      label: 'Personal y recursos',
      Icono: Users,
      badge: getStaffNotificationCount() + getResourcesNotificationCount(),
      // Sección de pago: solo la ven los planes que la incluyen
      oculta: !canAccessStaffAndResources(),
    },
    /* Add-on, no una función de un plan: se vende aparte, así que el
       interruptor es `moduloSaludActivo` en el documento del negocio y NO un
       flag de TIERS. Si estuviera en el tier, bajar de plan se lo apagaría de
       rebote a alguien que lo pagó por separado. El backend lo aplica igual en
       las 18 rutas —esconder la sección es comodidad, no seguridad. */
    { id: 'pacientes', label: 'Pacientes', Icono: HeartPulse, oculta: !tieneModuloSalud() },
    { id: 'stats', label: 'Estadísticas', Icono: BarChart3 },
    { id: 'integrations', label: 'Integraciones', Icono: Link2 },
    { id: 'whatsapp', label: 'Asistente WhatsApp', Icono: Bot },
    { id: 'settings', label: 'Configuración', Icono: Settings },
    { id: 'help', label: 'Ayuda', Icono: HelpCircle },
  ];

  return (
    <div className="dashboard-container">
      {/* Debug Mode Indicator */}
      {isDebugMode() && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          backgroundColor: '#ff6b6b',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '5px',
          fontSize: '12px',
          fontWeight: 'bold',
          zIndex: 10000,
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
        }}>
          🧪 DEBUG MODE
        </div>
      )}
      
      {/* Trial Alert */}
      <TrialAlert />

      {/* Botón Hamburguesa (solo visible en móvil) */}
      {isMobile && (
        <button 
          className={`hamburger-btn ${isSidebarOpen ? 'open' : ''}`}
          onClick={toggleSidebar}
          aria-label="Abrir menú"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      )}

      {/* Overlay (solo visible cuando sidebar está abierto en móvil) */}
      {isMobile && isSidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Cerrar menú"
        />
      )}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isMobile ? 'mobile' : ''} ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img 
            src="/logo-aturno.png" 
            alt="Aturno Logo" 
            className="sidebar-logo"
            style={{ width: '120px', height: 'auto' }}
          />
          <p>{businessInfo?.name || "Mi Negocio"}</p>
          {currentUser && (
            <p className="user-email">{currentUser.email}</p>
          )}
        </div>

        <nav className="sidebar-nav" aria-label="Secciones del panel">
          {SECCIONES.filter((s) => !s.oculta).map(({ id, label, Icono, badge }) => (
            <button
              key={id}
              className={activeSection === id ? 'active' : ''}
              onClick={() => handleSectionChange(id)}
              aria-current={activeSection === id ? 'page' : undefined}
            >
              <span className="icon" aria-hidden="true">
                <Icono size={18} strokeWidth={2} />
              </span>
              <span>{label}</span>
              {badge > 0 && <span className="notification-badge">{badge}</span>}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button 
            className="view-page-btn"
            onClick={async () => {
              try {
                // Primero intentar actualizar el slug en el backend si no existe
                if (!businessInfo?.slug) {
                  // Updating slug in backend
                  const token = await currentUser.getIdToken();
                  const response = await fetch(`${API_URL}/api/business/update-slug`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  
                  if (response.ok) {
                    const result = await response.json();
                    // Slug updated successfully
                    
                    // Actualizar el estado local con el nuevo slug
                    setBusinessInfo(prev => ({
                      ...prev,
                      slug: result.slug
                    }));
                    
                    // Abrir la página con el nuevo slug
                    window.open(`/${result.slug}`, '_blank');
                    return;
                  }
                }
                
                // Usar businessInfo.slug o generar slug desde businessInfo.name
                const slug = businessInfo?.slug || 
                            (businessInfo?.name ? 
                              businessInfo.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : 
                              null);
                
                if (slug) {
                  window.open(`/${slug}`, '_blank');
                } else {
                  alert('Tu negocio aún no tiene una URL configurada. Completa la información del negocio en Configuración.');
                }
              } catch (error) {
                console.error('❌ FRONTEND: Error al actualizar slug:', error);
                // Fallback: usar slug temporal basado en el nombre
                const tempSlug = businessInfo?.name ? 
                  businessInfo.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : 
                  'mi-negocio';
                window.open(`/${tempSlug}`, '_blank');
              }
            }}
            disabled={!businessInfo?.name}
          >
            Ver mi página
          </button>
          {/* Debug Tools - Solo visible en modo debug */}
          {isDebugMode() && (
            <button 
              className="diagnostic-btn"
              onClick={() => window.open('/payment-diagnostic', '_blank')}
              style={{
                backgroundColor: '#ff6b6b',
                color: 'white',
                padding: '8px 12px',
                margin: '10px 0',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              🔍 Diagnóstico de Pagos
            </button>
          )}
          
          <button 
            className="logout-btn"
            onClick={logout}
          >
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Plan Limits Notification */}
        {(() => {
          const limitCheck = checkPlanLimits();
          if (limitCheck.exceeded) {
            return (
              <div className="plan-limit-notification error">
                <div className="notification-icon">⚠️</div>
                <div className="notification-content">
                  <h4>Límite de reservas alcanzado</h4>
                  <p>
                    Has alcanzado el límite de {limitCheck.limit} reservas por mes del plan gratuito. 
                    Actualiza tu plan para continuar recibiendo reservas.
                  </p>
                  <button 
                    className="upgrade-btn"
                    onClick={() => navigate('/admin/plans')}
                  >
                    Actualizar Plan
                  </button>
                </div>
              </div>
            );
          } else if (limitCheck.nearLimit) {
            return (
              <div className="plan-limit-notification warning">
                <div className="notification-icon">📊</div>
                <div className="notification-content">
                  <h4>Acercándose al límite</h4>
                  <p>
                    Has usado {limitCheck.currentUsage} de {limitCheck.limit} reservas este mes. 
                    Considera actualizar tu plan para reservas ilimitadas.
                  </p>
                  <button 
                    className="upgrade-btn secondary"
                    onClick={() => navigate('/admin/plans')}
                  >
                    Ver Planes
                  </button>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Appointments Section */}
        {activeSection === "appointments" && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Gestión de Turnos</h2>
              {/* Cuántos entraron desde la última vez. Va en el encabezado
                  porque los turnos marcados pueden estar en cualquier punto de
                  una lista larga: sin el número hay que recorrerla entera para
                  saber si hay algo para mirar. */}
              {cantidadDeNuevos > 0 && (
                <p className="turnos-nuevos-resumen">
                  {cantidadDeNuevos === 1
                    ? '1 turno nuevo desde la última vez que entraste'
                    : `${cantidadDeNuevos} turnos nuevos desde la última vez que entraste`}
                </p>
              )}
            </div>

            {/* Los turnos que entraron mientras esta pantalla estaba abierta.
                Es un aviso y no una inserción: la lista se reacomoda cuando el
                negocio lo decide, no mientras está leyendo un turno. */}
            {idsPendientes.length > 0 && (
              <button
                type="button"
                onClick={integrarTurnosNuevos}
                className="turnos-nuevos"
              >
                <span className="turnos-nuevos__punto" aria-hidden="true" />
                {idsPendientes.length === 1
                  ? 'Llegó 1 turno nuevo'
                  : `Llegaron ${idsPendientes.length} turnos nuevos`}
                <span className="turnos-nuevos__accion">Ver</span>
              </button>
            )}

            <BarraDeTurnos
              filtros={appointmentFilters}
              onCambiarFiltros={setAppointmentFilters}
              servicios={services}
              mostrarPasados={showPastAppointments}
              onCambiarMostrarPasados={setShowPastAppointments}
              vista={appointmentView}
              onCambiarVista={setAppointmentView}
              orden={sortOrder}
              onCambiarOrden={setSortOrder}
              dias={diasCalendario}
              onCambiarDias={setDiasCalendario}
              onBloquearHorario={openBlockTimeModal}
              onAgregarTurno={() => {
                setError('');
                setMostrarAgregarTurno(true);
              }}
            />

            {/* Vista Lista */}
            {appointmentView === "list" && (
              <div className="appointments-list">
                {getSortedAppointments().length === 0 ? (
                  <div className="no-appointments">
                    {appointments.length === 0 ? (
                      <>
                        <p>No hay turnos confirmados aún.</p>
                        <p className="hint">Los turnos aparecerán aquí cuando los clientes reserven desde tu página.</p>
                      </>
                    ) : (
                      <>
                        <p>No hay turnos que coincidan con los filtros.</p>
                        <p className="hint">
                          {!showPastAppointments && appointments.some(a => isAppointmentPast(a)) 
                            ? "Los turnos pasados están ocultos. Activa 'Mostrar turnos pasados' para verlos." 
                            : "Intenta cambiar los filtros para ver más resultados."
                          }
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  getSortedAppointments().map(appointment => {
                    const isPast = isAppointmentPast(appointment);
                    return (
                    <div
                      key={appointment.id}
                      className={`appointment-card ${isPast ? 'past-appointment' : ''} ${
                        idsRecienLlegados.has(appointment.id) ? 'turno-recien-llegado' : ''
                      }`}
                    >
                      {/* Borrar el turno. Va suelta arriba a la derecha y no
                          entre las acciones de abajo a propósito: ahí ya hay
                          una ✕ que cancela, y dos cruces juntas que hacen cosas
                          distintas —una reversible y la otra no— es la forma de
                          que alguien borre queriendo cancelar. */}
                      <button
                        className="borrar-turno"
                        onClick={() => setTurnoABorrar(appointment)}
                        title="Borrar el turno definitivamente"
                        aria-label={`Borrar el turno de ${appointment.customerName}`}
                      >
                        ✕
                      </button>

                      <div className="appointment-time">
                        <span className="time">{appointment.time} hs</span>
                        <span className="date">
                          {new Date(appointment.date + 'T00:00:00').toLocaleDateString('es-AR', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short'
                          })}
                          {isPast && <span className="past-badge">Pasado</span>}
                        </span>
                      </div>
                      
                      <div className="appointment-info">
                        <h4>
                          {appointment.customerName}
                          {/* Lo que entró desde la última vez que se miró el
                              panel — no "lo de las últimas 12 horas", que era
                              el criterio anterior y marcaba de nuevo lo que ya
                              habías visto esa misma mañana. Ver lib/novedades.js. */}
                          {visitaLeida && !isPast && esTurnoNuevo(appointment.createdAt, { ultimaVisita: ultimaVisitaATurnos }) && (
                            <span className="reserva-nueva" title={`Entró ${haceCuanto(appointment.createdAt)}`}>
                              Nuevo
                            </span>
                          )}
                        </h4>
                        <p>{appointment.service}</p>
                        <p className="phone">{appointment.phone}</p>

                        {/* Datos de paciente, sólo si el turno los tiene.
                            Van juntos y en una línea propia porque se usan
                            juntos: al preparar la consulta o al facturarle a la
                            obra social se necesita el DNI Y la cobertura, no uno
                            de los dos. Un negocio que no los pide no ve nada de
                            esto: la línea entera no se renderiza. */}
                        {(appointment.dni || appointment.obraSocial) && (
                          <p className="datos-paciente">
                            {appointment.dni && (
                              <span className="dato-paciente">
                                <span className="dato-etiqueta">DNI</span>
                                {appointment.dni}
                              </span>
                            )}
                            {appointment.obraSocial && (
                              <span className="dato-paciente">
                                <span className="dato-etiqueta">Cobertura</span>
                                {nombreDeObraSocial(appointment.obraSocial, obrasPropias)}
                                {/* El número de afiliado va pegado a la
                                    cobertura, entre paréntesis: solo no dice
                                    nada, y es el par lo que sirve para facturar. */}
                                {appointment.numeroDeAfiliado && ` (${appointment.numeroDeAfiliado})`}
                              </span>
                            )}
                          </p>
                        )}

                        {/* Cuándo lo pidieron. La fecha exacta va en el title,
                            porque mirando la lista la pregunta es "¿esto es
                            nuevo?" y no "¿qué día fue?". Ver lib/tiempo.js. */}
                        {haceCuanto(appointment.createdAt) && (
                          <p className="reservado-cuando" title={fechaExacta(appointment.createdAt)}>
                            Reservado {haceCuanto(appointment.createdAt)}
                          </p>
                        )}

                        {/* La cuenta regresiva del pago.
                            Este turno todavía no es un turno: es un horario
                            apartado mientras la persona paga la seña. Decir
                            cuánto le queda es la diferencia entre "hay algo
                            raro en mi agenda" y "esto se resuelve solo en un
                            rato". Cuando llega a cero la reserva desaparece de
                            la lista, sin recargar. */}
                        {appointment.status === 'pending_deposit'
                          && minutosHasta(appointment.holdExpiresAt, ahora) !== null && (
                          <p className="esperando-pago">
                            Sin pagar · el horario se libera en{' '}
                            {Math.max(1, minutosHasta(appointment.holdExpiresAt, ahora))} min
                          </p>
                        )}


                        {/* Información de Personal y Recursos integrada */}
                        <div className="appointment-assignments-inline">
                          {/* Personal */}
                          <div className="assignment-inline">
                            <span className="assignment-label">👤</span>
                            <div className="assignment-value">
                              {(() => {
                                // 🔍 DEBUG: Ver qué información de personal llega (solo primera cita)
                                if (appointment.id === 'CRMWGtpJjutJ8q2hA8pT') {
                      
                                          // Appointment data analysis
                                }
                                
                                // Buscar personal específico de la reserva
                                let staffName = null;
                                
                                // Prioridad 1: staff directo como string
                                if (appointment.staff && typeof appointment.staff === 'string') {
                                  staffName = appointment.staff;
                                  // Staff found as string
                                }
                                // Prioridad 2: staff como objeto con name
                                else if (appointment.staff && typeof appointment.staff === 'object' && appointment.staff.name) {
                                  staffName = appointment.staff.name;
                                  // Staff found as object
                                }
                                // Prioridad 3: assignedStaff como string
                                else if (appointment.assignedStaff && typeof appointment.assignedStaff === 'string') {
                                  staffName = appointment.assignedStaff;
                                  // Assigned staff found as string
                                }
                                // Prioridad 4: assignedStaff como objeto con name
                                else if (appointment.assignedStaff && typeof appointment.assignedStaff === 'object' && appointment.assignedStaff.name) {
                                  staffName = appointment.assignedStaff.name;
                                  // Assigned staff found as object
                                }
                                
                                if (staffName) {
                                  /* Se toca y se abre la ficha de la persona.
                                     Es un <button> y no un <span> con onClick
                                     para que se llegue con el tabulador y un
                                     lector de pantalla lo anuncie como lo que
                                     es. La referencia que se pasa es la que
                                     guardó la reserva; quien abre la ficha la
                                     resuelve contra el equipo de hoy. */
                                  return (
                                    <button
                                      type="button"
                                      className="assignment-tag staff-tag staff-tag--tocable"
                                      onClick={() => abrirFichaDeProfesional(appointment)}
                                      title={`Ver la ficha de ${staffName}`}
                                    >
                                      {staffName}
                                    </button>
                                  );
                                }
                                
                                // Fallback: buscar en el servicio
                                const relatedService = services.find(s => s.name === appointment.service);
                                if (relatedService?.assignedStaff && relatedService.assignedStaff.length > 0) {
                                  return (
                                    <div className="assignment-tags">
                                      {relatedService.assignedStaff.map(id => staffMapping[id]).filter(Boolean).map((name, index) => (
                                        <span key={index} className="assignment-tag staff-tag">
                                          {name}
                                        </span>
                                      ))}
                                    </div>
                                  );
                                }
                                
                                return <span className="assignment-tag staff-tag fallback">Cualquier personal</span>;
                              })()}
                            </div>
                          </div>

                          {/* Recurso/Lugar.
                              Si no hay ninguno, no se dibuja NADA: se va la fila
                              entera, el 📍 incluido. Antes caía en "Cualquier
                              lugar", que no dice nada útil de un turno y que un
                              negocio sin recursos cargados —la mayoría— veía
                              repetido en TODOS sus turnos, como si el lugar
                              fuera un dato que le falta completar.
                              Por eso el 📍 vive adentro y no afuera: dejándolo
                              afuera, esconder el texto dejaba el ícono huérfano. */}
                          {(() => {
                                // Buscar recurso específico de la reserva
                                let resourceName = null;
                                
                                // Prioridad 1: resource directo como string
                                if (appointment.resource && typeof appointment.resource === 'string') {
                                  resourceName = appointment.resource;
                                  // Resource found as string
                                }
                                // Prioridad 2: resource como objeto con name
                                else if (appointment.resource && typeof appointment.resource === 'object' && appointment.resource.name) {
                                  resourceName = appointment.resource.name;
                                  // Resource found as object
                                }
                                // Prioridad 3: assignedResource como string
                                else if (appointment.assignedResource && typeof appointment.assignedResource === 'string') {
                                  resourceName = appointment.assignedResource;
                                  // Assigned resource found as string
                                }
                                // Prioridad 4: assignedResource como objeto con name
                                else if (appointment.assignedResource && typeof appointment.assignedResource === 'object' && appointment.assignedResource.name) {
                                  resourceName = appointment.assignedResource.name;
                                  // Assigned resource found as object
                                }
                                // Prioridad 5: resourceName directo
                                else if (appointment.resourceName) {
                                  resourceName = appointment.resourceName;
                                  // Resource found in resourceName
                                }
                                
                                // Ya no usamos slotIndex para generar nombres de salas automáticamente
                                // Los recursos deben venir de la base de datos real

                                let contenido = null;

                                if (resourceName) {
                                  contenido = (
                                    <span className="assignment-tag location-tag">
                                      {resourceName}
                                    </span>
                                  );
                                } else {
                                  // Si la reserva no trae recurso, los que el servicio tenga asignados.
                                  const relatedService = services.find(s => s.name === appointment.service);
                                  const delServicio = (relatedService?.assignedLocation || [])
                                    .map(id => locationMapping[id])
                                    .filter(Boolean);

                                  if (delServicio.length > 0) {
                                    contenido = (
                                      <div className="assignment-tags">
                                        {delServicio.map((name, index) => (
                                          <span key={index} className="assignment-tag location-tag">
                                            {name}
                                          </span>
                                        ))}
                                      </div>
                                    );
                                  }
                                }

                                // Sin recurso no se dibuja nada.
                                if (!contenido) return null;

                                return (
                                  <div className="assignment-inline">
                                    <span className="assignment-label">📍</span>
                                    <div className="assignment-value">{contenido}</div>
                                  </div>
                                );
                              })()}
                        </div>
                      </div>

                      <div className="appointment-status">
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: getStatusColors(appointment.status).bg,
                            color: getStatusColors(appointment.status).color
                          }}
                        >
                          {getStatusLabel(appointment.status)}
                        </span>
                      </div>

                      <div className="appointment-actions">
                        <button
                          className="action-btn confirm"
                          onClick={() => handleStatusChange(appointment.id, "confirmed")}
                          disabled={appointment.status === "confirmed" || appointment.status === "cancelled"}
                          title={appointment.status === "cancelled" ? "No se puede confirmar un turno cancelado" : "Confirmar turno"}
                        >
                          ✓
                        </button>
                        <button
                          className="action-btn pending"
                          onClick={() => handleStatusChange(appointment.id, "pending")}
                          disabled={appointment.status === "pending" || appointment.status === "cancelled"}
                          title={appointment.status === "cancelled" ? "No se puede cambiar un turno cancelado" : "Marcar como pendiente"}
                        >
                          ⏳
                        </button>
                        <button
                          className="action-btn cancel"
                          onClick={() => handleCancelAppointment(appointment)}
                          disabled={appointment.status === "cancelled"}
                          title={appointment.status === "cancelled" ? "Turno ya cancelado" : "Cancelar turno (libera el horario)"}
                        >
                          {appointment.status === "cancelled" ? "🚫" : "✕"}
                        </button>
                        <button 
                          className="action-btn whatsapp"
                          title="Contactar por WhatsApp"
                          onClick={() => {
                            const message = `Hola ${appointment.customerName}, te recordamos tu turno para ${appointment.service} el ${new Date(appointment.date + 'T00:00:00').toLocaleDateString('es-AR')} a las ${appointment.time} hs.`;
                            window.open(`https://wa.me/${appointment.phone}?text=${encodeURIComponent(message)}`, '_blank');
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Vista Calendario */}
            {appointmentView === "calendar" && (
              <CalendarioDeTurnos
                dias={diasCalendario}
                desplazamiento={desplazamientoCalendario}
                onDesplazar={setDesplazamientoCalendario}
                turnosDelDia={getAppointmentsForDay}
                esPasado={isAppointmentPast}
                mostrarPasados={showPastAppointments}
                resolverPersonal={nombrePersonalDeTurno}
                resolverLugar={nombreLugarDeTurno}
                onCambiarEstado={handleStatusChange}
                onCancelar={handleCancelAppointment}
                onBorrar={setTurnoABorrar}
              />
            )}


          </section>
        )}

        {/* Schedule Section - NUEVA */}
        {activeSection === "schedule" && (
          <section className="dashboard-section">
            {/* El botón de guardar aparece recién cuando hay algo que guardar, y
                con él el de descartar. Estando siempre a la vista no distingue
                entre un horario ya guardado y uno que falta guardar, que es
                justo lo que hay que poder ver de un vistazo en esta pantalla:
                los cambios de la grilla se ven aplicados apenas se sueltan. */}
            <div className="section-header">
              <h2>Configuración de Horarios</h2>
              {hayCambiosEnHorarios ? (
                <div className="acciones-horarios">
                  <button
                    type="button"
                    className="btn-descartar-horarios"
                    onClick={descartarHorarios}
                    disabled={guardandoHorarios}
                  >
                    Descartar
                  </button>
                  <Button
                    size="sm"
                    className="shrink-0"
                    onClick={guardarHorarios}
                    disabled={guardandoHorarios}
                  >
                    <FaSave className="mr-2" />
                    {guardandoHorarios ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                </div>
              ) : (
                <span className="horarios-al-dia">Todo guardado</span>
              )}
            </div>

            {scheduleError && (
              <div className="horarios-error" role="alert">
                {scheduleError}
              </div>
            )}

            <BusinessScheduleForm
              value={businessInfo?.schedule || {}}
              onChange={schedule => setBusinessInfo(prev => ({ ...prev, schedule }))}
              hours={hours}
              minutes={minutes}
              dayNames={dayNames}
              businessSchedule={businessData?.schedule}
            />
            
            {/* Los horarios ya bloqueados. Era la quinta pieza de esta pantalla
                y la única que seguía siendo markup suelto acá adentro, con sus
                estilos perdidos en Dashboard.css. Ver ListaDeBloqueos.jsx. */}
            <ListaDeBloqueos bloqueos={blockedSlots} onDesbloquear={handleUnblockTime} />
          </section>
        )}

        {/* Services Section */}
        {activeSection === "services" && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Gestión de Servicios</h2>
              <p>Administra los servicios que ofreces a tus clientes</p>
            </div>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            {message && (
              <div className="success-message">
                <span className="success-icon">✅</span>
                {message}
              </div>
            )}
            <div className="services-grid">
              {services.map(service => {
                // Determinar si es cuenta individual basado en el plan
                const planType = userPlan?.planType || userPlan?.plan?.id;
                const hasBusinessPlan = planType && planType.startsWith('business');
                const isIndividualAccount = businessData?.userType === 'individual' && !hasBusinessPlan;
                const canActivate = isIndividualAccount ||
                  (service.assignedStaff && service.assignedStaff.length > 0 && 
                   service.assignedStaff.some(staffId => staff.some(s => s.id === staffId)));
                const needsStaff = !canActivate && !isIndividualAccount;
                
                return (
                <div key={service.id} className={`service-card ${!service.active ? 'inactive' : ''} ${needsStaff ? 'needs-configuration' : ''}`}>
                  <div className="service-header">
                    <h3>{service.name}</h3>
                    {needsStaff && (
                      <div className="warning-indicator" title="Este servicio necesita personal asignado para poder activarse">
                        ⚠️
                      </div>
                    )}
                    <Switch
                      checked={service.active}
                      onChange={() => toggleServiceActive(service.id, service.active)}
                      disabled={!service.active && needsStaff}
                      aria-label={`Activar ${service.name}`}
                    />
                  </div>
                  
                  {needsStaff && (
                    <div className="configuration-warning">
                      <span>⚠️ Asigna personal para activar este servicio</span>
                    </div>
                  )}
                  
                  <div className="service-details">
                    <div className="service-price">
                      <label>Precio:</label>
                      <div className="price-display">
                        <span className="price-value">${service.price ? service.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '0'}</span>
                      </div>
                    </div>
                    
                    <div className="service-duration">
                      <label>Duración:</label>
                      <div className="duration-display">
                        <span className="duration-value">{service.duration} min</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Personal y Recursos Asignados */}
                  <div className="service-assignments">
                    <div className="service-assignment">
                      <label>👥 Personal:</label>
                      <div className="assignment-list">
                        {service.assignedStaff && service.assignedStaff.length > 0 ? (
                          service.assignedStaff.map(staffId => {
                            const staffMember = staff.find(s => s.id === staffId);
                            return staffMember ? (
                              <span key={staffId} className="assignment-tag">
                                {staffMember.name}
                              </span>
                            ) : null;
                          })
                        ) : (
                          <span className="no-assignment">Cualquier personal</span>
                        )}
                      </div>
                    </div>
                    
                    {/* La fila de recursos existe sólo si el negocio tiene alguno.
                        Un negocio que no usa recursos —la mayoría— veía "📍
                        Recursos: Cualquier recurso" en cada uno de sus servicios:
                        una fila que nombra algo que no existe en su cuenta y que
                        se lee como un dato pendiente de completar.

                        Teniéndolos, "Cualquier recurso" sí dice algo: que este
                        servicio no está atado a ninguno en particular. */}
                    {resources.length > 0 && (
                      <div className="service-assignment">
                        <label>📍 Recursos:</label>
                        <div className="assignment-list">
                          {service.assignedResources && service.assignedResources.length > 0 ? (
                            service.assignedResources.map(resourceId => {
                              const resource = resources.find(r => r.id === resourceId);
                              return resource ? (
                                <span key={resourceId} className="assignment-tag">
                                  {resource.name}
                                </span>
                              ) : null;
                            })
                          ) : (
                            <span className="no-assignment">Cualquier recurso</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="service-card-buttons">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openEditServiceCard(service)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleDeleteService(service.id)}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
                );
              })}

              {/* La casilla para agregar es una tarjeta más de la grilla, no un
                  botón en el encabezado: ocupa el mismo lugar que ocuparía el
                  servicio nuevo, así que se entiende sola y no cambia el ancho
                  de las tarjetas que ya están. Es un <button> de verdad —antes
                  era un <div onClick>— para que se llegue con el tabulador y
                  responda al Enter. Sus estilos existían sólo en Onboarding.css
                  scopeados a .services-grid-onboarding, y por eso acá se veía
                  como una tarjeta vacía con un "+" suelto: ahora están en
                  Dashboard.css. */}
              <button
                type="button"
                className="service-card add-service-card"
                onClick={openEditableServiceCard}
              >
                <span className="add-icon" aria-hidden="true">+</span>
                <span>Agregar Servicio</span>
              </button>
            </div>
          </section>
        )}

        {/* Resources Section - Personal y Recursos */}
        {activeSection === "resources" && canAccessStaffAndResources() && (
          <section className="dashboard-section" style={{ position: 'relative' }}>
            {/* La barra queda pegada arriba mientras se scrollea: la lista de
                personal es larga y el botón al final obliga a bajar hasta el
                fondo para guardar algo que cambiaste arriba de todo. Pegada, el
                estado —"sin guardar" o "todo guardado"— está a la vista todo el
                tiempo, que es la mitad del punto.

                Misma forma que la cabecera de Horarios: guardar y descartar
                aparecen sólo cuando hay algo que guardar, y si no hay nada dice
                "Todo guardado". Un botón que está siempre no distingue entre lo
                ya guardado y lo que falta. */}
            {/* Sin barra de guardar acá. Todo lo que se hace en esta pantalla
                se guarda solo en el momento —agregar, editar, prender, apagar,
                borrar personal y borrar un rol—, así que un botón de guardar
                para la sección entera obligaba a guardar DOS veces: una adentro
                del detalle de la persona y otra afuera. Si alguna vez vuelve a
                haber un cambio que quede en memoria, la barra vuelve; mientras
                tanto, mentía. */}
            <div className="section-header">
              <h2>Personal y Recursos</h2>
              <p>Gestiona tu equipo de trabajo y los espacios/recursos de tu negocio</p>
            </div>


            <div className="equipo-columnas">

              {/* Personal Section */}
              <div>
                <div className="equipo-titulo-fila">
                  <h3 className="equipo-titulo">
                    👥 Personal ({staff.filter(s => s.active).length}/{staff.length})
                  </h3>
                  <Button
                    size="sm"
                    className="shrink-0"
                    onClick={() => setShowNewStaffForm(true)}
                  >
                    Agregar Personal
                  </Button>
                </div>


                <BuscadorDeLista
                  texto={buscarPersonal}
                  onTexto={setBuscarPersonal}
                  orden={ordenPersonal}
                  onOrden={setOrdenPersonal}
                  etiquetaDelOrden="Por rol"
                  total={staff.length}
                  visibles={personalVisible.length}
                  placeholder="Buscar por nombre, rol, mail o teléfono"
                />

                <div className="equipo-lista">
                  {/* `AnimatePresence` es lo que permite animar la SALIDA: sin
                      él React desmonta la fila al instante y no hay nada que
                      desvanecer. Ver components/business/FilaAnimada.jsx. */}
                  <AnimatePresence initial={false}>
                  {personalVisible.map(person => (
                    <FilaAnimada key={person.id} className={`service-card ${!person.active ? 'inactive' : ''}`}>
                      <div className="service-header">
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {person.foto && (
                              <img src={person.foto} alt="" className="foto-de-persona" />
                            )}
                            {person.name}
                            {/* Un chip por rol: con dos, el texto junto se lee
                                como un puesto largo en vez de como dos cosas. */}
                            {rolesDe(person).map((rol) => (
                              <span key={rol} className="etiqueta-de-rol">
                                {rol}
                              </span>
                            ))}
                          </h4>
                          {/* Con el punto suelto: si no hay mail ni teléfono
                              —los dos son opcionales— quedaba un "•" colgando
                              debajo del nombre. */}
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                            {[person.email, person.phone].filter(Boolean).join(' • ')}
                          </div>
                        </div>
                        <Switch
                          checked={person.active}
                          aria-label={`Activar a ${person.name}`}
                          onChange={(e) => {
                            setStaff(prev => prev.map(s =>
                              s.id === person.id ? { ...s, active: e.target.checked } : s
                            ));
                            saveStaffChanges();
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', display: 'block', marginBottom: '0.5rem' }}>
                          Horarios de trabajo
                        </label>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', lineHeight: 1.4 }}>
                          {person.schedule ? Object.entries(person.schedule).map(([day, daySchedule]) => {
                            if (!daySchedule.enabled || daySchedule.ranges.length === 0) return null;
                            const dayName = dayNames[day];
                            const ranges = daySchedule.ranges.map(r => `${r.start}-${r.end}`).join(', ');
                            return (
                              <div key={day}>
                                <strong>{dayName}:</strong> {ranges}
                              </div>
                            );
                          }).filter(Boolean) : null}
                          {(!person.schedule || Object.values(person.schedule).every(daySchedule => !daySchedule.enabled)) && (
                            <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Sin horarios definidos</span>
                          )}
                        </div>
                      </div>

                      <div className="service-card-buttons">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => setEditingStaff(person)}
                        >
                          Editar Horarios
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            if (window.confirm(`¿Eliminar a ${person.name} del personal?`)) {
                              setStaff(prev => prev.filter(s => s.id !== person.id));
                              saveStaffChanges();
                            }
                          }}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </FilaAnimada>
                  ))}
                  </AnimatePresence>
                </div>
              </div>

              {/* Resources Section */}
              <div>
                <div className="equipo-titulo-fila">
                  <h3 className="equipo-titulo">
                    📍 Recursos ({resources.filter(r => r.active).length}/{resources.length})
                  </h3>
                  <Button
                    size="sm"
                    className="shrink-0"
                    onClick={() => setShowNewResourceForm(true)}
                  >
                    Agregar Recurso
                  </Button>
                </div>

                <BuscadorDeLista
                  texto={buscarRecurso}
                  onTexto={setBuscarRecurso}
                  orden={ordenRecurso}
                  onOrden={setOrdenRecurso}
                  etiquetaDelOrden="Por tipo"
                  total={resources.length}
                  visibles={recursosVisibles.length}
                  placeholder="Buscar por nombre o tipo"
                />

                <div className="equipo-lista">
                  <AnimatePresence initial={false}>
                  {recursosVisibles.map(resource => (
                    <FilaAnimada key={resource.id} className={`service-card ${!resource.active ? 'inactive' : ''}`}>
                      <div className="service-header">
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600' }}>
                            {resource.name}
                          </h4>
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                            {resource.description}
                          </div>
                        </div>
                        <Switch
                          checked={resource.active}
                          aria-label={`Activar ${resource.name}`}
                          onChange={(e) => {
                            setResources(prev => prev.map(r =>
                              r.id === resource.id ? { ...r, active: e.target.checked } : r
                            ));
                            saveResourceChanges();
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', display: 'block', marginBottom: '0.5rem' }}>
                          Horarios de disponibilidad
                        </label>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', lineHeight: 1.4 }}>
                          {resource.schedule ? Object.entries(resource.schedule).map(([day, daySchedule]) => {
                            if (!daySchedule.enabled || daySchedule.ranges.length === 0) return null;
                            const dayName = dayNames[day];
                            const ranges = daySchedule.ranges.map(r => `${r.start}-${r.end}`).join(', ');
                            return (
                              <div key={day}>
                                <strong>{dayName}:</strong> {ranges}
                              </div>
                            );
                          }).filter(Boolean) : null}
                          {(!resource.schedule || Object.values(resource.schedule).every(daySchedule => !daySchedule.enabled)) && (
                            <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Sin horarios definidos</span>
                          )}
                        </div>
                      </div>

                      <div className="service-card-buttons">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => setEditingResource(resource)}
                        >
                          Editar Horarios
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            if (window.confirm(`¿Eliminar el recurso ${resource.name}?`)) {
                              setResources(prev => prev.filter(r => r.id !== resource.id));
                              saveResourceChanges();
                            }
                          }}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </FilaAnimada>
                  ))}
                  </AnimatePresence>
                </div>
              </div>

            </div>
          </section>
        )}

        {/* Stats Section - Estadísticas funcionales */}
        {activeSection === "pacientes" && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Seguimiento de pacientes</h2>
            </div>
            <SeccionPacientes currentUser={currentUser} />
          </section>
        )}

        {activeSection === "stats" && (
          <section className="dashboard-section">
            {/* Todo el dibujo vive en components/EstadisticasPanel.jsx. Acá
                estaban las 264 líneas escritas a mano, con seis tarjetas de
                emojis y un gráfico hecho de <div> con altura en porcentaje.
                Los números se siguen calculando acá —esas funciones no se
                tocaron— y llegan al componente ya resueltos: el panel no
                consulta nada, sólo dibuja. */}
            <EstadisticasPanel
              basicas={calculateBasicStats()}
              rendimiento={calculatePerformanceStats()}
              operativas={calculateOperationalStats()}
              serieDeIngresos={getRevenueChartData()}
              distribucionDeServicios={getServicesDistribution()}
              formatearMoneda={formatCurrency}
            />
          </section>
        )}

        {/* Settings Section */}
        {activeSection === "settings" && (
          <section className="dashboard-section">
            <div className="section-header">
              <div>
                <h2>Configuración del Negocio</h2>
                <p>Gestiona la información básica de tu negocio</p>
              </div>
              {/* Con `onClick={handleSaveChanges}` el primer argumento sería el
                  evento del click, que se destructuraría como opciones. Anda de
                  casualidad —un evento no tiene `avisar`— pero se rompe solo el
                  día que la función use otra opción. */}
              <Button size="sm" className="shrink-0" onClick={() => handleSaveChanges()}>
                <FaSave className="mr-2" />
                Guardar cambios
              </Button>
            </div>

            <div className="settings-form">
              {/* Lista de configuración */}
              <div className="settings-list">
                
                {/* Información básica */}
                <PasoPlegable
                  icono="📋"
                  titulo="Información básica"
                  descripcion="Nombre, dirección, teléfono y URL de tu negocio"
                  abierto={seccionAbierta === 'basicInfo'}
                  onToggle={() => toggleSettingsSection('basicInfo')}
                >
                  <div className="content-wrapper">
                      <BusinessInfoForm
                        value={businessInfo}
                        onChange={(newValue) => {
                          console.log('🐛 [DEBUG] Dashboard setBusinessInfo called with:', newValue);
                          setBusinessInfo(newValue);
                        }}
                        // El rubro decide los textos de ejemplo del formulario.
                        // No se pasaba, así que caía siempre en los de 'other' y
                        // un consultorio veía "Ej: Mi Negocio" en vez del
                        // ejemplo de su rubro.
                        businessType={businessInfo?.businessType || 'other'}
                        isExistingBusiness={businessDataLoaded && businessInfo?.slug && businessInfo.slug !== 'mi-negocio'}
                      />
                  </div>
                </PasoPlegable>

                {/* Rubro del negocio */}
                <PasoPlegable
                  icono={rubroActual?.icon || '🏷️'}
                  titulo="Rubro del negocio"
                  descripcion={
                    rubroActual
                      ? `${rubroActual.name} — es la etiqueta que ven tus clientes`
                      : 'Elegí a qué se dedica tu negocio'
                  }
                  abierto={seccionAbierta === 'rubro'}
                  onToggle={() => toggleSettingsSection('rubro')}
                >
                  <div className="content-wrapper">
                    <SelectorDeRubro
                      valor={businessInfo?.businessType || ''}
                      onChange={(id) => setBusinessInfo(prev => ({ ...prev, businessType: id }))}
                    />
                  </div>
                </PasoPlegable>

                {/* Plan y Facturación */}
                <PasoPlegable
                  icono="💳"
                  titulo="Plan y Facturación"
                  descripcion="Gestiona tu suscripción y métodos de pago"
                  abierto={seccionAbierta === 'billing'}
                  onToggle={() => toggleSettingsSection('billing')}
                >
                  <div className="content-wrapper">
                      <CurrentPlan 
                        planName={subscriptionData?.planName || "Trial Gratuito"}
                        planType={subscriptionData?.planType || 'trial'}
                        billingCycle={subscriptionData?.billingCycle || 'monthly'}
                        price={subscriptionData?.price || 0}
                        currency="ARS"
                        features={subscriptionData?.features || [
                          { name: "Gestión de personal", included: false },
                          { name: "Gestión de servicios", included: false },
                          { name: "Gestión de recursos", included: false },
                          { name: "Gestión de turnos", included: false },
                          { name: "Analíticas avanzadas", included: false },
                          { name: "Soporte prioritario", included: false }
                        ]}
                        nextBillingDate={subscriptionData?.nextBillingDate || null}
                        freeTrialEndDate={subscriptionData?.freeTrialEndDate || null}
                        planStatus={subscriptionData?.planStatus || 'inactive'}
                        subscriptionId={subscriptionData?.subscriptionId || null}
                        onUpgrade={handleChangePlan}
                        onManage={handleCancelPlan}
                      />
                  </div>
                </PasoPlegable>

                {/* Obras sociales.
                    Estaba condicionada a `businessType === 'health'`, y eso la
                    volvía inalcanzable: el rubro se elige una sola vez en el
                    onboarding y el panel no tiene dónde cambiarlo, así que un
                    negocio sin ese campo cargado —o cargado con otro rubro— no
                    tenía forma de llegar acá. Y como el formulario de la reserva
                    también miraba el rubro, la función entera quedaba apagada sin
                    que nada lo dijera.
                    Ahora la sección está siempre y el interruptor es uno solo y
                    visible: si tildás coberturas, la reserva las pide; si no
                    tildás ninguna, no pregunta nada. Una peluquería simplemente
                    la deja vacía y no se entera. */}
                {(
                  <PasoPlegable
                    icono="🏥"
                    titulo="Obras sociales"
                    descripcion="Con qué coberturas atendés"
                    resumen={
                      obrasSocialesDelNegocio.length
                        ? `${obrasSocialesDelNegocio.length} ${obrasSocialesDelNegocio.length === 1 ? 'cobertura' : 'coberturas'}`
                        : 'Ninguna cargada'
                    }
                    abierto={seccionAbierta === 'obras'}
                    onToggle={() => toggleSettingsSection('obras')}
                  >
                    <div className="content-wrapper">
                      <p className="section-description">
                        Tildá las que atendés. Tus pacientes van a ver sólo estas al
                        sacar un turno, más la opción "Particular". Si no cargás
                        ninguna, el paso no aparece y nadie te pregunta por la
                        cobertura.
                      </p>

                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {[...TODAS_LAS_OBRAS, ...obrasPropias].map((obra) => {
                          const elegida = obrasSocialesDelNegocio.includes(obra.id);
                          return (
                            <label
                              key={obra.id}
                              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-sm ${
                                elegida
                                  ? 'border-primary bg-primary/5 font-semibold text-neutral-900'
                                  : 'border-neutral-200 text-neutral-700 hover:border-neutral-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={elegida}
                                onChange={() =>
                                  setObrasSocialesDelNegocio((prev) =>
                                    prev.includes(obra.id)
                                      ? prev.filter((id) => id !== obra.id)
                                      : [...prev, obra.id]
                                  )
                                }
                              />
                              {obra.nombre}
                            </label>
                          );
                        })}
                      </div>

                      {/* Alta de una cobertura que no está en la lista.
                          Hay cientos de obras sociales sindicales y regionales, y
                          hasta acá un consultorio que atendía una que no estaba
                          no tenía manera de ofrecerla: la lista era cerrada. El
                          id se arma del nombre, así que sigue valiendo lo que
                          hace útil a la lista maestra —dos formas de escribir lo
                          mismo caen en el mismo id— y el logo se puede enganchar
                          igual el día que exista. */}
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        <input
                          type="text"
                          value={nuevaObra}
                          onChange={(e) => setNuevaObra(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              agregarObraSocial();
                            }
                          }}
                          placeholder="Otra cobertura (ej: OSPRERA)"
                          maxLength={60}
                          className="min-w-0 flex-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0"
                          onClick={agregarObraSocial}
                          disabled={!nuevaObra.trim()}
                        >
                          Agregar cobertura
                        </Button>
                      </div>

                      {/* El <Button> del sistema, igual que el de Horarios.
                          Era un `<button className="btn-primary">`, una clase
                          suelta de Onboarding.css: quedaba de otro alto, otro
                          radio y otro color que el resto de los botones del
                          panel, al lado de los que sí salen del sistema. */}
                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <Button
                          size="sm"
                          className="shrink-0"
                          disabled={guardandoObras}
                          onClick={async () => {
                            setGuardandoObras(true);
                            try {
                              const token = await currentUser.getIdToken();
                              const res = await fetch(`${API_URL}/api/business/obras-sociales`, {
                                method: 'PUT',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                  obrasSociales: obrasSocialesDelNegocio,
                                  // Sólo las que están tildadas: una que se agregó y
                                  // después se destildó no tiene por qué quedar
                                  // guardada para siempre.
                                  obrasSocialesPropias: obrasPropias.filter((o) =>
                                    obrasSocialesDelNegocio.includes(o.id)
                                  ),
                                  /* Los nombres resueltos, para el asistente de
                                     WhatsApp. El documento guarda ids y la lista
                                     maestra vive acá en el frontend a propósito, así
                                     que este guardado es el único momento en que las
                                     dos cosas están juntas. Sin esto el bot no puede
                                     nombrar las coberturas y calla en vez de
                                     contestar. */
                                  obrasSocialesNombres: obrasSocialesDelNegocio.map((id) => ({
                                    id,
                                    nombre: nombreDeObraSocial(id, obrasPropias)
                                  }))
                                })
                              });
                              if (!res.ok) throw new Error('No se pudieron guardar');
                              toast.success('Coberturas guardadas');
                            } catch (e) {
                              toast.error('No pudimos guardar las coberturas', {
                                description: 'Probá de nuevo en un momento.'
                              });
                            } finally {
                              setGuardandoObras(false);
                            }
                          }}
                        >
                          <FaSave className="mr-2" />
                          {guardandoObras ? 'Guardando...' : 'Guardar coberturas'}
                        </Button>
                        {obrasSocialesDelNegocio.length === 0 && (
                          <span className="text-sm text-neutral-500">
                            Sin ninguna tildada, no se le pregunta la cobertura a nadie.
                          </span>
                        )}
                      </div>
                    </div>
                  </PasoPlegable>
                )}

                {/* Personalización de Marca */}
                <PasoPlegable
                  icono="🎨"
                  titulo="Personalización de Marca"
                  descripcion="Imagen y colores de tu página de reservas"
                  abierto={seccionAbierta === 'branding'}
                  onToggle={() => toggleSettingsSection('branding')}
                >
                  <div className="content-wrapper">
                      <p className="section-description">
                        Elegí la imagen y los colores de tu página de reservas. Las dos cosas se cambian
                        en la vista previa, mirando la página de verdad mientras las tocás.
                      </p>

                      <div className="brand-simple-container">
                        {/* Lo que hay hoy, para que se entienda qué se va a
                            cambiar antes de abrir el modal. La imagen va primero
                            porque es lo que más se nota en la página. */}
                        <div className="brand-current-color">
                          <span className="brand-color-label">Imagen actual:</span>
                          {logoDelNegocio ? (
                            <img
                              src={logoDelNegocio}
                              alt="Imagen de tu negocio"
                              className="brand-current-logo"
                            />
                          ) : (
                            <span className="brand-color-value">Sin imagen</span>
                          )}
                        </div>

                        <div className="brand-current-color">
                          <span className="brand-color-label">Color actual:</span>
                          {/* Sólo el círculo. El hexadecimal que iba al lado no
                              le dice nada a nadie: el color se reconoce viéndolo,
                              y "#FF5722" es un dato que no se usa para nada acá
                              —no se copia, no se pega, no se edita—. Donde sí
                              aparece escrito es en el selector de la vista previa,
                              que es donde se está eligiendo. */}
                          <div
                            className="brand-color-circle"
                            /* Queda en el title por si alguna vez hace falta —una
                               consulta de soporte, copiarlo a otra herramienta—,
                               pero sin ocupar lugar en la pantalla. */
                            title={businessBranding?.primaryColor || '#FF5722'}
                            style={{backgroundColor: businessBranding?.primaryColor || '#FF5722'}}
                          ></div>
                          {/* Un círculo de color no se puede leer en voz alta.
                              Esto es lo que escucha quien no ve la pantalla. */}
                          <span className="sr-only">
                            {businessBranding?.primaryColor || '#FF5722'}
                          </span>
                        </div>

                        <button
                          className="btn-brand-customize"
                          onClick={() => setShowBrandPreview(true)}
                        >
                          <span className="btn-icon">🎨</span>
                          Vista Previa Interactiva
                          <span className="btn-arrow">→</span>
                        </button>
                      </div>
                  </div>
                </PasoPlegable>

                {/* Datos Bancarios */}
                <PasoPlegable
                  icono="🏦"
                  titulo="Datos Bancarios"
                  descripcion="CBU, alias y datos para transferencias"
                  abierto={seccionAbierta === 'banking'}
                  onToggle={() => toggleSettingsSection('banking')}
                >
                  <div className="content-wrapper">
                      <p className="section-description">
                        Configura tus datos bancarios para recibir pagos de señas por transferencia. Esta información se mostrará a los clientes cuando elijan pagar por transferencia bancaria.
                      </p>
                      
                      <div className="banking-form">
                        <div className="form-row">
                          <div className="form-group">
                            <label>CBU (Clave Bancaria Uniforme)</label>
                            <input
                              type="text"
                              value={bankingData.cbu}
                              onChange={(e) => setBankingData(prev => ({...prev, cbu: e.target.value}))}
                              placeholder="0000003100010000000001"
                              maxLength="22"
                            />
                            <small className="form-help">22 dígitos que identifican tu cuenta bancaria</small>
                          </div>
                          
                          <div className="form-group">
                            <label>Alias</label>
                            <input
                              type="text"
                              value={bankingData.alias}
                              onChange={(e) => setBankingData(prev => ({...prev, alias: e.target.value}))}
                              placeholder="mi.negocio.aturno"
                            />
                            <small className="form-help">Alias de tu cuenta para transferencias fáciles</small>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Titular de la cuenta</label>
                            <input
                              type="text"
                              value={bankingData.accountHolder}
                              onChange={(e) => setBankingData(prev => ({...prev, accountHolder: e.target.value}))}
                              placeholder="Juan Pérez / Peluquería Estilo S.R.L."
                            />
                            <small className="form-help">Nombre completo del titular o razón social</small>
                          </div>
                          
                          <div className="form-group">
                            <label>Banco</label>
                            <input
                              type="text"
                              value={bankingData.bankName}
                              onChange={(e) => setBankingData(prev => ({...prev, bankName: e.target.value}))}
                              placeholder="Banco Nación"
                            />
                            <small className="form-help">Nombre de tu banco</small>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Tipo de cuenta</label>
                            <select
                              value={bankingData.accountType}
                              onChange={(e) => setBankingData(prev => ({...prev, accountType: e.target.value}))}
                            >
                              <option value="corriente">Cuenta Corriente</option>
                              <option value="ahorro">Caja de Ahorro</option>
                              <option value="cuenta-dni">Cuenta DNI</option>
                            </select>
                          </div>
                        </div>

                        <div className="banking-actions">
                          <button 
                            className="btn-banking-save"
                            onClick={saveBankingData}
                            disabled={savingBankingData}
                          >
                            {savingBankingData ? (
                              <>🔄 Guardando...</>
                            ) : (
                              <>💾 Guardar Datos Bancarios</>
                            )}
                          </button>
                          
                          {bankingData.cbu && (
                            <div className="banking-preview">
                              <h5>Vista previa para clientes:</h5>
                              <div className="banking-preview-card">
                                <div className="banking-detail">
                                  <strong>CBU:</strong> {bankingData.cbu}
                                </div>
                                <div className="banking-detail">
                                  <strong>Alias:</strong> {bankingData.alias || 'No configurado'}
                                </div>
                                <div className="banking-detail">
                                  <strong>Titular:</strong> {bankingData.accountHolder || 'No configurado'}
                                </div>
                                <div className="banking-detail">
                                  <strong>Banco:</strong> {bankingData.bankName || 'No especificado'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                  </div>
                </PasoPlegable>




                {/* Zona de riesgo. Va al final y separada del resto a propósito:
                    es la única acción del panel que no se puede deshacer. */}
                <div className="zona-riesgo">
                  <h3 className="zona-riesgo-titulo">Borrar mi cuenta</h3>
                  <p className="zona-riesgo-texto">
                    Se borran tu negocio, tus servicios, tu personal, tus recursos y todos tus
                    turnos. Tu página pública deja de funcionar. Si tenés una suscripción activa,
                    la cancelamos antes de borrar. <strong>No se puede deshacer.</strong>
                  </p>
                  <button
                    type="button"
                    className="btn-borrar-cuenta"
                    onClick={() => {
                      setConfirmacionBorrado('');
                      setMostrarBorrarCuenta(true);
                    }}
                    disabled={isDemo}
                    title={isDemo ? 'No disponible en la demo' : undefined}
                  >
                    Borrar mi cuenta
                  </button>
                </div>

              </div>
            </div>
          </section>
        )}



        {/* Integrations Section */}
        {activeSection === "integrations" && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Centro de Integraciones</h2>
              <p>Conecta Aturno con tus herramientas favoritas y automatiza tu negocio</p>
              

            </div>

            {/* Grid principal de integraciones */}
            <div className="integrations-main-grid">
              {availableIntegrations.map(integration => {
                const integrationData = integrations[integration.key];
                const isConnected = integrationData?.connected || false;
                const isConnecting = connectingIntegration === integration.key;
                
                return (
                  <div key={integration.key} className={`integration-card ${isConnected ? 'connected' : 'disconnected'}`}>
                    {/* Header de la card */}
                    <div className="integration-card-header">
                      <div className="integration-info">
                                                                        <div className="integration-icon-wrapper" style={{ backgroundColor: '#ffffff' }}>
                          {integration.icon === "googleLogo" ? 
                            <GoogleLogo isConnected={isConnected} /> : 
                            integration.icon
                          }
                        </div>
                        <div className="integration-details">
                          <h4>{integration.name}</h4>
                          <span className="integration-category">{integration.category}</span>
                        </div>
                      </div>
                      <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
                        {isConnected ? (
                          <span className="status-badge connected">
                            <span className="status-dot"></span>
                            Conectado
                          </span>
                        ) : (
                          <span className="status-badge disconnected">
                            <span className="status-dot"></span>
                            Desconectado
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Descripción */}
                    <p className="integration-description">
                      {integration.description}
                    </p>

                    {/* Beneficios */}
                    <div className="integration-benefits">
                      <h5>Beneficios:</h5>
                      <ul>
                        {integration.benefits.map((benefit, index) => (
                          <li key={index}>
                            <span className="benefit-check">✓</span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Estado de conexión y acciones */}
                    <div className="integration-actions">
                      {isConnected ? (
                        <>
                          <div className="connection-info">
                            <span className="connection-detail">
                              🟢 Conectado desde hace 2 días
                            </span>
                          </div>
                          <div className="action-buttons">
                            <button 
                              className="btn-integration-secondary"
                              onClick={() => {
                                if (integration.key === 'googleCalendar') {
                                  window.open('https://calendar.google.com', '_blank');
                                } else if (integration.key === 'mercadoPago') {
                                  window.open('https://www.mercadopago.com.ar/home', '_blank');
                                } else {
                                  alert(`Configurando ${integration.name}...`);
                                }
                              }}
                            >
                              ⚙️ Configurar
                            </button>
                            <small className="integration-manage-note">
                              Para desconectar, administra desde {integration.key === 'googleCalendar' ? 'Google' : integration.key === 'mercadoPago' ? 'MercadoPago' : 'la plataforma correspondiente'}
                            </small>
                          </div>
                        </>
                      ) : (
                        <div className="connection-actions">
                          {isConnecting ? (
                            <button className="btn-integration-primary connecting" disabled>
                              <span className="connecting-icon">🔄</span>
                              Conectando...
                            </button>
                          ) : (
                            <button 
                              className="btn-integration-primary"
                              onClick={() => handleConnect(integration.key)}
                              style={{ backgroundColor: integration.color }}
                            >
                              <span className="connect-icon">🔗</span>
                              Conectar con {integration.name}
                            </button>
                          )}
                          <small className="connection-note">
                            Gratis en tu plan actual
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Próximamente.
                Eran ocho tarjetas grandes con emoji, unos 520px de alto para
                enumerar cosas que todavía no se pueden usar. Como fila de
                etiquetas dice exactamente lo mismo en una línea. */}
            <div className="integrations-coming-soon">
              <h3>Próximamente</h3>
              <ul className="coming-soon-chips">
                {['WhatsApp Business', 'Zoom', 'Mailchimp', 'Instagram Business', 'Stripe', 'Google Analytics', 'Notion', 'Zapier'].map((nombre) => (
                  <li key={nombre}>{nombre}</li>
                ))}
              </ul>
              <p className="coming-soon-note">
                ¿Necesitás una integración específica? <a href="mailto:soporte@aturno.com">Escribinos</a> y la evaluamos.
              </p>
            </div>
          </section>
        )}

        {/* Help Section */}
        {/* WhatsApp Assistant Section */}
        {activeSection === "whatsapp" && (
          <section className="dashboard-section">
            {/* Dos pestañas: las conversaciones se miran varias veces por día,
                el estado del asistente una vez y cuando algo falla. Estaban
                una debajo de la otra y para llegar al estado había que pasar
                por encima de toda la lista y del hilo abierto. */}
            <SeccionWhatsApp />
          </section>
        )}

        {activeSection === "help" && (
          <section className="dashboard-section">
            <div className="section-header">
              <h2>Centro de Ayuda</h2>
              <p>Encuentra respuestas rápidas a tus preguntas más frecuentes</p>
            </div>

            {/* Contacto de soporte */}
            <div className="help-section">
              <h3>📞 Soporte Técnico</h3>
              <div className="support-options">
                <div className="support-card">
                  <div className="support-icon">📧</div>
                  <div className="support-info">
                    <h4>Email</h4>
                    <p>matiascalo@aturno.app</p>
                    <small>Respuesta en 24 horas</small>
                  </div>
                </div>
                <div className="support-card">
                  <div className="support-icon">💬</div>
                  <div className="support-info">
                    <h4>WhatsApp</h4>
                    <p>+54 9 11 3003-2002</p>
                    <small>Lun a Vie 9:00 - 18:00</small>
                  </div>
                </div>
                <div className="support-card">
                  <div className="support-icon">📚</div>
                  <div className="support-info">
                    <h4>Documentación</h4>
                    <p>Próximamente</p>
                    <small>En desarrollo</small>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="help-section">
              <h3>❓ Preguntas Frecuentes</h3>
              <div className="faq-container">
                {faqData.map((faq, index) => (
                  <div key={index} className={`faq-item ${expandedFAQ === index ? 'expanded' : ''}`}>
                    <button 
                      className="faq-question"
                      onClick={() => toggleFAQ(index)}
                    >
                      <span>{faq.question}</span>
                      <svg 
                        className={`faq-icon ${expandedFAQ === index ? 'rotated' : ''}`}
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                      >
                        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                      </svg>
                    </button>
                    {/* Se abre con una animación de alto, igual que los pasos
                        plegables de servicios y de configuración.

                        Antes esto era `max-height: 0 → 200px` en CSS, y no
                        animaba: la regla `.dashboard-container *` del final de
                        Dashboard.css fija con !important qué propiedades
                        transicionan, y max-height no está en esa lista. La
                        respuesta aparecía de golpe. Framer escribe el estilo
                        cuadro a cuadro, así que no depende de esa regla.

                        Y con `height: auto` se acabó el recorte: los 200px
                        fijos cortaban cualquier respuesta más larga. */}
                    <AnimatePresence initial={false}>
                      {expandedFAQ === index && (
                        <motion.div
                          className="faq-answer"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                          style={{ overflow: 'hidden', maxHeight: 'none' }}
                        >
                          <p>{faq.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>

            {/* Disclaimer para funciones próximamente */}
            <div className="coming-soon-disclaimer">
              <h3>🚀 Próximamente</h3>
              <p>Las siguientes funciones estarán disponibles en futuras actualizaciones:</p>
            </div>

          </section>
        )}
      </main>

      {/* Modal para agregar nuevo personal */}
      {/* Salir de Horarios con cambios sin guardar.
          Las tres salidas están escritas como lo que hacen y no como "sí/no":
          leyendo el botón solo se sabe qué va a pasar con lo que se cambió.
          "Seguir editando" va primero y es la que corre al tocar afuera o
          Escape, porque es la única que no decide nada. */}
      {salidaPendiente && (
        <div
          className="modal-overlay"
          onClick={() => !guardandoHorarios && seguirEditando()}
        >
          <div className="modal-content modal-horarios" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Tenés cambios sin guardar</h3>
            </div>

            {/* El texto nombra lo que la persona cambió. Decir siempre
                "cambiaste los horarios" cuando lo que tocó fue el personal es
                peor que no avisar: la manda a buscar un cambio que no hizo, y
                le enseña que el cartel no describe la realidad. */}
            <div className="modal-horarios-cuerpo">
              {hayCambiosEnHorarios && (
                <p>
                  Cambiaste los horarios de atención y todavía no los guardaste. Si salís ahora, tu
                  negocio sigue atendiendo con los horarios anteriores.
                </p>
              )}
              {hayCambiosEnConfiguracion && (
                <p>
                  Cambiaste {resumenDeCambiosEnConfiguracion} y todavía no los guardaste. Si salís
                  ahora, tus clientes siguen viendo lo anterior en tu página.
                </p>
              )}
            </div>

            {/* Los tres son el <Button> del sistema, y esa es la razón de que
                se vean como un juego: dos estaban escritos a mano con su propio
                padding y su propio tamaño de letra, y quedaban de otra altura
                que el de guardar, que sí venía del sistema.

                La jerarquía la dan las variantes, no el tamaño, y son las que
                ya tenía escritas a mano: `secondary` es el mismo gris de relleno
                que usaba "Seguir editando" y `outline` el mismo contorno sin
                relleno de "Salir sin guardar". Ninguna es `ghost`: las tres son
                salidas de un modal que pide una decisión, y una sin borde queda
                como texto suelto hasta que le pasás el mouse. */}
            <div className="modal-horarios-acciones">
              <Button
                variant="secondary"
                onClick={seguirEditando}
                disabled={guardandoHorarios}
              >
                Seguir editando
              </Button>
              <Button
                variant="outline"
                onClick={salirSinGuardar}
                disabled={guardandoHorarios}
              >
                Salir sin guardar
              </Button>
              {/* El mismo <Button> que el de la cabecera: los dos guardan lo
                  mismo, así que tienen que verse igual. Escrito a mano tomaba el
                  color de la marca del negocio y el de la cabecera el del
                  sistema, y en un negocio con marca propia quedaban de dos
                  colores distintos al lado del mismo horario. */}
              <Button onClick={guardarYSalir} disabled={guardandoHorarios}>
                {guardandoHorarios ? 'Guardando...' : 'Guardar y salir'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación de borrado de cuenta.
          Pide escribir el nombre del negocio: es la forma estándar de que una
          acción irreversible no se dispare por un click distraído. */}
      {mostrarBorrarCuenta && (
        <div
          className="modal-overlay"
          onClick={() => !borrandoCuenta && setMostrarBorrarCuenta(false)}
        >
          <div className="modal-content modal-borrar-cuenta" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Borrar mi cuenta</h3>
              <button
                className="modal-close"
                onClick={() => setMostrarBorrarCuenta(false)}
                disabled={borrandoCuenta}
              >
                ×
              </button>
            </div>

            <div className="borrar-cuenta-cuerpo">
              <p>Esto borra de forma permanente:</p>
              <ul>
                <li>tu negocio y su página pública</li>
                <li>tus servicios, tu personal y tus recursos</li>
                <li>todos tus turnos, pasados y futuros</li>
              </ul>
              <p className="borrar-cuenta-aviso">
                Los turnos que tus clientes ya tienen reservados se borran también, y no les vamos a
                avisar automáticamente. Si tenés turnos próximos, avisales antes.
              </p>

              <label className="borrar-cuenta-label" htmlFor="confirmar-borrado">
                Para confirmar, escribí <strong>{nombreDelNegocio}</strong>
              </label>
              <input
                id="confirmar-borrado"
                type="text"
                value={confirmacionBorrado}
                onChange={(e) => setConfirmacionBorrado(e.target.value)}
                placeholder={nombreDelNegocio}
                autoComplete="off"
                disabled={borrandoCuenta}
              />
            </div>

            <div className="borrar-cuenta-acciones">
              <button
                type="button"
                className="btn-cancelar-borrado"
                onClick={() => setMostrarBorrarCuenta(false)}
                disabled={borrandoCuenta}
              >
                No, volver
              </button>
              <button
                type="button"
                className="btn-borrar-cuenta"
                onClick={borrarCuenta}
                disabled={borrandoCuenta || confirmacionBorrado.trim() !== nombreDelNegocio}
              >
                {borrandoCuenta ? 'Borrando...' : 'Borrar mi cuenta para siempre'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alta de personal, con la misma forma que la del onboarding: el rol
          como campo propio, el mail y el teléfono opcionales, y el calendario
          de horarios en vez del editor de desplegables por día. */}
      {showNewStaffForm && (
        <div className="modal-overlay" onClick={pedirCerrarAlta}>
          <div className="modal-content staff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Agregar personal</h3>
              <button className="modal-close" onClick={pedirCerrarAlta}>×</button>
            </div>

            <div className="staff-form-container">
              {/* El mismo acordeón que el onboarding, y el mismo componente.
                  Acá había otra copia del formulario plano de seis campos —la
                  tercera, contando la del modal de edición—, y como pasa siempre
                  con las copias, ya se estaba separando de las otras.

                  `conEleccionDeHorario` sí va acá: una persona nueva todavía no
                  tiene horario propio, así que lo normal es que atienda cuando
                  atiende el negocio, y llenar siete días a mano para repetir lo
                  que ya está cargado es trabajo inventado. `agregarPersona`
                  copia el del negocio cuando se elige heredarlo. */}
              <FormularioDePersona
                valor={newStaff}
                onChange={setNewStaff}
                uid={currentUser?.uid}
                catalogoDeRoles={catalogoDeRoles(staff, rolesSugeridosDelRubro)}
                rolesSugeridos={rolesSugeridosDelRubro}
                onBorrarRol={borrarRolDelEquipo}
                usosDelRol={(rol) => cuantosUsanElRol(staff, rol)}
                scheduleDelNegocio={businessInfo?.schedule}
                dayNames={dayNames}
                conEleccionDeHorario
                sufijoDeId="panel-alta"
                textoGuardar={savingStaff ? 'Guardando...' : 'Agregar'}
                guardando={savingStaff}
                onGuardar={agregarPersona}
              />
            </div>

            {/* La pregunta va adentro del modal y arriba de los botones, no
                en otra ventana encima: apilar un modal para preguntar por el
                modal es donde la gente se pierde de cuál está cerrando. */}
            {descartePendiente === 'alta' && (
              <div className="modal-descartar" role="alert">
                <span>Tenés cambios sin guardar. Si cerrás, se pierden.</span>
                <div className="modal-descartar__acciones">
                  <Button size="sm" variant="ghost" onClick={() => { setDescartePendiente(null); setShowNewStaffForm(false); }}>
                    Descartar
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setDescartePendiente(null)}>
                    Seguir editando
                  </Button>
                </div>
              </div>
            )}

            {/* Sólo Cancelar: el de guardar es ahora el pie de la última parte,
                como en el onboarding. Tenerlo en los dos lados invita a apretar
                el de abajo sin haber pasado por las partes, que es justo lo que
                el recorrido trata de evitar. */}
            <div className="modal-actions">
              <Button variant="outline" onClick={pedirCerrarAlta}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar nuevo recurso */}
      {/* Alta de un espacio reservable, con la misma forma que el alta de
          personal: nombre obligatorio, descripción opcional y el calendario
          para el horario. */}
      {showNewResourceForm && (
        <div className="modal-overlay" onClick={() => setShowNewResourceForm(false)}>
          <div className="modal-content staff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Agregar recurso</h3>
              <button className="modal-close" onClick={() => setShowNewResourceForm(false)}>×</button>
            </div>

            <div className="staff-form-container">
              <div className="form-group">
                <label>
                  Nombre <span className="campo-obligatorio">*</span>
                </label>
                <input
                  type="text"
                  value={newResource.name}
                  onChange={(e) => setNewResource(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Sala 3"
                />
              </div>

              <div className="form-group">
                <label>Descripción (opcional)</label>
                <input
                  type="text"
                  value={newResource.description}
                  onChange={(e) => setNewResource(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Para qué se usa"
                />
              </div>

              <div className="staff-horario">
                <label className="staff-horario-titulo">Horario</label>
                <BusinessScheduleForm
                  value={newResource.schedule || {}}
                  onChange={(schedule) => setNewResource(prev => ({ ...prev, schedule }))}
                  dayNames={dayNames}
                />
              </div>
            </div>

            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowNewResourceForm(false)}>
                Cancelar
              </Button>
              <Button
                disabled={!newResource.name.trim()}
                onClick={() => {
                  const resourceItem = {
                    id: Date.now(),
                    name: newResource.name.trim(),
                    description: (newResource.description || '').trim(),
                    active: true,
                    // Copia propia: si se pasara la misma referencia, editar el
                    // horario de un recurso cambiaría el de todos.
                    schedule: JSON.parse(JSON.stringify(newResource.schedule || {}))
                  };
                  setResources(prev => [...prev, resourceItem]);
                  setNewResource({
                    name: '',
                    description: '',
                    schedule: {
                      monday: { enabled: false, ranges: [] },
                      tuesday: { enabled: false, ranges: [] },
                      wednesday: { enabled: false, ranges: [] },
                      thursday: { enabled: false, ranges: [] },
                      friday: { enabled: false, ranges: [] },
                      saturday: { enabled: false, ranges: [] },
                      sunday: { enabled: false, ranges: [] }
                    }
                  });
                  setShowNewResourceForm(false);
                  saveResourceChanges();
                }}>
                Agregar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar horarios del personal */}
      {/* Modal de editar personal.

          Tenía la estructura vieja: "Datos personales" con nombre, email y
          teléfono los tres obligatorios, y debajo un editor de horarios de
          siete columnas con desplegables de hora y minuto. Es la misma que
          tenía el onboarding antes de rehacerlo, y quedó acá sin actualizar.

          Ahora es igual que la del onboarding: el rol como campo propio, el
          mail y el teléfono opcionales, y el calendario de horarios. Pedir mail
          y teléfono para guardar era además una trampa: alguien cargado sin
          mail no se podía volver a editar nunca, porque el botón quedaba
          deshabilitado para siempre. */}
      {editingStaff && (
        <div className="modal-overlay" onClick={pedirCerrarEdicion}>
          {/* Sin `large-modal`. Lo tenía porque el historial es una tabla de cinco
              columnas y en los 500px del modal base "Persona" y "Día" se partían
              en dos renglones — pero eso mandaba la edición a 1200px mientras el
              alta, y el mismo modal en el onboarding, salían a 920. El mismo
              formulario con dos anchos según desde dónde lo abrís.
              `.modal-content.staff-modal` alcanza para la tabla y ahora es uno
              solo para los cuatro modales. */}
          <div className="modal-content staff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar personal</h3>
              <button className="modal-close" onClick={pedirCerrarEdicion}>×</button>
            </div>

            <div className="staff-form-container">
              {/* El mismo acordeón que el onboarding, y literalmente el mismo
                  componente. Acá había una tirada plana de seis campos —nombre,
                  foto, roles, mail, teléfono y el calendario de horarios— que
                  era una segunda copia de este formulario: el onboarding ya lo
                  había partido en cuatro partes plegables, y el panel se quedó
                  con la versión vieja. Dos copias del mismo formulario terminan
                  siendo dos formularios distintos, que es exactamente lo que
                  había pasado antes entre el alta y la edición del onboarding
                  (uno pedía sólo el nombre y el otro exigía mail y teléfono, así
                  que una persona cargada sin mail no se podía volver a editar).

                  `conEleccionDeHorario` va apagado a propósito: acá la persona
                  ya tiene su copia del horario, y ofrecerle "el mismo que el
                  negocio" sería pisársela sin avisar. El botón de guardar es el
                  del último paso, como en el onboarding; abajo queda sólo
                  Cancelar. */}
              <FormularioDePersona
                valor={editingStaff}
                onChange={setEditingStaff}
                uid={currentUser?.uid}
                catalogoDeRoles={catalogoDeRoles(staff, rolesSugeridosDelRubro)}
                rolesSugeridos={rolesSugeridosDelRubro}
                onBorrarRol={borrarRolDelEquipo}
                usosDelRol={(rol) => cuantosUsanElRol(staff, rol)}
                scheduleDelNegocio={businessInfo?.schedule}
                dayNames={dayNames}
                sufijoDeId="panel-edicion"
                textoGuardar={savingStaff ? 'Guardando...' : 'Guardar cambios'}
                guardando={savingStaff}
                onGuardar={guardarPersonaEditada}
              />

              {/* Historial de turnos.
                  Va al final y es lo único de este modal que NO se edita: todo
                  lo de arriba son campos del formulario, esto es una consulta.
                  Por eso no lleva `label` ni entra en la cuenta de "cambios sin
                  guardar".

                  Sale de los turnos que el panel ya tiene cargados —últimos 30
                  días en adelante—, no de una consulta nueva al servidor. Se
                  dice en pantalla, porque un historial que parece completo y no
                  lo es se lee como "esta persona atendió tres veces". */}
              {(() => {
                const historial = turnosDelProfesional(appointments, editingStaff);

                return (
                  <div className="staff-historial">
                    <h4 className="staff-historial__titulo">
                      Historial de turnos
                      <span className="staff-historial__conteo">
                        {historial.length === 0
                          ? 'últimos 30 días'
                          : `${historial.length} · últimos 30 días`}
                      </span>
                    </h4>

                    {historial.length === 0 ? (
                      <p className="staff-historial__vacio">
                        No tiene turnos en este período.
                      </p>
                    ) : (
                      /* `analytics-table` es la tabla de la app, la misma que usa
                         Estadísticas. Acá había una grilla escrita a mano con sus
                         propios anchos y separadores, o sea una segunda forma de
                         mostrar filas en la misma pantalla. */
                      <table className="analytics-table staff-historial__tabla">
                        <thead>
                          <tr>
                            <th>Persona</th>
                            <th>Día</th>
                            <th>Fecha</th>
                            <th>Hora</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historial.map((turno) => {
                            /* Mediodía y no medianoche: se arma la fecha en local
                               a partir de las partes, así el nombre del día no se
                               corre por la zona horaria. */
                            const dia = new Date(`${turno.date}T12:00:00`);

                            return (
                              <tr
                                key={turno.id}
                                className={
                                  turno.status === 'cancelled' ? 'staff-historial__cancelado' : ''
                                }
                              >
                                <td>{turno.customerName || 'Sin nombre'}</td>
                                <td>{dia.toLocaleDateString('es-AR', { weekday: 'long' })}</td>
                                <td>{dia.toLocaleDateString('es-AR')}</td>
                                <td>{turno.time}</td>
                                <td>
                                  {/* La misma insignia que la lista de turnos, con
                                      los mismos colores y las mismas etiquetas. */}
                                  <span
                                    className="status-badge"
                                    style={{
                                      backgroundColor: getStatusColors(turno.status).bg,
                                      color: getStatusColors(turno.status).color,
                                    }}
                                  >
                                    {getStatusLabel(turno.status)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* La pregunta va adentro del modal y arriba de los botones, no
                en otra ventana encima: apilar un modal para preguntar por el
                modal es donde la gente se pierde de cuál está cerrando. */}
            {descartePendiente === 'edicion' && (
              <div className="modal-descartar" role="alert">
                <span>Tenés cambios sin guardar. Si cerrás, se pierden.</span>
                <div className="modal-descartar__acciones">
                  <Button size="sm" variant="ghost" onClick={() => { setDescartePendiente(null); setEditingStaff(null); }}>
                    Descartar
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setDescartePendiente(null)}>
                    Seguir editando
                  </Button>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <Button variant="outline" onClick={pedirCerrarEdicion}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar recursos */}
      {/* Edición de un espacio reservable, con la misma forma que la del
          personal: nombre obligatorio, descripción opcional y el calendario
          para el horario. Tenía la estructura vieja, con un bloque
          "Información del recurso" y los desplegables por día. */}
      {editingResource && (
        <div className="modal-overlay" onClick={() => setEditingResource(null)}>
          <div className="modal-content staff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar recurso</h3>
              <button className="modal-close" onClick={() => setEditingResource(null)}>×</button>
            </div>

            <div className="staff-form-container">
              <div className="form-group">
                <label>
                  Nombre <span className="campo-obligatorio">*</span>
                </label>
                <input
                  type="text"
                  value={editingResource.name}
                  onChange={(e) => setEditingResource(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Sala 3"
                />
              </div>

              <div className="form-group">
                <label>Descripción (opcional)</label>
                <input
                  type="text"
                  value={editingResource.description || ''}
                  onChange={(e) => setEditingResource(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Para qué se usa"
                />
              </div>

              <div className="staff-horario">
                <label className="staff-horario-titulo">Horario</label>
                <BusinessScheduleForm
                  value={editingResource.schedule || {}}
                  onChange={(schedule) => setEditingResource(prev => ({ ...prev, schedule }))}
                  dayNames={dayNames}
                />
              </div>
            </div>

            <div className="modal-actions">
              <Button variant="outline" onClick={() => setEditingResource(null)}>
                Cancelar
              </Button>
              <Button
                disabled={savingResources || !editingResource.name?.trim()}
                onClick={async () => {
                  // Validar el horario antes de guardar
                  const scheduleValidation = validateSchedule(editingResource.schedule);
                  if (!scheduleValidation.isValid) {
                    alert(scheduleValidation.message);
                    return;
                  }
                  
                  setResources(prev => prev.map(r => 
                    r.id === editingResource.id ? editingResource : r
                  ));
                  
                  await saveResourceChanges();
                  setEditingResource(null);
                }}>
                {savingResources ? "Guardando..." : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para selección de planes */}
      {showPlansModal && (
        <div className="modal-overlay" onClick={() => setShowPlansModal(false)}>
          <div className="modal-content large-modal plans-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Elige tu Plan Perfecto</h3>
              <button className="modal-close" onClick={() => setShowPlansModal(false)}>×</button>
            </div>
            
            <div className="plans-container">
              {/* Toggle para billing mensual/anual */}
              <div className="billing-toggle-container">
                <div className="billing-toggle">
                  <span className={!isAnnualBilling ? 'active' : ''}>Mensual</span>
                  <Switch
                    checked={isAnnualBilling}
                    aria-label="Facturación anual"
                    onChange={(e) => setIsAnnualBilling(e.target.checked)}
                  />
                  <span className={isAnnualBilling ? 'active' : ''}>
                    Anual <span className="discount-badge">15% OFF</span>
                  </span>
                </div>
              </div>

              <div className="plans-grid">
                {availablePlans.map(plan => {
                  const currentPlan = getCurrentPlan();
                  const isCurrentPlan = currentPlan && currentPlan.id === plan.id;
                  const monthlyPrice = plan.monthlyPrice;
                  const totalPrice = isAnnualBilling ? plan.yearlyPrice : plan.monthlyPrice;
                  const billingLabel = isAnnualBilling ? 'facturado anualmente' : 'por mes';

                  return (
                    <div 
                      key={plan.id} 
                      className={`plan-card ${plan.recommended ? 'recommended' : ''} ${isCurrentPlan ? 'current' : ''}`}
                    >
                      {plan.recommended && (
                        <div className="recommended-badge">Más Popular</div>
                      )}
                      {isCurrentPlan && (
                        <div className="current-badge">Plan Actual</div>
                      )}
                      
                      <div className="plan-header">
                        <h4>{plan.name}</h4>
                        <p className="plan-description">{plan.description}</p>
                      </div>

                      <div className="plan-price">
                        <div className="price-per-month">
                          <span className="price-amount">${monthlyPrice}</span>
                          <span className="price-period">/mes</span>
                        </div>
                        {isAnnualBilling ? (
                          <div className="total-price">
                            ${totalPrice.toFixed(2)} {billingLabel}
                          </div>
                        ) : (
                          <div className="total-price">
                            Precio mensual
                          </div>
                        )}
                        {isAnnualBilling && (
                          <div className="savings">
                            Ahorras ${(plan.monthlyPrice * 12 - plan.yearlyPrice).toFixed(2)} al año
                          </div>
                        )}
                      </div>

                      <div className="plan-features">
                        <ul>
                          {plan.features.map((feature, index) => (
                            <li key={index}>
                              <span className="feature-check">✅</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="plan-limits">
                        <div className="limit-item">
                          <span className="limit-label">Servicios</span>
                          <span className="limit-value">{plan.maxServices}</span>
                        </div>
                        <div className="limit-item">
                          <span className="limit-label">Turnos/mes</span>
                          <span className="limit-value">{plan.maxAppointments}</span>
                        </div>
                        <div className="limit-item">
                          <span className="limit-label">Equipo</span>
                          <span className="limit-value">
                            {plan.teamSize === 1 ? '1 persona' : plan.teamSize}
                          </span>
                        </div>
                      </div>

                      <button 
                        className={`plan-select-btn ${isCurrentPlan ? 'current-plan-btn' : ''}`}
                        onClick={() => handleSelectPlan(plan.id)}
                        disabled={isCurrentPlan || processingSubscription}
                      >
                        {isCurrentPlan ? 'Plan Actual' : 
                         processingSubscription ? 'Procesando...' : 'Elegir Plan'}
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="plans-note">
                <p>💡 <strong>¿Necesitas algo más personalizado?</strong> Contactanos para planes empresariales.</p>
                <p>📞 <strong>¿Dudas?</strong> Nuestro equipo está aquí para ayudarte: soporte@aturno.com</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Brand Preview Modal */}
      <BrandPreview
        isOpen={showBrandPreview}
        onClose={() => setShowBrandPreview(false)}
        businessInfo={businessInfo}
        services={services}
        // La vista previa monta la página pública de verdad, y esa arma los
        // pasos de la reserva con el personal, los lugares y los horarios.
        staff={staff}
        resources={resources}
        schedule={businessInfo?.schedule}
        branding={businessBranding}
        onBrandingChange={handleBrandingChange}
        logo={logoDelNegocio}
        onLogoChange={setLogoDelNegocio}
        currentUser={currentUser}
        apiUrl={API_URL}
      />

      {/* Modal para bloquear horarios con sistema de steps */}
      <BloquearHorarios
        abierto={showBlockTimeModal}
        onCerrar={closeBlockTimeModal}
        onConfirmar={handleBlockTime}
        personal={staff}
        guardando={bloqueandoHorarios}
        error={error}
      />

      <AgregarTurno
        abierto={mostrarAgregarTurno}
        onCerrar={() => {
          setError('');
          setMostrarAgregarTurno(false);
        }}
        onConfirmar={handleAgregarTurnos}
        // El calendario de horarios es el mismo componente que usa la página
        // pública, y espera el negocio con su personal y sus lugares adentro; en
        // el panel esas tres cosas viven en estados separados.
        // `schedule` va aparte y no desde `businessData`: el panel lo mantiene en su
        // propio estado, que es el que se edita en la sección Horarios. Tomándolo
        // de `businessData` el calendario veía el horario con el que se cargó la
        // página, y si acababas de cambiarlo marcaba mal qué días hay turnos.
        negocio={{
          ...businessData,
          id: businessData?.id || currentUser?.uid,
          schedule,
        }}
        servicios={services}
        personal={staff}
        recursos={resources}
        guardando={guardandoTurnos}
        error={error}
      />

      {/* Discounts Modal */}
      {showDiscountsModal && (
        <DiscountsModal
          service={discountsService}
          onClose={closeDiscountsModal}
          onSave={handleSaveDiscounts}
        />
      )}

      {/* Editable Service Card */}
      {showEditableCard && (
        <div className="modal-overlay" onClick={closeEditableServiceCard}>
          {/* `service-modal` y no `large-modal`: es el MISMO componente que monta
              el onboarding, y tenerlo con dos clases distintas lo hacía salir con
              dos anchos distintos —1200px acá contra 920 allá— según desde dónde
              lo abrieras. `large-modal` es la variante para tablas anchas (el
              historial de turnos del personal, la lista de planes); un formulario
              en una sola columna no la necesita. */}
          <div className="modal-content service-modal" onClick={(e) => e.stopPropagation()}>
            <EditableServiceCard
              service={editingService || SERVICIO_NUEVO}
              onSave={handleSaveEditableService}
              onCancel={closeEditableServiceCard}
              businessHours={businessInfo?.schedule || {}}
              availableStaff={staff}
              availableResources={resources}
              availablePaymentMethods={['mercadopago', 'efectivo', 'transferencia']}
              mercadoPagoConectado={Boolean(integrations?.mercadoPago?.connected)}
              onConectarMercadoPago={handleMercadoPagoConnect}
              serviciosExistentes={services}
              userType={
                // Determinar tipo basado en el plan actual
                (() => {
                  const originalUserType = businessData?.userType;
                  const planType = userPlan?.planType || userPlan?.plan?.id;
                  
                  // Si tienes un plan business, eres business (independientemente del userType guardado)
                  const hasBusinessPlan = planType && planType.startsWith('business');
                  const shouldBeBusiness = hasBusinessPlan;
                  
                  
                  return shouldBeBusiness ? 'business' : (originalUserType || 'business');
                })()
              }
            />
          </div>
        </div>
      )}

      {/* Modal de confirmación de cancelación */}
      {showCancelModal && appointmentToCancel && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Confirmar Cancelación</h3>
              <button className="modal-close" onClick={() => setShowCancelModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="cancel-warning">
                <div className="warning-icon">🚨</div>
                <div className="warning-content">
                  <h4>¿Estás seguro que querés cancelar este turno?</h4>
                  <div className="appointment-details">
                    <p><strong>Cliente:</strong> {appointmentToCancel.customerName}</p>
                    <p><strong>Servicio:</strong> {appointmentToCancel.service}</p>
                    <p><strong>Fecha:</strong> {new Date(appointmentToCancel.date + 'T00:00:00').toLocaleDateString('es-AR')}</p>
                    <p><strong>Hora:</strong> {appointmentToCancel.time} hs</p>
                  </div>
                  <div className="warning-consequences">
                    <h5>⚠️ Esto va a:</h5>
                    <ul>
                      <li>✅ <strong>Liberar el horario</strong> para que otros clientes puedan reservar</li>
                      <li>📊 <strong>Mantener el registro</strong> en las estadísticas como turno cancelado</li>
                      <li>📧 <strong>Notificar al cliente</strong> sobre la cancelación (si está configurado)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowCancelModal(false)}
              >
                No, mantener turno
              </button>
              <button
                className="btn-danger"
                onClick={confirmCancelAppointment}
              >
                Sí, cancelar turno
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación del borrado definitivo.
          Pregunta —aunque el gesto ya fue un click— porque es la única acción
          de esta pantalla que no se puede deshacer, y muestra los datos del
          turno para que se vea cuál se está por borrar: en una lista de turnos
          parecidos, la fila que se apretó no siempre es la que se creía. */}
      {turnoABorrar && (
        <div className="modal-overlay" onClick={() => !borrandoTurno && setTurnoABorrar(null)}>
          <div className="modal-content cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🗑️ Borrar turno</h3>
              <button
                className="modal-close"
                onClick={() => setTurnoABorrar(null)}
                disabled={borrandoTurno}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="cancel-warning">
                <div className="warning-icon">🗑️</div>
                <div className="warning-content">
                  <h4>Esto borra el turno para siempre</h4>
                  <div className="appointment-details">
                    <p><strong>Cliente:</strong> {turnoABorrar.customerName}</p>
                    <p><strong>Servicio:</strong> {turnoABorrar.service}</p>
                    <p><strong>Fecha:</strong> {new Date(turnoABorrar.date + 'T00:00:00').toLocaleDateString('es-AR')}</p>
                    <p><strong>Hora:</strong> {turnoABorrar.time} hs</p>
                  </div>
                  <div className="warning-consequences">
                    <h5>⚠️ Tené en cuenta:</h5>
                    <ul>
                      <li>🗑️ <strong>Se borra de la base de datos</strong> y no se puede recuperar</li>
                      <li>📊 <strong>No queda en las estadísticas</strong>, como si nunca hubiera existido</li>
                      <li>✅ <strong>El horario queda libre</strong> para que otro cliente lo reserve</li>
                    </ul>
                    <p className="borrar-turno-sugerencia">
                      Si el turno existió y no se cumplió, lo que corresponde es
                      cancelarlo: libera el horario igual, pero deja el registro.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setTurnoABorrar(null)}
                disabled={borrandoTurno}
              >
                No, dejarlo
              </button>
              <button
                className="btn-danger"
                onClick={borrarTurno}
                disabled={borrandoTurno}
              >
                {borrandoTurno ? 'Borrando...' : 'Sí, borrar turno'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panel de prueba para el trial - Solo en desarrollo */}
      {window.location.hostname === 'localhost' && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#f8f9fa',
          border: '2px solid #007bff',
          borderRadius: '10px',
          padding: '15px',
          zIndex: 9999,
          maxWidth: '300px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#007bff' }}>
            🧪 Pruebas de Trial
          </div>
          <div style={{ fontSize: '12px', marginBottom: '10px', color: '#666' }}>
            Simula diferentes estados del trial:
          </div>
          
          <button
            onClick={async () => {
              try {
                const token = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/debug/trial/${currentUser.uid}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                console.log('🔍 Estado actual del trial:', data);
                alert(`Estado del trial:\n\nNegocio existe: ${data.business.exists}\nFecha fin: ${data.business.freeTrialEndDate}\nDías restantes: ${data.calculated?.daysRemaining || 'N/A'}\n\nVer consola para más detalles.`);
              } catch (error) {
                console.error('Error obteniendo estado:', error);
              }
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px',
              margin: '5px 0',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🔍 Ver estado actual
          </button>
          
          <button
            onClick={async () => {
              try {
                console.log('🧪 Frontend: Iniciando actualización trial nuevo...');
                const token = await currentUser.getIdToken();
                console.log('🧪 Frontend: Token obtenido');
                
                const response = await fetch(`${API_URL}/api/debug/trial/update`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ daysFromNow: 14 })
                });
                
                console.log('🧪 Frontend: Response status:', response.status);
                const result = await response.json();
                console.log('🧪 Frontend: Response data:', result);
                
                if (response.ok) {
                  console.log('✅ Frontend: Trial actualizado, recargando...');
                  window.location.reload();
                } else {
                  console.error('❌ Frontend: Error en respuesta:', result);
                  alert(`Error: ${result.error}`);
                }
              } catch (error) {
                console.error('❌ Frontend: Error completo:', error);
                alert(`Error: ${error.message}`);
              }
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px',
              margin: '5px 0',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🟢 Trial nuevo (Día 1 de 14)
          </button>

          <button
            onClick={async () => {
              try {
                const token = await currentUser.getIdToken();
                await fetch(`${API_URL}/api/debug/trial/update`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ daysFromNow: 7 })
                });
                window.location.reload();
              } catch (error) {
                console.error('Error:', error);
              }
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px',
              margin: '5px 0',
              backgroundColor: '#ffc107',
              color: 'black',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🟡 Trial medio (Día 7 de 14)
          </button>

          <button
            onClick={async () => {
              try {
                const token = await currentUser.getIdToken();
                await fetch(`${API_URL}/api/debug/trial/update`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ daysFromNow: 1 })
                });
                window.location.reload();
              } catch (error) {
                console.error('Error:', error);
              }
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px',
              margin: '5px 0',
              backgroundColor: '#fd7e14',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🟠 Trial por vencer (Día 13 de 14)
          </button>

          <button
            onClick={async () => {
              try {
                const token = await currentUser.getIdToken();
                await fetch(`${API_URL}/api/debug/trial/update`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ daysFromNow: -1 })
                });
                window.location.reload();
              } catch (error) {
                console.error('Error:', error);
              }
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px',
              margin: '5px 0',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🔴 Trial expirado
          </button>
          
          <button
            onClick={async () => {
              try {
                console.log('🧪 Frontend: Iniciando trial muy expirado...');
                const token = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/debug/trial/update`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ daysFromNow: -7 })
                });
                
                const result = await response.json();
                console.log('🧪 Frontend: Response data:', result);
                
                if (response.ok) {
                  window.location.reload();
                } else {
                  alert(`Error: ${result.error}`);
                }
              } catch (error) {
                console.error('❌ Frontend: Error:', error);
                alert(`Error: ${error.message}`);
              }
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px',
              margin: '5px 0',
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            💀 Trial muy expirado (-7 días)
          </button>
          
          <button
            onClick={async () => {
              try {
                console.log('🧪 Frontend: Simulando trial expirado sin plan...');
                const token = await currentUser.getIdToken();
                
                // Primero actualizar el trial a expirado
                const trialResponse = await fetch(`${API_URL}/api/debug/trial/update`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ daysFromNow: -5 })
                });
                
                if (trialResponse.ok) {
                  // Luego limpiar datos de subscripción para simular plan gratuito
                  const cleanResponse = await fetch(`${API_URL}/api/debug/clean-subscription`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  
                  console.log('🧪 Datos limpiados, recargando...');
                  window.location.reload();
                }
              } catch (error) {
                console.error('❌ Frontend: Error:', error);
              }
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px',
              margin: '5px 0',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            ☠️ FORZAR BLOQUEO
          </button>

          {/* Elegir cualquier plan, las veces que haga falta.
              Es un <select> nativo a propósito: los de Radix se rompen dentro
              del panel, porque `.dashboard-container *` mata los transforms y
              el popover queda pegado arriba a la izquierda. */}
          {planesDePrueba.length > 0 && (
            <div style={{ marginTop: '12px', borderTop: '1px solid #dee2e6', paddingTop: '10px' }}>
              <div style={{ fontSize: '12px', marginBottom: '6px', color: '#666' }}>
                O pararte en cualquier plan:
              </div>
              <select
                value={planElegido}
                onChange={(e) => setPlanElegido(e.target.value)}
                style={{
                  width: '100%', padding: '6px', fontSize: '12px',
                  borderRadius: '5px', border: '1px solid #ced4da', backgroundColor: 'white'
                }}
              >
                <option value="">— elegí un plan —</option>
                {planesDePrueba.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} · {p.maxServices === -1 ? 'servicios ilimitados' : `${p.maxServices} servicios`}
                  </option>
                ))}
              </select>

              {(() => {
                const aplicar = async (planId) => {
                  try {
                    const token = await currentUser.getIdToken();
                    const r = await fetch(`${API_URL}/api/debug/plan`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ planId }),
                    });
                    const cuerpo = await r.json();
                    if (!r.ok) {
                      toast.error(cuerpo.message || 'No se pudo cambiar el plan');
                      return;
                    }
                    // Recarga completa y no un refetch: el plan decide qué
                    // secciones se dibujan, qué límites aplican y qué manda cada
                    // pantalla. Recargar es la única forma de verlo entero y sin
                    // restos del plan anterior.
                    toast.success(planId ? `Ahora sos ${cuerpo.nombre}` : 'Quedaste sin plan');
                    setTimeout(() => window.location.reload(), 600);
                  } catch {
                    toast.error('No se pudo cambiar el plan');
                  }
                };
                return (
                  <>
                    <button
                      onClick={() => aplicar(planElegido)}
                      disabled={!planElegido}
                      style={{
                        display: 'block', width: '100%', padding: '8px', margin: '6px 0 0',
                        backgroundColor: planElegido ? '#0d6efd' : '#adb5bd', color: 'white',
                        border: 'none', borderRadius: '5px', fontSize: '12px',
                        cursor: planElegido ? 'pointer' : 'not-allowed', fontWeight: 'bold'
                      }}
                    >
                      ✅ Aplicar plan
                    </button>
                    <button
                      onClick={() => aplicar(null)}
                      style={{
                        display: 'block', width: '100%', padding: '8px', margin: '5px 0 0',
                        backgroundColor: '#6c757d', color: 'white', border: 'none',
                        borderRadius: '5px', fontSize: '12px', cursor: 'pointer'
                      }}
                    >
                      ↩️ Sacarme el plan (cuenta nueva)
                    </button>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Overlay de trial expirado */}
      {showTrialExpiredOverlay && (
        <TrialExpiredOverlay 
          onClose={() => setShowTrialExpiredOverlay(false)}
        />
      )}

    </div>
  );
} 